<script lang="ts">
export default { name: 'DpClientV2' };
</script>
<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, onActivated, onDeactivated, toRefs, toRaw, getCurrentInstance, nextTick, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useWindowSize } from "@vueuse/core";
import { ElLoading, ElMessage } from "element-plus";
import axios from "axios";
import dayjs from "dayjs";
import _ from "lodash";

import Header from "@/layout/components/v2/header.vue";
import RevenueRange from "@/layout/components/v2/revenue-range.vue";
import Occupancy from "@/layout/components/v2/occupancy.vue";
import Total from "@/layout/components/v2/total.vue";
import SeatTotal from "@/layout/components/v2/seatTotal.vue";
import Comment from "@/layout/components/v2/comment.vue";
import Youtube from "@/layout/components/v2/youtube.vue";
import Ins from "@/layout/components/v2/ins.vue";
import Twitter from "@/layout/components/v2/twitter.vue";
import XPlatformIcon from "@/layout/components/v2/x-platform-icon.vue";
import Hotword from "@/layout/components/v2/hotword.vue";
import GoogleTrends from "@/layout/components/v2/google-trends.vue";
import AudiencePieChart from "@/views/dp/client-v2/components/audience-pie-chart.vue";
import FinalPredictionBody from "@/views/dp/client-v2/components/final-prediction-body.vue";

import { version1NameList, version2NameList, periodNameList } from "@/utils/hardcode";
import { queryParamsProps, versionItemProps } from "@/views/dp/client/interface/index.ts";
import LazyLoadMovieSelect from "@/components/LazyLoadMovieSelect/index.vue";
import ChartCom from "@/components/chart/index.vue";
import ResponsiveDateRangePicker from "@/components/ResponsiveDateRangePicker/index.vue";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";
import {
  destroyAllChartsUnderSelector,
  markClientV2IndexChartsDisposing,
  waitClientV2DetailChartsIdle,
  waitClientV2IndexChartsIdle,
} from '@/views/dp/client-v2/chart-nav-guard';
import useAppStore from '@/store/modules/app'
import useTagsViewStore from '@/store/modules/tagsView'
import useMovieStore from '@/store/modules/movie'

// Register Chart.js components
Chart.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  Filler
);

/** テーマ CSS 変数を読み取る（html.dark の設計トークンと連携） */
function themeColor(varName: string, fallback: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback;
}
function chartGridColor() {
  return document.documentElement.classList.contains('dark')
    ? 'rgba(255,255,255,0.08)'
    : 'rgba(0,0,0,0.06)';
}
function chartGridColorStrong() {
  return document.documentElement.classList.contains('dark')
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.12)';
}

/** 推移分析图 y 轴标题（跟随主题色） */
function chartAxisTitle(text: string) {
  return {
    display: true,
    text,
    color: themeColor('--text-secondary', '#666'),
    font: { size: 11, weight: 'bold' as const },
  };
}

/** 显式声明 events，避免 destroy 后残留监听触发 eventFilter 时 options.events 为 undefined */
const DP_CHART_EVENTS = ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] as const;

/** index 模式兼容性最好；nearest+axis 在部分场景下 interaction 解析为 undefined */
const DP_CHART_INTERACTION = { mode: 'index' as const, intersect: false };

const dpChartDataStaggerDelay = (msPerPoint: number) => (
  context: { type?: string; mode?: string; dataIndex?: number },
) => {
  if (context.type === 'data' && context.mode === 'default') {
    return (context.dataIndex ?? 0) * msPerPoint;
  }
  return 0;
};

const DP_CHART_ANIMATION = {
  duration: 2000,
  easing: 'easeOutQuart' as const,
};

const DP_CHART_LINE_ANIMATION = {
  duration: 2200,
  easing: 'easeOutCubic' as const,
};

const dpChartLineDatasetAnimation = (duration: number, staggerMs: number) => ({
  duration,
  easing: 'easeOutCubic' as const,
  delay: dpChartDataStaggerDelay(staggerMs),
});

/** hover 定点过渡；仅关闭 resize，避免 destroy 时与布局变更竞态 */
const DP_CHART_TRANSITIONS = {
  active: {
    animation: {
      duration: 400,
      easing: 'easeOutQuart' as const,
    },
  },
  resize: { animation: { duration: 0 } },
};

const DP_CHART_TOOLTIP = {
  enabled: true,
  animation: {
    duration: 250,
    easing: 'easeOutQuart' as const,
  },
};

/** 「上映回数 vs 総座席数」の凡例と tooltip で表示順・マーカー形状を揃える */
const transitionChartLegend = {
  position: 'top' as const,
  align: 'start' as const,
  fullSize: true,
  labels: {
    usePointStyle: true,
    boxWidth: 18,
    boxHeight: 8,
    pointStyleWidth: 22,
    padding: 12,
    font: {
      size: 11,
      lineHeight: 1.2,
    },
    sort: (a: any, b: any) => (a.datasetIndex ?? 0) - (b.datasetIndex ?? 0),
    generateLabels(chart: any) {
      const generateDefaultLabels = Chart.defaults.plugins.legend.labels.generateLabels;
      const labels = generateDefaultLabels ? generateDefaultLabels(chart) : [];
      return labels.map((item: any) => {
        const isLine = chart.data.datasets[item.datasetIndex]?.type === 'line';
        return {
          ...item,
          pointStyle: isLine ? 'line' : 'rectRounded',
          lineDash: isLine ? [6, 4] : [],
        };
      });
    },
  },
};

const tooltipDashedLineMarkers = new Map<string, HTMLCanvasElement>();

/** Chart.js の point-style tooltip 用に、系列色を反映した破線マーカーを生成する */
const getTooltipDashedLineMarker = (borderColor: unknown) => {
  const color = typeof borderColor === 'string' ? borderColor : '#909399';
  const cached = tooltipDashedLineMarkers.get(color);
  if (cached) return cached;
  if (typeof document === 'undefined') return 'line';

  const marker = document.createElement('canvas');
  marker.width = 16;
  marker.height = 10;
  const context = marker.getContext('2d');
  if (!context) return 'line';

  context.strokeStyle = color;
  context.lineWidth = 2;
  context.lineCap = 'butt';
  context.setLineDash([4, 2]);
  context.beginPath();
  context.moveTo(1, marker.height / 2);
  context.lineTo(marker.width - 1, marker.height / 2);
  context.stroke();
  tooltipDashedLineMarkers.set(color, marker);
  return marker;
};

/**
 * Chart.js は null の点を active elements から除外するため、同日の一部系列が
 * tooltip に出ない。hover 中の日付について、表示中の全 dataset を明示的に active にする。
 */
const transitionChartCompleteTooltipPlugin = {
  id: 'transitionChartCompleteTooltip',
  afterEvent(chart: any, args: any) {
    const event = args.event;
    const tooltip = chart.tooltip;
    if (!tooltip || !args.inChartArea || event.type === 'mouseout') return;

    const activeElements = chart.getActiveElements();
    const dataIndex = activeElements[0]?.index;
    if (dataIndex == null) return;

    const allVisibleItems = chart.data.datasets.flatMap((dataset: any, datasetIndex: number) => (
      chart.isDatasetVisible(datasetIndex) && dataIndex < (dataset.data?.length ?? 0)
        ? [{ datasetIndex, index: dataIndex }]
        : []
    ));

    tooltip.setActiveElements(allVisibleItems, { x: event.x, y: event.y });
    args.changed = true;
  },
};

const transitionChartTooltip = {
  ...DP_CHART_TOOLTIP,
  usePointStyle: true,
  itemSort: (a: any, b: any) => (a.datasetIndex ?? 0) - (b.datasetIndex ?? 0),
  callbacks: {
    label(context: any) {
      const label = context.dataset.label ?? '';
      const value = context.raw;
      return `${label}: ${value == null ? '-' : Number(value).toLocaleString()}`;
    },
    labelPointStyle(context: any) {
      const isLine = context.dataset.type === 'line';
      return {
        pointStyle: isLine
          ? getTooltipDashedLineMarker(context.dataset.borderColor)
          : 'rectRounded',
        rotation: 0,
      };
    },
  },
};

const DP_CHART_ELEMENTS = {
  point: {
    radius: 0,
    hoverRadius: 8,
    hitRadius: 12,
    hoverBorderWidth: 2,
  },
};

/**
 * 折线的 scriptable pointRadius：仅当某点“孤立”（前后都无相邻有效值，
 * 例如只有一个日期、或被 null 隔断的单点）时显示圆点，否则维持 0 保持简洁。
 * 解决单日期时 line 没有线段、pointRadius:0 导致什么都看不到的问题。
 */
const isolatedPointRadius = (radius = 4) => (ctx: any) => {
  const data = ctx?.dataset?.data;
  const i = ctx?.dataIndex;
  if (!Array.isArray(data)) return 0;
  if (data[i] == null) return 0;
  const hasPrev = data[i - 1] != null;
  const hasNext = data[i + 1] != null;
  return hasPrev || hasNext ? 0 : radius;
};

const waitAnimationFrames = (count = 3) => new Promise<void>((resolve) => {
  let left = count;
  const step = () => {
    if (left <= 0) {
      resolve();
      return;
    }
    left -= 1;
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
});
import {
  getMovieListService,
  getCompanyNameService,
  getAreaChartService,
  getAreaListService,
  getPrefectureCountService,
  getMovieReviewService,
  getScheduleCountService,
  getDailyBoxOfficeService,
  getSocialStatsService,
  getWeekDigestAIServiceV2,
  getBenchmarkMoviesService,
  getBoxOfficePredictionService,
} from "@/api/dp/index.js";
// 投稿密集度（日次） densityIndex の表示用フォーマッタ（socialRate.ts に新規追加）
import { formatDensityIndex, densityIndexBarWidth } from "@/utils/socialRate";

const dailyBoxOfficeChartRef = ref<HTMLCanvasElement | null>(null);
const dailyBoxOfficeCanvasKey = ref(0);
/** keep-alive 返回时递增，强制换新 canvas 再据缓存数据重绘 */
const clientV2ChartsRemountKey = ref(0);
/** false 时从 DOM 移除全部 chart canvas，避免隐藏页面上 Animator 继续 draw */
const clientV2ChartsMounted = ref(true);
const dailyBoxOfficeChartInstance = ref<Chart | null>(null);
const dailyBoxOfficeChartPack = ref<any>(null);
const dailyBoxOfficeLoading = ref(false);
let dailyBoxOfficeFetchId = 0;
let dailyBoxOfficeActivateId = 0;
/** 取消过期的 keep-alive 激活 / init 任务 */
let clientV2ActivateSeq = 0;
/** 串行化离开页、进 detail、deactivate 时的 chart 销毁，避免并发 destroy 与 Animator 竞态 */
let chartTeardownChain: Promise<void> = Promise.resolve();

const bumpDailyBoxOfficeActivateId = () => {
  dailyBoxOfficeActivateId += 1;
};
const defaultSocialStats = () => ({
  youtube: { total: null as number | null, dayRate: null as number | null, weekRate: null as number | null },
  instagram: {
    total: null as number | null,
    hashtag: null as string | null,
    dayRate: null as number | null,
    weekRate: null as number | null,
  },
  twitter: {
    total: null as number | null,
    hashtag: null as string | null,
    dayRate: null as number | null,
    weekRate: null as number | null,
    // 投稿密集度（日次）（0~100、後端新規フィールド densityIndex）。null は「—」表示
    densityIndex: null as number | null,
  },
});
const socialStats = ref(defaultSocialStats());
const movieStore = useMovieStore();

let proxy: any = null;
const instance = getCurrentInstance();
const route = useRoute();
const router = useRouter();
const { width } = useWindowSize();
const companyId = window.localStorage.getItem('deptId');
// const companyId = 1244;
if (instance && instance.proxy) {
  proxy = instance.proxy;
}

// 响应式表单配置
const labelWidth = computed(() => {
  // 〜400px（iPhone SE 等）は少しだけ詰める。
  // ※「* 作品」（アスタリスク＋2文字＋右パディング 12px）が折り返さない下限が 56px
  if (width.value < 400) return '56px';
  if (width.value < 768) return '60px';
  if (width.value < 992) return '70px';
  return '60px';
});

const labelPosition = computed(() => {
  return 'right'; // Always keep label and content on the same line
});

const gutterSize = computed(() => {
  if (width.value < 400) return 8;
  if (width.value < 576) return 10;
  return 20;
});

const device = computed(() => useAppStore().device);

// keep-alive のキャッシュに自分自身を登録し、遷移後も状態を保持する
const tagsViewStore = useTagsViewStore();
if (!tagsViewStore.cachedViews.includes('DpClientV2')) {
  tagsViewStore.cachedViews.push('DpClientV2');
}

onMounted(() => {
  getCompanyNameService({
    companyId: Number(companyId),
  }).then((res) => {
    companyName.value = res;
  });

  getAreaListService().then((res) => {
    areaList.value = res.rows.map((item: { id: number; name: string }) => ({
      id: item.id,
      name: item.name,
    }));
  });
  // initCharts();
});

const allChartInstances = () => [
  mainChartInstance,
  occupancyChartInstance,
  cinemaChartInstance,
  mainRegionChartInstance,
  occupancyRegionChartInstance,
  cinemaRegionChartInstance,
];

/** 经 goToReviewDetail 已 detach 时 onDeactivated 不再重复 teardown */
let clientV2LeavingViaDetail = false;
/** 仅 keep-alive 再次显示时恢复 chart，跳过首次 mount 的 onActivated */
let clientV2NeedsChartRestore = false;

const disposeAllChartsSafely = (afterDispose?: () => void) => {
  void runChartTeardown(afterDispose);
};

// reactive
const queryParams = reactive<queryParamsProps>({
  movieId: undefined,
  duration: [],
});
const areaQueryParams = reactive<{
  areaId: number | undefined;
}>({
  areaId: undefined,
});
const { movieId, duration } = toRefs(queryParams);

// ─── 公開前／公開後 模式（DEV-14891）─────────────────────────────────
/**
 * 'post' = 公開後（原有流程：期間 → 作品 → 確定）
 * 'pre'  = 公開前（无期间：作品 → 確定；作品仅限公开日 > JST 今天，由后端 /dp/movieList?preRelease=true 判定）
 */
type ReleaseMode = 'pre' | 'post';
const releaseMode = ref<ReleaseMode>('post');
const isPreRelease = computed(() => releaseMode.value === 'pre');
/** <main> 面板是否已反映当前作品的数据（公開前不调 scheduleCount，不能再用 cinemaTotalTableData 判断） */
const panelReady = ref(false);
/** 切换作品／期间／模式或重新確定时 +1，用于丢弃切换前发出的请求响应 */
let panelRequestSeq = 0;
/**
 * 公開前模式跳转详情页时的回溯天数。
 * SNS 数据从公开日前 2 个月（最长 62 天）开始采集，公开日最早为明天，最近 63 天即可覆盖其全部已采集数据。
 */
const PRE_RELEASE_DETAIL_LOOKBACK_DAYS = 63;
/** 详情页跳转用的期间（毫秒，dateTo 为次日 0 点、不含）；公開後未选期间时返回 null */
const getDetailDateRange = () => {
  if (isPreRelease.value) {
    return {
      dateFrom: dayjs().subtract(PRE_RELEASE_DETAIL_LOOKBACK_DAYS, 'day').startOf('day').valueOf(),
      dateTo: dayjs().add(1, 'day').startOf('day').valueOf(),
    };
  }
  if (!duration.value?.length) return null;
  return {
    dateFrom: dayjs(duration.value[0]).startOf('day').valueOf(),
    dateTo: dayjs(duration.value[1]).add(1, 'day').startOf('day').valueOf(),
  };
};
// ref
const isFetch = ref(true);
const companyName = ref();
const areaList = ref([]);
const appendVersion1Names = ref<
  {
    name: string;
    width: number;
  }[]
>([]);
const appendVersion2Names = ref<
  {
    name: string;
    width: number;
  }[]
>([]);
const cinemaTotalTableData = ref<
  {
    countTotal: number;
  }[]
>([]);
const tableData = ref<
  {
    countTotal: number;
  }[]
>([]);
const areaTableData = ref<
  {
    id?: any;
    areaName: string;
    prefectureName: string;
    cinemaTotalNo: number;
    countTotal: number;
    seatTotalNo: number;
    emptyRate: number | null;
  }[]
>([]);
const detailTableData = ref<
  {
    prefectureName: string;
    brandName: string;
    cinemaName: string;
    countTotal: number;
    seatTotalNo: number;
    emptyRate: number;
    isBrandRow: boolean;
    level: "brand" | "cinema";
    isFirstInGroup: boolean;
  }[]
>([]);
const isShowDetail = ref(false);
const SeatTotalTableData = ref<
  {
    seatTotalNo: number;
    maxBoxOffice: number;
    minBoxOffice: number;
    /** 票房点估计（円）：区间中心，对外展示用的主数字 */
    boxOffice: number;
    emptyRate: number;
    morningSeatTotalNo: number;
    afternoonSeatTotalNo: number;
    nightSeatTotalNo: number;
    morningEmptyRate: number;
    afternoonEmptyRate: number;
    nightEmptyRate: number;
  }[]
>([]);
const AIPredictionTableData = ref<
  {
    seatTotalNo: number;
  }[]
>([]);
const version1ListData = ref<versionItemProps[]>([]);
const version2ListData = ref<versionItemProps[]>([]);
const periodListData = ref<
  {
    name: string;
    count: number;
  }[]
>([]);
const periodSeatTotalData = ref<
  {
    name: string;
    count: number;
  }[]
>([]);
const periodSeatOccupiedRateData = ref<
  {
    name: string;
    count: number;
  }[]
>([]);
const formatNumber = (num: number) => {
  if (num >= 100000000) {
    return {
      value: parseFloat((num / 100000000).toFixed(2)),
      unit: "億"
    };
  } else if (num >= 10000000) {
    return {
      value: parseFloat((num / 10000000).toFixed(2)),
      unit: "千万"
    };
  } else if (num >= 10000) {
    return {
      value: parseFloat((num / 10000).toFixed(2)),
      unit: "万"
    };
  } else {
    return {
      value: parseFloat(num.toFixed(2)),
      unit: ""
    };
  }
};
const AIPredictionSeatTotalNo = computed(() => {
  if (SeatTotalTableData.value.length === 0) {
    return 0;
  }
  const row = SeatTotalTableData.value[0];
  // 「興行収入（AI推測値）」展示的应是点估计（区间中心），它才是对票房的最佳估计。
  // maxBoxOffice 只是区间上沿（点估计 ×1.10），单独拿来展示会系统性偏高约一成。
  // 后端未返回 boxOffice（旧版本接口）时按 max / 1.1 兜底反推，与后端 pointFromMax 同一换算。
  const point = row.boxOffice ?? Math.floor(row.maxBoxOffice / 1.1);
  return {
    min: row.minBoxOffice,
    max: row.maxBoxOffice,
    point,
  };
});
/** 「興行収入（AI推測値）」カードの主数値＝点推定 */
const formattedRevenue = computed(() => {
  if (AIPredictionSeatTotalNo.value === 0 || typeof AIPredictionSeatTotalNo.value === 'number') {
    return { value: 0, unit: '' };
  }
  return formatNumber(AIPredictionSeatTotalNo.value.point);
});
const commentCount = ref(0);
const likeCount = ref(0);
const movieList = ref<
  {
    name: string;
    id: number;
    openDate?: string;
  }[]
>([]);

// ─── 対比電影（ベンチマーク作品）関連状態 ─────────────────────────────
/** タブ表示用ラベル：候補[0]→A / [1]→B / [2]→C */
const COMPARE_MOVIE_LABELS = ['A', 'B', 'C'];
/** `/comparableMovie/benchmark` の返却値（最大3件、片名は使用しない＝非表示のため）。similarity は総合類似度(0~100、5次元算術平均を四捨五入) */
const benchmarkMovies = ref<{ movieId: number; openDate?: string; similarity?: number }[]>([]);
/** ベンチマーク比較作品を取得中かどうか（レスポンスが遅いため専用のローディング表示を出す） */
const benchmarkLoading = ref(false);
/** 現在表示中のタブ：'main'=本片、number=benchmarkMovies のインデックス */
const activeMovieTab = ref<'main' | number>('main');
/** 対比電影データ取得中のタブ（ボタンのローディング表示用） */
const compareLoadingTab = ref<number | null>(null);
/** タブ切替処理（キャッシュ反映／再取得のどちらでも）が進行中かどうか。パネル全体のローディングオーバーレイに使う */
const panelSwitching = ref(false);
/** タブ切替時に興行データ系パネルへ丸ごと差し替える値のスナップショット */
type PrimaryPanelSnapshot = {
  version1ListData: any[];
  version2ListData: any[];
  periodListData: { name: string; count: number }[];
  cinemaTotalTableData: { countTotal: number }[];
  tableData: { countTotal: number }[];
  SeatTotalTableData: any[];
  chartData: any[];
  dailyBoxOfficeChartPack: any;
};
/** tab key ('main' | index) ごとに一度取得したデータをキャッシュし、タブ再選択時の再リクエストを避ける */
const movieDataCache = new Map<string | number, PrimaryPanelSnapshot>();
const activeMovieTabLabel = computed(() => {
  if (activeMovieTab.value === 'main') return '';
  return COMPARE_MOVIE_LABELS[activeMovieTab.value as number] || '';
});
/** 「興行データ」大見出しの動的テキスト（対比電影表示中は対比電影A/B/Cに差し替え） */
const primarySectionTitle = computed(() =>
  activeMovieTab.value === 'main' ? '興行データ' : `作品${activeMovieTabLabel.value}`,
);

// 使用 store 存储选中的 movie（useMovieStore は script 先頭で import）

const goToReviewDetail = async (source: 'review' | 'youtube' | 'instagram' | 'twitter' = 'review') => {
  // 公開前模式没有期间，改用最近的采集期间打开详情页
  const detailRange = getDetailDateRange();
  if (!movieId.value || !detailRange) return;
  const selectedMovie = movieList.value.find((m: any) => m.id === movieId.value);
  const selectedMovieName = selectedMovie?.name ?? '';
  clientV2LeavingViaDetail = true;
  clientV2NeedsChartRestore = true;
  await detachClientV2Charts();
  await router.push({
    path: '/dp/client-v2/review-detail',
    query: {
      movieId: movieId.value,
      movieName: selectedMovieName,
      ...(selectedMovie?.openDate ? { openDate: selectedMovie.openDate } : {}),
      dateFrom: detailRange.dateFrom,
      dateTo: detailRange.dateTo,
      ...(source !== 'review' ? { source } : {}),
      ...(source === 'instagram' && socialStats.value.instagram.hashtag
        ? { instagramHashtag: socialStats.value.instagram.hashtag }
        : {}),
      ...(source === 'twitter' && socialStats.value.twitter.hashtag
        ? { twitterHashtag: socialStats.value.twitter.hashtag }
        : {}),
    },
  });
};

const goToVisitorForecast = async () => {
  if (!movieId.value || !duration.value?.length) return;
  const selectedMovieName = movieList.value.find((m: any) => m.id === movieId.value)?.name ?? '';
  clientV2LeavingViaDetail = true;
  clientV2NeedsChartRestore = true;
  await detachClientV2Charts();
  await router.push({
    path: '/dp/client-v2/visitor-forecast',
    query: {
      movieId: movieId.value,
      movieName: selectedMovieName,
      dateFrom: dayjs(duration.value[0]).startOf('day').valueOf(),
      dateTo: dayjs(duration.value[1]).add(1, 'day').startOf('day').valueOf(),
    },
  });
};

/** ホットワードクリック → ホットワード詳細ページへ遷移（chart detach は他の詳細遷移と同様） */
const goToHotwordDetail = async (word: string) => {
  // 公開前模式没有期间，改用最近的采集期间打开详情页
  const detailRange = getDetailDateRange();
  if (!movieId.value || !detailRange) return;
  if (!word) return;
  const selectedMovieName = movieList.value.find((m: any) => m.id === movieId.value)?.name ?? '';
  clientV2LeavingViaDetail = true;
  clientV2NeedsChartRestore = true;
  await detachClientV2Charts();
  await router.push({
    path: '/dp/client-v2/hotword-detail',
    query: {
      word,
      movieId: movieId.value,
      movieName: selectedMovieName,
      dateFrom: detailRange.dateFrom,
      dateTo: detailRange.dateTo,
    },
  });
};

// 监听 movieId 变化，更新 store
watch(movieId, (newMovieId) => {
  if (newMovieId && movieList.value.length > 0) {
    const movie = movieList.value.find((item: any) => item.id === newMovieId);
    if (movie) {
      movieStore.setSelectedMovie(newMovieId, movie.name);
    }
  } else {
    movieStore.clearSelectedMovie();
  }
}, { immediate: true });

// 监听 movieList 变化，更新 store
watch(movieList, (newList) => {
  movieStore.setMovieList(newList);
  // 如果已有选中的 movieId，更新名称
  if (movieId.value && newList.length > 0) {
    const movie = newList.find((item: any) => item.id === movieId.value);
    if (movie) {
      movieStore.setSelectedMovie(movieId.value, movie.name);
    }
  }
}, { deep: true });

const loading = ref(false);
const detailLoading = ref(false);
let loadingInstance: any = null;

// 全屏 loading 控制函数
const showLoading = () => {
  if (!loadingInstance) {
    loadingInstance = ElLoading.service({
      lock: true,
      text: '読み込み中...',
      background: 'rgba(0, 0, 0, 0.85)',
      target: document.body,
      customClass: 'app-request-loading'
    });
  }
};

const hideLoading = () => {
  if (loadingInstance) {
    loadingInstance.close();
    loadingInstance = null;
  }
};

const rules = ref({
  movieId: [
    {
      required: true,
      trigger: "change",
      message: "作品を選択してください",
    },
  ],
});
const areaRules = ref({
  areaId: [
    {
      required: true,
      trigger: "change",
      message: "エリアを選択してください",
    },
  ],
});
const chartData = ref();
const areaChartData = ref();
const isShowMovieSelect = ref(false);
const hasExpandedPrefecture = ref(false); // 跟踪是否有展开的都道府県节点
const hasExpandedBrand = ref(false); // 跟踪是否有展开的映画館チェーン节点
const commentObj = ref<any>({});
const showRatingRules = ref(false);
const isMobile = ref(typeof window !== 'undefined' && window.innerWidth <= 1024);
function onRatingRulesResize() { isMobile.value = window.innerWidth <= 1024; }
onMounted(() => { window.addEventListener('resize', onRatingRulesResize); });
onUnmounted(() => { window.removeEventListener('resize', onRatingRulesResize); });

//根据返回的version名字mapping 获取count
const getCountForVersionName = (name: string, versionList: versionItemProps[]) => {
  const count = (versionList.find((item, idx) => item.name === name) || {}).count;
  if (count !== undefined && count !== null) {
    return count.toLocaleString();
  }
  return 0;
};
const getCountForPeriodName = (name: string, periodList: { name: string; count: number }[]) => {
  const count = (periodList.find((item, idx) => item.name === name) || {}).count;
  if (count !== undefined && count !== null) {
    return count.toLocaleString();
  }
  return 0;
};
const getRateForPeriodNum = (name: string, periodList: { name: string; count: number }[]) => {
  const count = (periodList.find((item, idx) => item.name === name) || {}).count;
  if (count !== undefined && count !== null) {
    return count;
  }
  return 0;
};
const getDiffArrays = (versionList: any[], versionNameList: any[]) => {
  const diffArrs = _.cloneDeep(
    _.differenceBy(
      versionList.map((item: { versionId: string; name: string }) => ({
        id: item.versionId,
        name: item.name,
      })),
      versionNameList,
      "name",
    ),
  );
  return diffArrs.map((item) => ({
    name: item.name,
    width: 120,
  }));
};

// 补充缺失日期的数据，用0填充
const fillMissingDates = (chatDataList: any[], dateRange: Date[]) => {
  if (!dateRange || dateRange.length !== 2) {
    return chatDataList || [];
  }

  // 如果没有数据，根据日期范围生成空数据
  if (!chatDataList || chatDataList.length === 0) {
    const startDate = dayjs(dateRange[0]).startOf('day');
    const endDate = dayjs(dateRange[1]).startOf('day');
    const allDates: any[] = [];
    let currentDate = startDate;
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
      allDates.push({
        date: currentDate.format('YYYY-MM-DD'),
        countTotal: 0,
        seatTotalNo: 0,
        emptyRate: 0,
        cinemaTotalNo: 0,
      });
      currentDate = currentDate.add(1, 'day');
    }
    return allDates;
  }

  const startDate = dayjs(dateRange[0]).startOf('day');
  const endDate = dayjs(dateRange[1]).startOf('day');

  // 生成完整的日期序列
  const allDates: string[] = [];
  let currentDate = startDate;
  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
    allDates.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'day');
  }

  // 创建日期到数据的映射，保持原始数据的日期格式
  const dataMap = new Map();
  chatDataList.forEach((item: any) => {
    const dateKey = dayjs(item.date).format('YYYY-MM-DD');
    dataMap.set(dateKey, item);
  });

  // 按日期顺序填充数据，缺失的用0填充，保持原始数据的日期格式
  const filledData = allDates.map((dateStr) => {
    if (dataMap.has(dateStr)) {
      return dataMap.get(dateStr);
    } else {
      // 缺失的日期用0填充，使用原始数据中第一个日期的格式作为参考
      const firstItem = chatDataList[0];
      const originalDate = firstItem?.date;
      let dateValue: string | number = dateStr;

      // 如果原始数据是时间戳或其他格式，尝试保持格式一致
      if (typeof originalDate === 'number') {
        dateValue = dayjs(dateStr).valueOf();
      } else if (originalDate && dayjs(originalDate).isValid()) {
        // 保持原始格式
        dateValue = dayjs(dateStr).format(dayjs(originalDate).format('YYYY-MM-DD') === originalDate ? 'YYYY-MM-DD' : dayjs(originalDate).format());
      }

      return {
        date: dateValue,
        countTotal: 0,
        seatTotalNo: 0,
        emptyRate: 0,
        cinemaTotalNo: 0,
      };
    }
  });

  return filledData;
};

/** `/dp/dailyBoxOffice`：顶层或 `data` 内带 `dailyList`；项含 `date`、`maxBoxOffice`、`minBoxOffice` 等 */
const pickDailyBoxOfficeList = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    if (Array.isArray(res.dailyList)) return res.dailyList;
    if (res.data && typeof res.data === 'object' && Array.isArray(res.data.dailyList)) return res.data.dailyList;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.rows)) return res.rows;
    if (Array.isArray(res.list)) return res.list;
  }
  return [];
};

const enableChartCanvas = (canvas: HTMLCanvasElement | null | undefined) => {
  if (canvas) canvas.style.pointerEvents = 'auto';
};

/** 销毁前关闭动画与 fill，避免 Animator 在 ctx 释放后仍走 clipArea/save */
const muteChartAnimations = (chart: Chart) => {
  try {
    (chart.options as { animation?: false; responsive?: boolean }).animation = false;
    (chart.options as { responsive?: boolean }).responsive = false;
    if (chart.config?.options) {
      (chart.config.options as { animation?: false }).animation = false;
    }
    chart.data?.datasets?.forEach((ds) => {
      const row = ds as { animation?: false; fill?: boolean };
      row.animation = false;
      row.fill = false;
    });
    const transitions = chart.options.transitions as Record<string, { animation?: { duration: number } }> | undefined;
    if (transitions) {
      transitions.active = { animation: { duration: 0 } };
      transitions.resize = { animation: { duration: 0 } };
    }
    const plugins = chart.options.plugins as { tooltip?: { animation?: { duration: number } } } | undefined;
    if (plugins?.tooltip) {
      plugins.tooltip.animation = { duration: 0 };
    }
  } catch {
    /* ignore */
  }
};

const waitForChartAnimatorIdle = async (chart: Chart, maxFrames = 16) => {
  for (let i = 0; i < maxFrames; i += 1) {
    try {
      chart.stop();
    } catch {
      /* ignore */
    }
    const animating = (chart as Chart & { animating?: boolean }).animating;
    if (!animating) break;
    await waitAnimationFrames(1);
  }
};

/** 同步打断绘制；勿在销毁前 chart.update，否则会触发 clipArea/save */
const syncAbortChart = (chart: Chart | null | undefined) => {
  if (!chart) return;
  try {
    chart.options.events = [];
  } catch {
    /* ignore */
  }
  muteChartAnimations(chart);
  try {
    chart.stop();
  } catch {
    /* ignore */
  }
};

const syncAbortAllClientV2Charts = () => {
  const seen = new Set<Chart>();
  const abortOne = (chart: Chart | null | undefined) => {
    if (!chart || seen.has(chart)) return;
    seen.add(chart);
    syncAbortChart(chart);
  };
  allChartInstances().forEach((inst) => abortOne(inst.value));
  abortOne(dailyBoxOfficeChartInstance.value);
  for (const canvasRef of clientV2ChartCanvasRefs()) {
    const canvas = canvasRef.value;
    if (!canvas) continue;
    abortOne(Chart.getChart(canvas));
  }
};

const stopAndDestroyChart = async (chart: Chart | undefined | null) => {
  if (!chart) return;
  const canvas = chart.canvas as HTMLCanvasElement | undefined;
  if (canvas) canvas.style.pointerEvents = 'none';
  syncAbortChart(chart);
  await waitForChartAnimatorIdle(chart);
  await waitAnimationFrames(2);
  try {
    if (chart.ctx) chart.destroy();
  } catch {
    /* ignore */
  }
  if (canvas) {
    const orphan = Chart.getChart(canvas);
    if (orphan) await stopAndDestroyChart(orphan);
    enableChartCanvas(canvas);
  }
};

const clientV2ChartCanvasRefs = () => [
  mainChartRef,
  occupancyChartRef,
  cinemaChartRef,
  mainRegionChartRef,
  occupancyRegionChartRef,
  cinemaRegionChartRef,
  dailyBoxOfficeChartRef,
];

/** 按 canvas 顺序销毁，避免 Promise.all 与全局 Animator 竞态 */
const destroyChartsOnClientV2Canvases = async () => {
  for (const canvasRef of clientV2ChartCanvasRefs()) {
    const canvas = canvasRef.value;
    if (!canvas) continue;
    await stopAndDestroyChart(Chart.getChart(canvas));
    enableChartCanvas(canvas);
  }
};

/** 销毁日次予測 chart（canvas 须保持挂载，勿用 v-if 卸载） */
const disposeDailyBoxOfficeChartForInit = async () => {
  const canvas = dailyBoxOfficeChartRef.value;
  const ch = dailyBoxOfficeChartInstance.value ?? (canvas ? Chart.getChart(canvas) : null);
  dailyBoxOfficeChartInstance.value = null;
  await stopAndDestroyChart(ch);
  if (canvas) {
    await stopAndDestroyChart(Chart.getChart(canvas));
  }
};

/** 停止 Animator 后再重建，避免 destroy 与 resize 竞态 */
const destroyDailyBoxOfficeChartDeferred = async () => {
  await disposeDailyBoxOfficeChartForInit();
  await waitAnimationFrames(3);
};

/** 换新 canvas 节点，彻底断开 Chart.js 在旧 canvas 上的注册与 resize 监听 */
const remountDailyBoxOfficeCanvas = async () => {
  await disposeDailyBoxOfficeChartForInit();
  dailyBoxOfficeCanvasKey.value += 1;
  await nextTick();
  await waitAnimationFrames(1);
};

const formatDailyBoxOfficeNumber = (value: any) => {
  const n = Number(value);
  return Number.isNaN(n) ? '-' : n.toLocaleString();
};

const formatBoxOfficeWithUnit = (raw: unknown, yUnit: string) => {
  const n = Number(raw);
  if (Number.isNaN(n)) return '-';
  if (yUnit === '万円') {
    return `${(n / 10000).toLocaleString(undefined, { maximumFractionDigits: 2 })} 万円`;
  }
  return `${n.toLocaleString()} 円`;
};

const pickSocialStatsPayload = (res: any) => {
  if (!res || typeof res !== 'object') return null;
  if (res.youtube || res.instagram || res.twitter) return res;
  if (res.data && typeof res.data === 'object' && (res.data.youtube || res.data.instagram || res.data.twitter)) {
    return res.data;
  }
  return null;
};

const toSocialNumber = (value: any): number | null => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const formatYoutubeViewCount = (total: number | null) => {
  if (total == null) return '—';
  if (total >= 1_000_000) {
    const m = total / 1_000_000;
    return `${m >= 10 || m % 1 === 0 ? Math.round(m) : m.toFixed(1)}M`;
  }
  if (total >= 10_000) return `${Math.round(total / 1000)}K`;
  if (total >= 1000) return `${(total / 1000).toFixed(1)}K`;
  return total.toLocaleString();
};

const formatInstagramPostCount = (total: number | null) => {
  if (total == null) return '—';
  return total.toLocaleString();
};

const formatTwitterPostCount = formatInstagramPostCount;

const hasSocialStatTotal = (total: number | null) => total != null && total > 0;

/** SNS 統計のデータ取得期間（作品公開日基準） */
const SOCIAL_DATA_SOURCE_TIP =
  'データは作品の公開日の2か月前から、公開後2か月までを対象としています。';

/** 公開前模式可选作品范围的提示（DEV-14891），与后端 /dp/movieList?preRelease=true 的条件保持一致 */
const PRE_RELEASE_MOVIE_RANGE_TIP =
  '公開前では、公開日が明日から2か月後までの作品を選択できます。SNS データは公開日の2か月前から収集しています。';

const SOCIAL_CARD_TITLE = {
  engagement: '評価 & エンゲージメント',
  youtube: 'YouTube予告編再生回数',
  instagram: 'Instagram ハッシュタグポスト数',
  twitter: 'X インデックスのトレンド',
  trends: 'Google トレンド 検索インタレスト',
} as const;

/** Google トレンド カードの説明（相対値であること・対象期間/地域を明示、DEV-14892） */
const TRENDS_DATA_SOURCE_TIP =
  'Google トレンドの検索インタレストは、表示期間内の最高値を 100 とした相対的な検索の多さを示す指数で、実際の検索回数ではありません。'
  + '期間は作品の公開日の90日前から公開後2か月まで（最新のデータ取得日まで）、地域は日本です。作品間の数値比較にはご利用いただけません。';

/**
 * レビュー・感情分析：ホットワード ヒートスケール凡例。
 * 現在ページに描画されている <Hotword> の word-tag-size を読み取り、
 * 描画された色の数だけスケールを動的に表示する（色はページの実色を反映）。
 * ラベルは数値範囲（60+ 等）を省き、ヒート段階名のみ表示する。
 */
const HOTWORD_SIZE_LABELS: Record<number, string> = {
  5: 'ULTRA HOT', // size-5（最大＝最ホット）
  4: 'HOT',
  3: 'HIGH',
  2: 'MID',
  1: 'LOW',        // size-1（最小）
};

const hotwordHeatScales = ref<{ color: string; label: string }[]>([]);

/** <Hotword> のインスタンス参照。<main v-if> 描画後に fetchHotwords() を呼ぶため保持。 */
const hotwordRef = ref<InstanceType<typeof Hotword> | null>(null);

/** /dp/scheduleCount 完了で cinemaTotalTableData が更新される → <main v-if> が true になり
 *  <Hotword> が mount される。その後にホットワードを取得（初回・映画切替ともここ経由）。
 *  flush:'post' で DOM 更新（Hotword mount ＋ ref バインド）後に実行する。 */
watch(
  cinemaTotalTableData,
  (val) => {
    if (val && val.length > 0) {
      hotwordRef.value?.fetchHotwords();
    }
  },
  { flush: 'post' },
);

/** rgba/rgb を不透明の rgb に変換（凡例ドットを見やすくするため実色のアルファを 1 にする） */
const toSolidRgb = (cssColor: string): string => {
  const m = cssColor.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  return m ? `rgb(${m[1]}, ${m[2]}, ${m[3]})` : cssColor;
};

/** <Hotword> の描画完了時に呼ばれ、ページ上のホットワード色から凡例を構築する */
const syncHotwordHeatScales = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const tags = Array.from(
        document.querySelectorAll<HTMLElement>('.word-cloud .word-tag'),
      );
      const bySize = new Map<number, string>();
      tags.forEach((el) => {
        const sizeCls = Array.from(el.classList).find((c) => c.startsWith('word-tag-size-'));
        if (!sizeCls) return;
        const size = Number(sizeCls.replace('word-tag-size-', ''));
        if (bySize.has(size)) return;
        bySize.set(size, toSolidRgb(getComputedStyle(el).backgroundColor));
      });
      // 大きい（ホット）順
      const sortedSizes = [...bySize.keys()].sort((a, b) => b - a);
      hotwordHeatScales.value = sortedSizes.map((size) => ({
        color: bySize.get(size) ?? '',
        label: HOTWORD_SIZE_LABELS[size] ?? `SIZE ${size}`,
      }));
    });
  });
};
const youtubeDataTipVisible = ref(false);
const instagramDataTipVisible = ref(false);
const twitterDataTipVisible = ref(false);
const trendsDataTipVisible = ref(false);
const ratingRulesPopoverVisible = ref(false);

/** Google トレンド カード：本片の公開日（YYYY-MM-DD、グラフの縦線表示用） */
const trendsOpenDate = computed<string | undefined>(() => getMainMovieOpenDate() || undefined);

/** Google トレンド カード：選択期間（YYYY-MM-DD、両端含む）。期間未選択時は undefined（背景強調なし） */
const trendsPeriod = computed<{ start?: string; end?: string }>(() => {
  // 公開前模式没有期间选择：即使期间框里残留公開後的旧值，也不在图上高亮「選択期間」（DEV-14891）
  if (isPreRelease.value) return {};
  const range = duration.value as any[];
  if (!Array.isArray(range) || range.length < 2 || !range[0] || !range[1]) return {};
  return {
    start: dayjs(range[0]).format('YYYY-MM-DD'),
    end: dayjs(range[1]).format('YYYY-MM-DD'),
  };
});

const onEngagementCardMouseLeave = () => {
  ratingRulesPopoverVisible.value = false;
  showRatingRules.value = false;
};

const resetSocialStats = () => {
  socialStats.value = defaultSocialStats();
};

const applySocialStatsPayload = (payload: any) => {
  const yt = payload?.youtube || {};
  const ig = payload?.instagram || {};
  const tw = payload?.twitter || {};
  socialStats.value = {
    youtube: {
      total: toSocialNumber(yt.total),
      dayRate: toSocialNumber(yt.dayRate),
      weekRate: toSocialNumber(yt.weekRate),
    },
    instagram: {
      total: toSocialNumber(ig.total),
      hashtag: ig.hashtag ?? null,
      dayRate: toSocialNumber(ig.dayRate),
      weekRate: toSocialNumber(ig.weekRate),
    },
    twitter: {
      total: toSocialNumber(tw.total),
      hashtag: tw.hashtag ?? null,
      dayRate: toSocialNumber(tw.dayRate),
      weekRate: toSocialNumber(tw.weekRate),
      // 投稿密集度（日次）（0~100）。非数値/欠落は toSocialNumber により null に正規化
      densityIndex: toSocialNumber(tw.densityIndex),
    },
  };
};

const fetchSocialStats = () => {
  // 公開前模式没有期间：后端 total 取 endTime 当日及之前的最新累计值，start/end 都传「现在」即可拿到最新数据
  const now = Date.now();
  const range = isPreRelease.value
    ? { startTime: now, endTime: now }
    : (duration.value && duration.value.length >= 2
      ? {
        startTime: dayjs(duration.value[0]).startOf('day').valueOf(),
        endTime: dayjs(duration.value[1]).add(1, 'day').startOf('day').valueOf(),
      }
      : null);
  if (!movieId.value || !range) {
    resetSocialStats();
    return;
  }
  const seq = panelRequestSeq;
  const requestedMovieId = movieId.value;
  getSocialStatsService({
    movieId: requestedMovieId,
    ...range,
  })
    .then((res: any) => {
      // 已切换作品/模式时丢弃旧响应，避免残留其他作品的数据
      if (seq !== panelRequestSeq || movieId.value !== requestedMovieId) return;
      const payload = pickSocialStatsPayload(res);
      if (payload) applySocialStatsPayload(payload);
      else resetSocialStats();
    })
    .catch(() => {
      if (seq !== panelRequestSeq) return;
      resetSocialStats();
    });
};

/** 仅 isFuture===true 为预测点；null/false 不当作未来（避免误画虚线） */
const isDailyBoxOfficeFuturePoint = (_timestamp: number, source: any) => {
  return source?.isFuture === true;
};

const DAILY_BOX_OFFICE_SERIES_COLORS = [
  '#10B981',
  '#3B82F6',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#F97316',
];

const buildDailyBoxOfficeFillGradient = (context: { chart: Chart }) => {
  const chart = context.chart;
  const cctx = chart?.ctx;
  const { chartArea } = chart;
  if (!cctx || !chartArea) return 'rgba(16, 185, 129, 0.12)';
  const g = cctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  g.addColorStop(0, 'rgba(16, 185, 129, 0.28)');
  g.addColorStop(1, 'rgba(16, 185, 129, 0)');
  return g;
};

/** 历史段渐变填充 + 预测段虚线无填充；销毁前须走 runChartTeardown */
const buildDailyBoxOfficeChartDatasets = (
  values: (number | null)[],
  isFutureFlags?: boolean[],
  options?: { label?: string; color?: string; fill?: boolean },
) => {
  const lineColor = options?.color || '#10B981';
  const label = options?.label || 'BoxOfficeAI 予測';
  const allowFill = options?.fill !== false;
  const base = {
    label,
    borderColor: lineColor,
    borderWidth: 2,
    tension: 0.35,
    pointRadius: 3,
    pointHoverRadius: 6,
    pointBackgroundColor: lineColor,
    interaction: DP_CHART_INTERACTION,
    spanGaps: false,
  };
  const flags = isFutureFlags?.length === values.length
    ? isFutureFlags
    : values.map(() => false);
  const firstFutureIdx = flags.findIndex(Boolean);

  if (firstFutureIdx === -1) {
    return [{
      ...base,
      data: values,
      fill: allowFill,
      backgroundColor: allowFill ? buildDailyBoxOfficeFillGradient : 'transparent',
    }];
  }
  if (firstFutureIdx === 0) {
    return [{
      ...base,
      data: values,
      fill: false,
      borderDash: [6, 4],
    }];
  }

  // 首个 isFuture===true 的点起为预测：实线画到其前一日，
  // 虚线从前一日起接上，使「前一日→当日」区间视觉上为虚线（如 7/15→7/16）
  const boundaryIdx = firstFutureIdx - 1;
  const histValues = values.map((v, i) => (i <= boundaryIdx ? v : null));
  const futureValues = values.map((v, i) => (i >= boundaryIdx ? v : null));
  return [
    {
      ...base,
      data: histValues,
      fill: allowFill,
      backgroundColor: allowFill ? buildDailyBoxOfficeFillGradient : 'transparent',
      spanGaps: false,
      order: 1,
    },
    {
      ...base,
      data: futureValues,
      fill: false,
      borderDash: [6, 4],
      spanGaps: false,
      order: 0,
    },
  ];
};

const dailyBoxOfficeLegendLabelFilter = (
  item: { datasetIndex?: number; text?: string },
  chartData: { datasets: { label?: string }[] },
) => {
  const idx = item.datasetIndex ?? 0;
  const ds = chartData.datasets[idx];
  if (!ds) return true;
  const firstIdx = chartData.datasets.findIndex((d) => d.label === ds.label);
  return firstIdx === idx;
};

/** 历史实线 + 预测虚线为两个 dataset 但共用一个 legend，点击时同步显隐 */
const dailyBoxOfficeLegendOnClick = (
  _e: MouseEvent,
  legendItem: { datasetIndex?: number },
  legend: { chart: Chart },
) => {
  const chart = legend.chart;
  const clickedIdx = legendItem.datasetIndex ?? 0;
  const label = chart.data.datasets[clickedIdx]?.label;
  if (!label) return;
  const indices: number[] = [];
  chart.data.datasets.forEach((ds, i) => {
    if (ds.label === label) indices.push(i);
  });
  const anyVisible = indices.some((i) => chart.isDatasetVisible(i));
  indices.forEach((i) => {
    chart.setDatasetVisibility(i, !anyVisible);
  });
  chart.update();
};

const initDailyBoxOfficeChart = async (
  retry = 0,
  opts?: { fetchId?: number; activateId?: number },
) => {
  if (opts?.fetchId != null && opts.fetchId !== dailyBoxOfficeFetchId) return;
  if (opts?.activateId != null && opts.activateId !== dailyBoxOfficeActivateId) return;
  if (!clientV2ChartsMounted.value) return;
  const pack = dailyBoxOfficeChartPack.value;
  if (!pack || pack.labels.length === 0) {
    await disposeDailyBoxOfficeChartForInit();
    return;
  }
  const canvas = dailyBoxOfficeChartRef.value;
  if (!canvas) {
    if (retry < 2) nextTick(() => void initDailyBoxOfficeChart(retry + 1, opts));
    return;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  await disposeDailyBoxOfficeChartForInit();
  enableChartCanvas(canvas);

  const seriesList = Array.isArray(pack.series) && pack.series.length
    ? pack.series
    : [{
      label: 'BoxOfficeAI 予測',
      values: pack.values,
      tooltipRows: pack.tooltipRows,
      isFutureFlags: pack.isFutureFlags,
    }];

  // multi-series: datasetIndex -> { seriesIndex, tooltipRows }
  const datasetMeta: { seriesIndex: number; tooltipRows: any[] }[] = [];
  const datasets = seriesList.flatMap((s: any, seriesIndex: number) => {
    const color = DAILY_BOX_OFFICE_SERIES_COLORS[seriesIndex % DAILY_BOX_OFFICE_SERIES_COLORS.length];
    const built = buildDailyBoxOfficeChartDatasets(
      s.values || [],
      s.isFutureFlags as boolean[] | undefined,
      {
        label: s.label,
        color,
        fill: seriesList.length === 1,
      },
    );
    built.forEach(() => {
      datasetMeta.push({ seriesIndex, tooltipRows: s.tooltipRows || [] });
    });
    return built;
  });

  const yTitle = pack.yUnit === '万円' ? '推定興収（万円）' : '推定興収（円）';
  const chart = new Chart(ctx, {
    type: 'line',
    data: {
      labels: pack.labels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...DP_CHART_ANIMATION },
      events: [...DP_CHART_EVENTS],
      transitions: { ...DP_CHART_TRANSITIONS },
      interaction: { mode: 'nearest', intersect: true },
      elements: { ...DP_CHART_ELEMENTS },
      plugins: {
        legend: {
          position: 'top',
          onClick: dailyBoxOfficeLegendOnClick,
          labels: {
            usePointStyle: true,
            pointStyle: 'circle' as const,
            filter: dailyBoxOfficeLegendLabelFilter,
          },
        },
        tooltip: {
          ...DP_CHART_TOOLTIP,
          displayColors: seriesList.length > 1,
          filter(tooltipItem) {
            return tooltipItem.parsed?.y != null;
          },
          callbacks: {
            title(tooltipItems) {
              if (!tooltipItems?.length) return '';
              const tip = tooltipItems[0];
              const idx = tip.dataIndex;
              const meta = datasetMeta[tip.datasetIndex ?? 0];
              const row = meta?.tooltipRows?.[idx];
              if (row?.date) return String(row.date);
              if (Array.isArray(pack.dates) && pack.dates[idx]) return String(pack.dates[idx]);
              return pack.labels?.[idx] != null ? String(pack.labels[idx]) : '';
            },
            // 每条悬停系列各自取 tooltipRows，不再回退到所选电影（series[0]）
            beforeBody(tooltipItems) {
              if (!tooltipItems?.length) return [];
              const seenSeries = new Set<number>();
              const lines: string[] = [];
              tooltipItems.forEach((tip) => {
                const meta = datasetMeta[tip.datasetIndex ?? 0];
                if (!meta) return;
                if (seenSeries.has(meta.seriesIndex)) return;
                seenSeries.add(meta.seriesIndex);
                const row = meta.tooltipRows?.[tip.dataIndex];
                if (!row || typeof row !== 'object') return;
                const seriesLabel = seriesList[meta.seriesIndex]?.label || tip.dataset?.label;
                if (seriesList.length > 1 && seriesLabel) {
                  lines.push(String(seriesLabel));
                }
                lines.push(
                  `興行収入: ${formatBoxOfficeWithUnit(row.boxOffice ?? row.maxBoxOffice, pack.yUnit)}`,
                  `上映回数: ${formatDailyBoxOfficeNumber(row.scheduleCount)} 回`,
                  `上映館数: ${formatDailyBoxOfficeNumber(row.cinemaCount)} 館`,
                );
              });
              return lines;
            },
            label() {
              return null;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: chartGridColor() },
          ticks: { maxRotation: 45, minRotation: 0 },
        },
        y: {
          beginAtZero: true,
          grid: { color: chartGridColor() },
          ticks: {
            callback(value) {
              const n = Number(value);
              if (Number.isNaN(n)) return `${value}`;
              return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
            },
          },
          title: {
            display: true,
            text: yTitle,
          },
        },
      },
    },
  }) as Chart;
  chart.options.interaction = { mode: 'nearest', intersect: true };
  dailyBoxOfficeChartInstance.value = chart;
};

const fetchDailyBoxOfficePrediction = () => {
  if (!movieId.value) return;
  bumpDailyBoxOfficeActivateId();
  const fetchId = ++dailyBoxOfficeFetchId;
  const requestedMovieId = movieId.value;

  const run = async () => {
    await remountDailyBoxOfficeCanvas();
    if (fetchId !== dailyBoxOfficeFetchId) return;
    dailyBoxOfficeLoading.value = true;
    dailyBoxOfficeChartPack.value = null;
    try {
      const res: any = await getDailyBoxOfficeService({
        movieId: requestedMovieId,
        ...(duration.value && duration.value.length >= 2
          ? {
            dateFrom: dayjs(duration.value[0]).startOf('day').valueOf(),
            dateTo: dayjs(duration.value[1]).add(1, 'day').startOf('day').valueOf(),
          }
          : {}),
      });
      if (fetchId !== dailyBoxOfficeFetchId || movieId.value !== requestedMovieId) return;
      const list = pickDailyBoxOfficeList(res);
      const rows = list
        .map((item: any) => {
          const rawDate = item?.date ?? item?.day;
          const d = rawDate != null ? dayjs(rawDate) : null;
          const v = Number(
            item?.boxOffice ?? item?.maxBoxOffice ?? item?.predictValue ?? item?.prediction ?? item?.amount ?? item?.value,
          );
          if (!d || !d.isValid() || v == null || Number.isNaN(v)) return null;
          return { t: d.valueOf(), v, source: item };
        })
        .filter(Boolean) as { t: number; v: number; source: any }[];
      rows.sort((a, b) => a.t - b.t);
      if (!rows.length) {
        dailyBoxOfficeChartPack.value = null;
      } else {
        const maxV = Math.max(...rows.map((r) => r.v), 0);
        const scaleToMan = maxV >= 100000;
        const labels = rows.map((r) => dayjs(r.t).format('MM/DD'));
        const isFutureFlags = rows.map((r) => isDailyBoxOfficeFuturePoint(r.t, r.source));
        dailyBoxOfficeChartPack.value = {
          labels,
          values: scaleToMan ? rows.map((r) => r.v / 10000) : rows.map((r) => r.v),
          tooltipRows: rows.map((r) => r.source),
          isFutureFlags,
          yUnit: scaleToMan ? '万円' : '円',
        };
      }
      dailyBoxOfficeLoading.value = false;
      if (fetchId !== dailyBoxOfficeFetchId || movieId.value !== requestedMovieId) return;
      await nextTick();
      await waitAnimationFrames(2);
      await initDailyBoxOfficeChart(0, { fetchId });
    } catch {
      if (fetchId !== dailyBoxOfficeFetchId) return;
      await disposeDailyBoxOfficeChartForInit();
      dailyBoxOfficeChartPack.value = null;
      dailyBoxOfficeLoading.value = false;
    }
  };
  void run();
};

/** `/dp/dailyBoxOffice` の返却値から dailyBoxOfficeChartPack を組み立てる（fetchDailyBoxOfficePrediction 内のロジックと同一） */
const buildDailyBoxOfficeChartPackFromRes = (res: any) => {
  const list = pickDailyBoxOfficeList(res);
  const rows = list
    .map((item: any) => {
      const rawDate = item?.date ?? item?.day;
      const d = rawDate != null ? dayjs(rawDate) : null;
      const v = Number(
        item?.boxOffice ?? item?.maxBoxOffice ?? item?.predictValue ?? item?.prediction ?? item?.amount ?? item?.value,
      );
      if (!d || !d.isValid() || v == null || Number.isNaN(v)) return null;
      return { t: d.valueOf(), v, source: item };
    })
    .filter(Boolean) as { t: number; v: number; source: any }[];
  rows.sort((a, b) => a.t - b.t);
  if (!rows.length) return null;
  const maxV = Math.max(...rows.map((r) => r.v), 0);
  const scaleToMan = maxV >= 100000;
  const labels = rows.map((r) => dayjs(r.t).format('MM/DD'));
  const isFutureFlags = rows.map((r) => isDailyBoxOfficeFuturePoint(r.t, r.source));
  return {
    labels,
    values: scaleToMan ? rows.map((r) => r.v / 10000) : rows.map((r) => r.v),
    tooltipRows: rows.map((r) => r.source),
    isFutureFlags,
    yUnit: scaleToMan ? '万円' : '円',
  };
};

/** 表示用 ref の現在値から本片パネルのスナップショットを作成（getList 成功直後に呼ぶ） */
const snapshotCurrentPanelData = (): PrimaryPanelSnapshot => ({
  version1ListData: version1ListData.value,
  version2ListData: version2ListData.value,
  periodListData: periodListData.value,
  cinemaTotalTableData: cinemaTotalTableData.value,
  tableData: tableData.value,
  SeatTotalTableData: SeatTotalTableData.value,
  chartData: chartData.value,
  dailyBoxOfficeChartPack: dailyBoxOfficeChartPack.value,
});

/** スナップショットを表示用 ref に丸ごと反映し、既存のチャート初期化ロジックを流用して再描画する */
const applyPanelSnapshot = async (snapshot: PrimaryPanelSnapshot) => {
  // 日次予測チャートは既存の読み込み中オーバーレイ（dailyBoxOfficeLoading）を
  // そのまま流用し、切替中であることを示す
  dailyBoxOfficeLoading.value = true;
  try {
    version1ListData.value = snapshot.version1ListData;
    version2ListData.value = snapshot.version2ListData;
    periodListData.value = snapshot.periodListData;
    cinemaTotalTableData.value = snapshot.cinemaTotalTableData;
    tableData.value = snapshot.tableData;
    SeatTotalTableData.value = snapshot.SeatTotalTableData;
    chartData.value = snapshot.chartData;
    dailyBoxOfficeChartPack.value = snapshot.dailyBoxOfficeChartPack;

    await runChartTeardown();
    clientV2ChartsMounted.value = true;
    await nextTick();
    const seq = clientV2ActivateSeq;

    if (
      mainChartRef.value &&
      occupancyChartRef.value &&
      cinemaChartRef.value &&
      snapshot.chartData &&
      snapshot.chartData.length > 0
    ) {
      try {
        await initChartsAsync(snapshot.chartData, {
          mainChartRef: mainChartRef.value.getContext('2d'),
          occupancyChartRef: occupancyChartRef.value.getContext('2d'),
          cinemaChartRef: cinemaChartRef.value.getContext('2d'),
        }, seq);
      } catch (error) {
        console.error('対比電影チャート初期化失敗:', error);
      }
    }
    if (seq !== clientV2ActivateSeq) return;

    await remountDailyBoxOfficeCanvas();
    if (seq !== clientV2ActivateSeq) return;
    await waitAnimationFrames(2);
    if (snapshot.dailyBoxOfficeChartPack?.labels?.length) {
      const activateId = dailyBoxOfficeActivateId;
      try {
        await initDailyBoxOfficeChart(0, { activateId });
      } catch (error) {
        console.error('対比電影日次予測チャート初期化失敗:', error);
      }
    }
  } finally {
    dailyBoxOfficeLoading.value = false;
  }
};

/** 候補電影の興行データ（カード4枚＋推移分析3図＋日次予測）を取得しスナップショット化する */
const loadComparePanelData = async (
  targetMovieId: number,
  dateFrom: number,
  dateTo: number,
  displayDuration: Date[],
): Promise<PrimaryPanelSnapshot> => {
  const [scheduleRes, dailyRes] = await Promise.all([
    getScheduleCountService({ movieId: targetMovieId, dateFrom, dateTo }),
    getDailyBoxOfficeService({ movieId: targetMovieId, dateFrom, dateTo }).catch(() => null),
  ]);
  const {
    countTotal = 0,
    maxBoxOffice = 0,
    minBoxOffice = 0,
    boxOffice = 0,
    cinemaTotalNo = 0,
    version1List = [],
    version2List = [],
    seatTotalNo = 0,
    emptyRate = 0,
    morningCountTotal = 0,
    afternoonCountTotal = 0,
    nightCountTotal = 0,
    morningSeatTotalNo,
    afternoonSeatTotalNo,
    nightSeatTotalNo,
    morningEmptyRate,
    afternoonEmptyRate,
    nightEmptyRate,
  } = scheduleRes || {};

  return {
    version1ListData: version1List,
    version2ListData: version2List,
    periodListData: [
      { name: "午前", count: morningCountTotal || 0 },
      { name: "午後", count: afternoonCountTotal || 0 },
      { name: "夜", count: nightCountTotal || 0 },
    ],
    cinemaTotalTableData: [{ countTotal: cinemaTotalNo }],
    tableData: [{ countTotal }],
    SeatTotalTableData: [
      {
        seatTotalNo,
        maxBoxOffice,
        minBoxOffice,
        boxOffice,
        emptyRate,
        morningSeatTotalNo: morningSeatTotalNo || 0,
        afternoonSeatTotalNo: afternoonSeatTotalNo || 0,
        nightSeatTotalNo: nightSeatTotalNo || 0,
        morningEmptyRate,
        afternoonEmptyRate,
        nightEmptyRate,
      },
    ],
    chartData: fillMissingDates((scheduleRes as any)?.chatDataList || [], displayDuration),
    dailyBoxOfficeChartPack: dailyRes ? buildDailyBoxOfficeChartPackFromRes(dailyRes) : null,
  };
};

/** 本片の openDate を movieList から引く（movieList には openDate が含まれる） */
const getMainMovieOpenDate = () => movieList.value.find((m: any) => m.id === movieId.value)?.openDate;

/**
 * 本片の選択期間と本片 openDate との自然日差分（オフセット）を、候補電影自身の openDate に
 * そのまま適用して候補電影用の日付区間を算出する。
 * 例：本片openDate=2026-07-19、選択期間=2026-07-20〜21 → オフセット=1〜2日
 *     候補openDate=2026-07-10 → 候補期間=2026-07-11〜12
 */
const computeCompareDateRange = (candidateOpenDate?: string) => {
  const mainOpenDate = getMainMovieOpenDate();
  if (!mainOpenDate || !candidateOpenDate || !duration.value || duration.value.length < 2) return null;
  const mainOpenDay = dayjs(mainOpenDate).startOf('day');
  const offsetStart = dayjs(duration.value[0]).startOf('day').diff(mainOpenDay, 'day');
  const offsetEnd = dayjs(duration.value[1]).startOf('day').diff(mainOpenDay, 'day');
  const candidateOpenDay = dayjs(candidateOpenDate).startOf('day');
  const compareStart = candidateOpenDay.add(offsetStart, 'day');
  const compareEnd = candidateOpenDay.add(offsetEnd, 'day');
  return {
    displayDuration: [compareStart.toDate(), compareEnd.toDate()],
    dateFrom: compareStart.valueOf(),
    dateTo: compareEnd.add(1, 'day').startOf('day').valueOf(),
  };
};

/** 本片の確定成功後に呼び、ベンチマーク比較作品（最大3件）を取得してタブ候補にする */
const fetchBenchmarkMovies = () => {
  if (!movieId.value) return;
  const seq = panelRequestSeq;
  const requestedMovieId = movieId.value;
  benchmarkLoading.value = true;
  getBenchmarkMoviesService({ movieId: requestedMovieId })
    .then((res: any) => {
      // 已切换作品/模式时丢弃旧响应，避免给新作品挂上旧作品的对比 Tab
      if (seq !== panelRequestSeq || movieId.value !== requestedMovieId) return;
      benchmarkMovies.value = Array.isArray(res) ? res.slice(0, 3) : [];
    })
    .catch((error) => {
      if (seq !== panelRequestSeq) return;
      console.error('ベンチマーク比較作品取得失敗:', error);
      benchmarkMovies.value = [];
    })
    .finally(() => {
      if (seq !== panelRequestSeq) return;
      benchmarkLoading.value = false;
    });
};

/** 対比電影タブの切替：キャッシュ済みなら再リクエストせず即反映、未取得なら偏移量換算して取得する */
const switchMovieTab = async (tabKey: 'main' | number) => {
  if (activeMovieTab.value === tabKey || panelSwitching.value) return;
  panelSwitching.value = true;
  try {
    const cached = movieDataCache.get(tabKey);
    if (cached) {
      activeMovieTab.value = tabKey;
      await applyPanelSnapshot(cached);
      return;
    }
    if (tabKey === 'main') {
      // 本片は必ず一度は表示済みのはずだが、念のため現在値からスナップショットして復元する
      movieDataCache.set('main', snapshotCurrentPanelData());
      activeMovieTab.value = tabKey;
      return;
    }
    // 初回の他タブ切替前に、まだ上書きされていない本片の現在値を保全しておく
    if (!movieDataCache.has('main')) {
      movieDataCache.set('main', snapshotCurrentPanelData());
    }
    const candidate = benchmarkMovies.value[tabKey];
    if (!candidate) return;
    const range = computeCompareDateRange(candidate.openDate);
    if (!range) {
      ElMessage.error('作品のデータを取得できませんでした');
      return;
    }
    compareLoadingTab.value = tabKey;
    try {
      const snapshot = await loadComparePanelData(candidate.movieId, range.dateFrom, range.dateTo, range.displayDuration);
      movieDataCache.set(tabKey, snapshot);
      activeMovieTab.value = tabKey;
      await applyPanelSnapshot(snapshot);
    } catch (error) {
      console.error('対比電影データ取得失敗:', error);
      ElMessage.error('作品のデータを取得できませんでした');
    } finally {
      compareLoadingTab.value = null;
    }
  } finally {
    panelSwitching.value = false;
  }
};

const getList = () => {
  proxy.$refs.queryParamsRef.validate((valid: boolean) => {
    if (valid) {
      loading.value = true;
      showLoading();
      // 本次確定的请求序号：之后切换作品/模式时，本次发出的请求响应会被丢弃
      const seq = ++panelRequestSeq;
      // 対比電影のタブ状態は本片データを取り直すたびにリセットする
      movieDataCache.clear();
      benchmarkMovies.value = [];
      benchmarkLoading.value = false;
      activeMovieTab.value = 'main';
      compareLoadingTab.value = null;
      panelSwitching.value = false;
      getScheduleCountService({
        movieId: movieId.value,
        ...(duration.value
          ? {
            dateFrom: dayjs(duration.value[0]).startOf("day").valueOf(),
            dateTo: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
          }
          : {}),
      })
        .then((res: any) => {
          console.log('countTotal', res)
          // 已切换作品/模式时丢弃旧响应
          if (seq !== panelRequestSeq) {
            loading.value = false;
            hideLoading();
            return;
          }
          const requestedMovieId = movieId.value;
          getMovieReviewService({
            movieId: requestedMovieId,
          }).then((reviewRes: any) => {
            if (seq !== panelRequestSeq || movieId.value !== requestedMovieId) return;
            // 保存当前的评论list
            applyMovieReview(reviewRes);
          });
          loading.value = false;
          hideLoading();
          // ベンチマーク比較作品（対比電影A/B/C）を取得
          fetchBenchmarkMovies();
          // 检查是否有新增的Version

          const {
            countTotal = 0,
            maxBoxOffice = 0,
            minBoxOffice = 0,
            boxOffice = 0,
            cinemaTotalNo = 0,
            version1List = [],
            version2List = [],
            seatTotalNo = 0,
            emptyRate = 0,
            morningCountTotal = 0,
            afternoonCountTotal = 0,
            nightCountTotal = 0,
            morningSeatTotalNo,
            afternoonSeatTotalNo,
            nightSeatTotalNo,
            morningEmptyRate,
            afternoonEmptyRate,
            nightEmptyRate,
          } = res || {};
          const difVersions1 = getDiffArrays(version1List, version1NameList);
          const difVersions2 = getDiffArrays(version2List, version2NameList);
          if (difVersions1 && difVersions1.length > 0) {
            appendVersion1Names.value = difVersions1;
          }
          if (difVersions2 && difVersions2.length > 0) {
            appendVersion2Names.value = difVersions2;
          }
          version1ListData.value = version1List;
          version2ListData.value = version2List;
          periodListData.value = [
            {
              name: "午前",
              count: morningCountTotal || 0,
            },
            {
              name: "午後",
              count: afternoonCountTotal || 0,
            },
            {
              name: "夜",
              count: nightCountTotal || 0,
            },
          ];
          cinemaTotalTableData.value = [{ countTotal: cinemaTotalNo }];
          // 当前作品数据已反映，显示 <main> 面板
          panelReady.value = true;
          fetchBoxOfficePrediction();
          // 「確定」のたびに AI 週次ダイジェストを取得し、「分析中」→ レポート表示を再生する
          loadReportDigest();
          tableData.value = [{ countTotal }];
          SeatTotalTableData.value = [
            {
              seatTotalNo,
              maxBoxOffice,
              minBoxOffice,
              boxOffice,
              emptyRate,
              morningSeatTotalNo: morningSeatTotalNo || 0,
              afternoonSeatTotalNo: afternoonSeatTotalNo || 0,
              nightSeatTotalNo: nightSeatTotalNo || 0,
              morningEmptyRate,
              afternoonEmptyRate,
              nightEmptyRate,
            },
          ];

          // set chart
          // const dates = chatDataList.map((item: { date: string }) => item.date);
          // const counts = chatDataList.map((item: { countTotal: number }) => item.countTotal);
          // const seats = chatDataList.map((item: { seatTotalNo: number }) => item.seatTotalNo);
          // const rates = chatDataList.map((item: { emptyRate: number}) => item.emptyRate);
          // 补充缺失日期的数据，用0填充
          const filledChatDataList = fillMissingDates(res.chatDataList || [], duration.value);
          chartData.value = filledChatDataList;
          // 兼容处理：确保 refs 存在且数据有效后再初始化图表
          nextTick(() => {
            void (async () => {
              await runChartTeardown();
              clientV2ChartsMounted.value = true;
              await nextTick();
              const seq = clientV2ActivateSeq;
              if (
                mainChartRef.value &&
                occupancyChartRef.value &&
                cinemaChartRef.value &&
                filledChatDataList &&
                filledChatDataList.length > 0
              ) {
                try {
                  await initChartsAsync(filledChatDataList, {
                    mainChartRef: mainChartRef.value.getContext('2d'),
                    occupancyChartRef: occupancyChartRef.value.getContext('2d'),
                    cinemaChartRef: cinemaChartRef.value.getContext('2d'),
                  }, seq);
                } catch (error) {
                  console.error('图表初始化失败:', error);
                }
              }
              if (seq !== clientV2ActivateSeq) return;
              await waitAnimationFrames(2);
              fetchDailyBoxOfficePrediction();
              fetchSocialStats();
            })();
          });
        })
        .catch((error) => {
          console.log(error);
          chartData.value = [];
          tableData.value = [{ countTotal: 0 }];
          void disposeDailyBoxOfficeChartForInit();
          dailyBoxOfficeChartPack.value = null;
          dailyBoxOfficeLoading.value = false;
          resetSocialStats();
          loading.value = false;
          hideLoading();
        });
    }
  });
  // axios
  //   .get(`/api/dp/scheduleCount?movieId=${movieId}&dateForm=${duration[0]}&dateTo=${duration[1]}`)
  //   .then((res: any) => {
  //     loading.value = false;
  //     const { countTotal = 0, version1List = [], version2List = [] } = res || {};
  //     versionList.value = [...version1List, ...version2List];
  //     tableData.value = [{ countTotal }]
  //   })
  //   .catch((error) => {
  //     loading.value = false;
  //   });
};
const getRate = (seatOccupiedNo: number, seatTotalNo: number) => {
  return seatTotalNo === 0
    ? "0%"
    : Math.round((seatOccupiedNo / seatTotalNo) * 100 * 10) / 10 + "%";
};
const getMovieListFn = () => {
  loading.value = true;
  showLoading();
  chartData.value = [];
  movieId.value = "";
  // 重新获取作品列表会使当前选中作品失效，同时收起旧作品的面板和对比 Tab，避免残留
  resetData();
  // 响应返回前模式已切换时，不反映旧模式的列表
  const requestedMode = releaseMode.value;
  getMovieListService({
    companyId,
    ...(requestedMode === 'pre'
      // 公開前：不传期间，由后端按「公开日 > JST 今天」筛选
      ? { preRelease: true }
      : duration.value && duration.value.length > 0
        ? {
          startTime: dayjs(duration.value[0]).startOf("day").valueOf(),
          endTime: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
        }
        : {}),
    page: 0,
    pageSize: 50,
  })
    .then((res: any) => {
      loading.value = false;
      hideLoading();
      if (releaseMode.value !== requestedMode) return;
      movieList.value = res;
      isShowMovieSelect.value = true;
      if (res.length < 50) {
        nextTick(() => {
          isFetch.value = false;
        });
      }
    })
    .catch((error) => {
      loading.value = false;
      hideLoading();
    });
};
const getAreaChart = () => {
  const { areaId } = areaQueryParams;
  proxy.$refs.areaQueryParamsRef.validate((valid: boolean) => {
    if (valid) {
      loading.value = true;
      showLoading();
      getAreaChartService({
        areaId,
        movieId: movieId.value,
        ...(duration.value
          ? {
            dateFrom: dayjs(duration.value[0]).startOf("day").valueOf(),
            dateTo: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
          }
          : {}),
      })
        .then((res) => {
          loading.value = false;
          hideLoading();
          areaTableData.value = res.areaScheduleList.flatMap(
            (area: {
              areaName: string;
              preFectureScheduleList: {
                prefectureId: string;
                prefectureName: string;
                countTotal: number;
                cinemaTotalNo: number;
                seatTotalNo: number;
                emptyRate: number;
              }[];
            }) =>
              area.preFectureScheduleList.map((prefecture, idx) => ({
                areaName: device.value === 'desktop' && idx === 0 ? area.areaName : device.value === 'mobile' ? area.areaName : "",
                prefectureName: prefecture.prefectureName,
                prefectureId: prefecture.prefectureId,
                countTotal: prefecture.countTotal,
                cinemaTotalNo: prefecture.cinemaTotalNo,
                seatTotalNo: prefecture.seatTotalNo,
                emptyRate: prefecture.emptyRate,
              })),
          );
          // append row alltotal
          areaTableData.value.push({
            areaName: "合計",
            prefectureName: "",
            countTotal: res.scheduleTotal,
            seatTotalNo: res.seatTotal,
            emptyRate: res.emptyRate,
            cinemaTotalNo: res.cinemaTotalNo,
          });
          // 补充缺失日期的数据，用0填充
          const filledAreaChatDataList = fillMissingDates(res.chatDataList || [], duration.value);
          areaChartData.value = filledAreaChatDataList;
          // 兼容处理：确保 refs 存在且数据有效后再初始化图表
          nextTick(() => {
            void (async () => {
              clientV2ChartsMounted.value = true;
              await nextTick();
              await stopRegionChartAnimators();
              if (
                mainRegionChartRef.value &&
                occupancyRegionChartRef.value &&
                cinemaRegionChartRef.value &&
                filledAreaChatDataList &&
                filledAreaChatDataList.length > 0
              ) {
                try {
                  initRegionCharts(filledAreaChatDataList, {
                    mainRegionChartRef: mainRegionChartRef.value.getContext('2d'),
                    occupancyRegionChartRef: occupancyRegionChartRef.value.getContext('2d'),
                    cinemaRegionChartRef: cinemaRegionChartRef.value.getContext('2d'),
                  });
                } catch (error) {
                  console.error('区域图表初始化失败:', error);
                }
              }
            })();
          });
        })
        .catch((err) => {
          loading.value = false;
          hideLoading();
          areaChartData.value = [];
          areaTableData.value = [];
        });
    }
  });
};
// 处理详细按钮点击
const handleDetail = (row: any) => {
  console.log("查看详细:", row);
  isShowDetail.value = true;
  detailLoading.value = true;
  // detailTableData.value = row.children;
  getPrefectureCountService({
    movieId: movieId.value,
    prefectureId: row.prefectureId,
    ...(duration.value
      ? {
        dateFrom: dayjs(duration.value[0]).startOf("day").valueOf(),
        dateTo: dayjs(duration.value[1]).add(1, "day").startOf("day").valueOf(),
      }
      : {}),
  }).then((res) => {
    detailLoading.value = false;
    if (!res.brandCinemaSchedules || res.brandCinemaSchedules.length === 0) {
      detailTableData.value = [];
      return;
    }
    // 扁平化数据：将嵌套的数组结构转换为扁平数组
    const flatData: any[] = [];

    res.brandCinemaSchedules.forEach((brand: any) => {
      // 先添加品牌级别的汇总行
      flatData.push({
        prefectureName: res.prefectureName,
        brandName: brand.brandName,
        cinemaName: "", // 品牌行不显示具体电影院
        countTotal: brand.countTotal || 0, // 使用 brand 的 countTotal（品牌汇总）
        seatTotalNo: brand.seatTotalNo || 0, // 使用 brand 的 seatTotalNo（品牌汇总）
        emptyRate: brand.emptyRate || 0, // 使用 brand 的 emptyRate（品牌汇总）
        morningCountTotal: brand.morningCountTotal || 0,
        afternoonCountTotal: brand.afternoonCountTotal || 0,
        nightCountTotal: brand.nightCountTotal || 0,
        morningSeatTotalNo: brand.morningSeatTotalNo || 0,
        afternoonSeatTotalNo: brand.afternoonSeatTotalNo || 0,
        nightSeatTotalNo: brand.nightSeatTotalNo || 0,
        morningEmptyRate: brand.morningEmptyRate || 0,
        afternoonEmptyRate: brand.afternoonEmptyRate || 0,
        nightEmptyRate: brand.nightEmptyRate || 0,
        isBrandRow: true, // 标记这是品牌行
        level: "brand", // 标记层级
        isFirstInGroup: true, // 标记这是组内第一行
      });

      // 再添加该品牌下所有电影院的具体行
      brand.prefectureCinemaSchedules.forEach((cinema: any, cinemaIndex: number) => {
        flatData.push({
          prefectureName: res.prefectureName,
          brandName: brand.brandName,
          cinemaName: cinema.cinemaName,
          countTotal: cinema.countTotal || 0, // 使用 cinema 的 countTotal（电影院具体数据）
          seatTotalNo: cinema.seatTotalNo || 0,
          emptyRate: cinema.emptyRate || 0,
          morningCountTotal: cinema.morningCountTotal || 0,
          afternoonCountTotal: cinema.afternoonCountTotal || 0,
          nightCountTotal: cinema.nightCountTotal || 0,
          morningSeatTotalNo: cinema.morningSeatTotalNo || 0,
          afternoonSeatTotalNo: cinema.afternoonSeatTotalNo || 0,
          nightSeatTotalNo: cinema.nightSeatTotalNo || 0,
          morningEmptyRate: cinema.morningEmptyRate || 0,
          afternoonEmptyRate: cinema.afternoonEmptyRate || 0,
          nightEmptyRate: cinema.nightEmptyRate || 0,
          isBrandRow: false, // 标记这是电影院行
          level: "cinema", // 标记层级
          isFirstInGroup: false, // 标记这不是组内第一行
        });
      });
    });

    detailTableData.value = flatData;
    console.log("扁平化后的数据:", flatData);
  });
  // 这里可以添加查看详细的逻辑，比如：
  // - 打开详情弹窗
  // - 跳转到详情页面
  // - 显示更多数据等
};
const selectPeriod_fn = () => {
  // 重置loadmore
  nextTick(() => {
    isFetch.value = true;
  });
  if (movieId.value) {
    movieId.value = "";
  }
  resetData();
};
const resetData = () => {
  tableData.value = [{ countTotal: 0 }];
  SeatTotalTableData.value = [];
  areaTableData.value = [
    {
      id: "empty",
      areaName: "",
      countTotal: 0,
      prefectureName: "",
      seatTotalNo: 0,
      emptyRate: 0,
      cinemaTotalNo: 0,
    },
  ];

  chartData.value = [];
  areaChartData.value = [];
  bumpDailyBoxOfficeActivateId();
  dailyBoxOfficeFetchId += 1;
  void remountDailyBoxOfficeCanvas();
  dailyBoxOfficeChartPack.value = null;
  dailyBoxOfficeLoading.value = false;
  resetSocialStats();
  hasExpandedPrefecture.value = false; // 重置展开状态
  hasExpandedBrand.value = false; // 重置映画館チェーン展开状态
  // 対比電影のタブ状態もリセット（作品/期間の再選択で無効化されるため）
  movieDataCache.clear();
  benchmarkMovies.value = [];
  benchmarkLoading.value = false;
  activeMovieTab.value = 'main';
  compareLoadingTab.value = null;
  panelSwitching.value = false;
  // DEV-14891：切换作品/期间/模式时收起旧作品面板，重新確定前不显示，避免残留其他作品的数据
  panelRequestSeq += 1;
  if (panelReady.value) {
    // 趁 canvas 还在 DOM 中先同步停止并销毁 chart，再随 <main> 卸载
    void detachClientV2Charts();
  }
  panelReady.value = false;
  cinemaTotalTableData.value = [];
  version1ListData.value = [];
  version2ListData.value = [];
  periodListData.value = [];
  applyMovieReview(null);
  boxOfficePrediction.value = null;
  boxOfficePredictionLoading.value = false;
  stopTyping();
  reportData.value = null;
  reportEmpty.value = false;
  reportAnalyzing.value = false;
};

/** 最終興行収入予測（DEV-14891）：GET /dp/boxOfficePrediction/latest 的响应；取得失败时为 null（卡片显示取得失败文案） */
const boxOfficePrediction = ref<any>(null);
const boxOfficePredictionLoading = ref(false);

/** 获取当前作品的最终票房预测。公開前／公開後共用，确定后调用；切换作品/模式后返回的旧响应丢弃 */
const fetchBoxOfficePrediction = () => {
  if (!movieId.value) return;
  const seq = panelRequestSeq;
  const requestedMovieId = movieId.value;
  boxOfficePrediction.value = null;
  boxOfficePredictionLoading.value = true;
  getBoxOfficePredictionService({ movieId: requestedMovieId })
    .then((res: any) => {
      if (seq !== panelRequestSeq || movieId.value !== requestedMovieId) return;
      boxOfficePrediction.value = res ?? null;
    })
    .catch(() => {
      if (seq !== panelRequestSeq) return;
      boxOfficePrediction.value = null;
    })
    .finally(() => {
      if (seq !== panelRequestSeq) return;
      boxOfficePredictionLoading.value = false;
    });
};

/** 把 POST /dp/movieReview 的响应反映到评价卡片；null / 空数组按「无评价」清空，不残留旧作品的值 */
const applyMovieReview = (reviewRes: any) => {
  const row = Array.isArray(reviewRes) ? reviewRes[0] : null;
  if (!row) {
    commentCount.value = 0;
    likeCount.value = 0;
    commentObj.value = {};
    return;
  }
  commentCount.value = row.rateCount;
  likeCount.value = row.wantToSee + row.haveSeen;
  commentObj.value = row;
};

/** 作品下拉的远程搜索／加载更多：公開前模式附加 preRelease=true（期间不传给 LazyLoadMovieSelect） */
const fetchMovieOptions = (params: any) => getMovieListService({
  ...params,
  ...(isPreRelease.value ? { preRelease: true } : {}),
});

/**
 * 切换「公開前／公開後」：清空作品选择、作品列表与面板数据，不残留另一模式的作品数据。
 * 公開前没有期间选择，切换后立即获取可选作品列表；公開後沿用原流程（期間 → 検索）。
 */
const switchReleaseMode = (mode: ReleaseMode) => {
  if (releaseMode.value === mode) return;
  releaseMode.value = mode;
  movieId.value = '';
  movieList.value = [];
  isShowMovieSelect.value = false;
  nextTick(() => {
    isFetch.value = true;
  });
  resetData();
  proxy?.$refs.queryParamsRef?.clearValidate?.();
  if (mode === 'pre') {
    getMovieListFn();
  }
};

/** 公開前模式的「確定」：不调用兴行/预约类接口，只获取评价、SNS、热词的最新数据 */
const getPreReleasePanel = () => {
  proxy.$refs.queryParamsRef.validate(async (valid: boolean) => {
    if (!valid) return;
    const seq = ++panelRequestSeq;
    const requestedMovieId = movieId.value;
    loading.value = true;
    showLoading();
    try {
      const reviewRes = await getMovieReviewService({ movieId: requestedMovieId }).catch(() => null);
      if (seq !== panelRequestSeq || movieId.value !== requestedMovieId) return;
      applyMovieReview(reviewRes);
      panelReady.value = true;
      fetchBoxOfficePrediction();
      // SNS 卡片只在 total > 0 时显示，无数据时不会以 0 占位
      fetchSocialStats();
      // 等 <main> 渲染（Hotword 挂载）后再取热词
      await nextTick();
      if (seq !== panelRequestSeq) return;
      hotwordRef.value?.fetchHotwords();
    } finally {
      loading.value = false;
      hideLoading();
    }
  });
};

/** 「確定」按钮：按模式分流 */
const handleConfirm = () => {
  if (isPreRelease.value) {
    getPreReleasePanel();
  } else {
    getList();
  }
};
const disabledDate = (time: Date) => {
  const targetDate = new Date("2025-03-16");
  return time < targetDate;
};
const getProgressPercentage = (item: any, number: number) => {
  if (!number) {
    return 0;
  }
  const { rate1Count = 0, rate2Count = 0, rate3Count = 0, rate4Count = 0, rate5Count = 0 } = item;
  return (number / (rate1Count + rate2Count + rate3Count + rate4Count + rate5Count)) * 100;
};
const getStarClass = (rating: number) => {
  // 判断rating是否为整数
  if (Number.isInteger(rating)) {
    return `star-${rating}`;
  }
  // 否则返回小数点后的第一位
  return `star-${Math.floor(rating)}-5`;
};
const getRatingCount = (rating: number) => {
  if (!rating) {
    return 0;
  }
  if (rating <= 1000) {
    return rating;
  }
  return (rating / 1000).toFixed(1) + "K";
};
// Chart refs
const mainChartRef = ref<HTMLCanvasElement | null>(null);
const mainChartInstance = ref<Chart | null>(null);

const occupancyChartRef = ref<HTMLCanvasElement | null>(null);
const occupancyChartInstance = ref<Chart | null>(null);

const cinemaChartRef = ref<HTMLCanvasElement | null>(null);
const cinemaChartInstance = ref<Chart | null>(null);

const mainRegionChartRef = ref<HTMLCanvasElement | null>(null);
const mainRegionChartInstance = ref<Chart | null>(null);

const occupancyRegionChartRef = ref<HTMLCanvasElement | null>(null);
const occupancyRegionChartInstance = ref<Chart | null>(null);

const cinemaRegionChartRef = ref<HTMLCanvasElement | null>(null);
const cinemaRegionChartInstance = ref<Chart | null>(null);

/** 分析チャート（全国 / エリア別）のモバイル全画面用キー */
type IndexChartKey = 'main' | 'occupancy' | 'cinema' | 'mainRegion' | 'occupancyRegion' | 'cinemaRegion';

const indexFullscreenChartKey = ref<IndexChartKey | null>(null);
const indexFullscreenClosing = ref(false);
/** 退出全屏动画时长（需 ≥ CSS 中 indexChartFsOut / backdrop out） */
const INDEX_FS_ANIM_MS = 720;

/**
 * iOS など screen.orientation.lock 非対応環境では全画面に入っても縦屏のままになる。
 * 全画面 + 縦屏のときだけ「横向きで見ると見やすくなります」ヒントを出し、
 * ユーザーにデバイス回転を促す（回転後は orientationchange -> resize で图表が自動再描画）。
 */
const indexFsShowRotateHint = ref(false);
function updateIndexFsRotateHint() {
  if (!indexFullscreenChartKey.value) {
    indexFsShowRotateHint.value = false;
    return;
  }
  indexFsShowRotateHint.value = window.matchMedia('(orientation: portrait)').matches;
}

function isFsDebugEnabled() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("dpFsDebug") === "1") return true;
    }
    return localStorage.getItem("dpFsDebug") === "1";
  } catch {
    return false;
  }
}

/**
 * 模擬器（非真机）での挙動を切り替える：模拟器判定の場合は
 * `screen.orientation.lock` を行わず、普通の fullscreen 表示だけにする。
 */
function isEmulatorLikeDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  const platform = ((navigator as any).platform || '').toString().toLowerCase();

  // iOS UA を偽装した desktop emulation の場合、
  // navigator.platform が iPhone/iPad/iPod にならないことが多い。
  // これを利用して「iPhone UA なのに platform が iPhone ではない」= 模擬端末と判定する。
  const uaIndicatesIos = ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod');
  const platformIndicatesIos =
    platform.includes('iphone') || platform.includes('ipad') || platform.includes('ipod');
  if (uaIndicatesIos && !platformIndicatesIos) return true;

  // 额外兜底：UA 像 iOS，但 platform 明显是桌面（mac/win/linux）=> 当作模拟器
  const platformLooksDesktop = platform.includes('mac') || platform.includes('win') || platform.includes('linux');
  if (uaIndicatesIos && platformLooksDesktop) return true;

  // さらにあなたが提示した iOS シミュレータ UA（精密一致）も追加で判定（オプション）
  const looksLikeProvidedIosSimulatorUa =
    ua.includes('iphone; cpu iphone os 18_5') &&
    ua.includes('mobile/15e148') &&
    ua.includes('safari/604.1');
  if (looksLikeProvidedIosSimulatorUa) return true;
  // browser 上で Build.* などが取れないため、UA の特徴から推測する
  const indicators = [
    'android sdk built for x86',
    'generic_x86',
    'x86_64',
    'emulator',
    'genymotion',
    'goldfish',
    'ranchu',
    'sdk_gphone',
    'sdk_google',
    'test-keys',
  ];
  return indicators.some((i) => ua.includes(i));
}

const shouldAutoRotateOnFullscreen = !isEmulatorLikeDevice();

function logFsDebug(prefix: string, extra?: Record<string, any>) {
  if (!isFsDebugEnabled()) return;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const platform = typeof navigator !== 'undefined' ? ((navigator as any).platform || '') : '';
  // eslint-disable-next-line no-console
  console.log(`[dp-chart-fs:${prefix}]`, {
    ua,
    platform,
    shouldAutoRotateOnFullscreen,
    ...extra,
  });
}

/** 全画面に入る直前の .chart-container サイズ（終了後に同じ表示寸法へ戻す） */
const indexFsLayoutSnapshot = new Map<IndexChartKey, { w: number; h: number }>();

function captureChartContainerLayout(key: IndexChartKey) {
  const canvas = getCanvasForKey(key);
  const container = canvas?.parentElement;
  if (!container) return;
  const r = container.getBoundingClientRect();
  const w = Math.round(r.width);
  const h = Math.round(r.height);
  if (w > 0 && h > 0) {
    indexFsLayoutSnapshot.set(key, { w, h });
  }
}

function getScreenOrientationApi(): (ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
  unlock?: () => void;
}) | null {
  return typeof screen !== 'undefined' && screen.orientation
    ? (screen.orientation as ScreenOrientation & {
        lock?: (orientation: string) => Promise<void>;
        unlock?: () => void;
      })
    : null;
}

/** 进入图表全屏：尽量锁定为横屏（需用户手势触发；iOS 等可能不支持） */
function lockLandscape() {
  const o = getScreenOrientationApi();
  if (!o?.lock) return;
  const candidates = ['landscape', 'landscape-primary', 'landscape-secondary'] as const;
  const run = (i: number) => {
    if (i >= candidates.length) return;
    o.lock!(candidates[i]).catch(() => run(i + 1));
  };
  run(0);
}

/** 退出图表全屏：尽量锁定为竖屏，失败则 unlock */
function lockPortrait() {
  const o = getScreenOrientationApi();
  if (!o) return;
  if (o.lock) {
    const candidates = ['portrait', 'portrait-primary', 'portrait-secondary'] as const;
    const run = (i: number) => {
      if (i >= candidates.length) {
        tryUnlock();
        return;
      }
      o!.lock!(candidates[i]).catch(() => run(i + 1));
    };
    run(0);
    return;
  }
  tryUnlock();

  function tryUnlock() {
    if (o?.unlock) {
      try {
        o.unlock();
      } catch {
        /* ignore */
      }
    }
  }
}

function getIndexChartCardEl(key: IndexChartKey): HTMLElement | null {
  return document.querySelector(`[data-index-chart-key="${key}"]`) as HTMLElement | null;
}

/**
 * 尝试进入原生全屏（更接近视频全屏），以提高安卓下触发横屏的成功率。
 * 注意：requestFullscreen 必须在用户手势触发的同步调用链内调用，
 * 所以这里不要放到 nextTick/rAF 里。
 */
function tryRequestDomFullscreenThenLockLandscape(key: IndexChartKey) {
  const autoRotate = shouldAutoRotateOnFullscreen;
  const el = getIndexChartCardEl(key);
  logFsDebug('enter', { key, autoRotate });
  // 模拟器/桌面仿真：仅使用页面内全屏视图，不进入浏览器原生全屏
  if (!autoRotate) {
    logFsDebug('enterSkipNativeFullscreen', { key, reason: 'emulatorDetected' });
    return;
  }
  if (!el) {
    if (autoRotate) lockLandscape();
    return;
  }
  const fn = (el as any).requestFullscreen as undefined | (() => Promise<void>);
  if (typeof fn !== 'function') {
    if (autoRotate) lockLandscape();
    return;
  }

  try {
    const p = fn.call(el);
    if (p && typeof (p as any).then === 'function') {
      (p as Promise<void>)
        .then(() => {
          if (autoRotate) lockLandscape();
        })
        .catch(() => {
          if (autoRotate) lockLandscape();
        });
    } else {
      if (autoRotate) lockLandscape();
    }
  } catch {
    if (autoRotate) lockLandscape();
  }
}

function getCanvasForKey(key: IndexChartKey): HTMLCanvasElement | null {
  const m: Record<IndexChartKey, typeof mainChartRef> = {
    main: mainChartRef,
    occupancy: occupancyChartRef,
    cinema: cinemaChartRef,
    mainRegion: mainRegionChartRef,
    occupancyRegion: occupancyRegionChartRef,
    cinemaRegion: cinemaRegionChartRef,
  };
  return m[key].value;
}

function getChartInstanceForKey(key: IndexChartKey): Chart | null {
  const m: Record<IndexChartKey, typeof mainChartInstance> = {
    main: mainChartInstance,
    occupancy: occupancyChartInstance,
    cinema: cinemaChartInstance,
    mainRegion: mainRegionChartInstance,
    occupancyRegion: occupancyRegionChartInstance,
    cinemaRegion: cinemaRegionChartInstance,
  };
  return m[key].value;
}

function resizeChartForKey(key: IndexChartKey) {
  const chart = getChartInstanceForKey(key);
  if (!chart) return;
  try {
    chart.resize();
  } catch {
    /* ignore */
  }
}

let fsViewportCleanup: (() => void) | null = null;

function detachFullscreenViewportListeners() {
  fsViewportCleanup?.();
  fsViewportCleanup = null;
}

function attachFullscreenViewportListeners() {
  detachFullscreenViewportListeners();
  let rafId = 0;
  const handler = () => {
    const k = indexFullscreenChartKey.value;
    // ヒントは即時反映（縦横の切り替えをその場で）
    updateIndexFsRotateHint();
    if (!k) return;
    // iOS などでは orientationchange 発火直後まだ viewport 寸法が未更新のことがある。
    // 次フレーム（layout 確定後）に resize することで、端末回転への图表追従を確実にする。
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      resizeChartForKey(k);
    });
  };
  window.addEventListener('orientationchange', handler);
  window.addEventListener('resize', handler);
  fsViewportCleanup = () => {
    if (rafId) cancelAnimationFrame(rafId);
    window.removeEventListener('orientationchange', handler);
    window.removeEventListener('resize', handler);
  };
}

function clearChartContainerSizeLock(container: HTMLElement | null) {
  if (!container) return;
  container.style.removeProperty('width');
  container.style.removeProperty('height');
  container.style.removeProperty('min-height');
  container.style.removeProperty('max-height');
  container.style.removeProperty('box-sizing');
}

/**
 * 全画面終了後：canvas の style を外し、入る前に記録したコンテナ寸法で 1 回だけ確定してから resize。
 * 複数回 resize や不安定な flex 高さのフィードバックで高さが積み上がるのを防ぐ。
 */
function resetCanvasStylesAndResize(key: IndexChartKey) {
  const canvas = getCanvasForKey(key);
  const chart = getChartInstanceForKey(key);
  const snap = indexFsLayoutSnapshot.get(key);
  indexFsLayoutSnapshot.delete(key);

  if (!canvas || !chart) return;

  canvas.style.removeProperty('width');
  canvas.style.removeProperty('height');

  const container = canvas.parentElement;

  const applyResize = () => {
    try {
      chart.resize();
    } catch {
      /* ignore */
    }
  };

  if (container && snap && snap.w > 0 && snap.h > 0) {
    container.style.boxSizing = 'border-box';
    container.style.width = `${snap.w}px`;
    container.style.height = `${snap.h}px`;
    container.style.minHeight = `${snap.h}px`;
    container.style.maxHeight = `${snap.h}px`;
  }

  requestAnimationFrame(() => {
    applyResize();
    if (snap && snap.w > 0 && snap.h > 0 && container) {
      requestAnimationFrame(() => {
        clearChartContainerSizeLock(container);
        applyResize();
      });
    }
  });
}

/** ルート離脱・破棄時：アニメーションなしで戻す */
function closeIndexFullscreenImmediate() {
  detachFullscreenViewportListeners();
  const key = indexFullscreenChartKey.value;
  logFsDebug('exitImmediate', { key, autoRotate: shouldAutoRotateOnFullscreen });
  indexFullscreenClosing.value = false;
  indexFsShowRotateHint.value = false;
  indexFullscreenChartKey.value = null;
  document.body.style.overflow = '';
  try {
    if (document.fullscreenElement) {
      // 退出原生全屏（如果当前有）
      document.exitFullscreen?.().catch(() => {});
    }
  } catch {
    /* ignore */
  }
  if (shouldAutoRotateOnFullscreen) lockPortrait();
  if (key) {
    nextTick(() => resetCanvasStylesAndResize(key));
  }
}

function openIndexFullscreen(key: IndexChartKey) {
  if (device.value !== 'mobile') return;
  captureChartContainerLayout(key);
  indexFullscreenClosing.value = false;
  indexFullscreenChartKey.value = key;
  document.body.style.overflow = 'hidden';
  attachFullscreenViewportListeners();
  // 进入全屏直後に縦屏か判定（iOS は锁屏不可なので提示を表示；Android は锁屏成功後に orientationchange で更新）
  updateIndexFsRotateHint();
  // 关键：尽量在用户点击链路里先进入原生全屏，再锁横屏（安卓更可靠）
  tryRequestDomFullscreenThenLockLandscape(key);
  nextTick(() => {
    requestAnimationFrame(() => {
      resizeChartForKey(key);
      requestAnimationFrame(() => resizeChartForKey(key));
    });
  });
}

function closeIndexFullscreen() {
  if (device.value !== 'mobile') return;
  const key = indexFullscreenChartKey.value;
  if (!key) return;
  logFsDebug('exit', { key, autoRotate: shouldAutoRotateOnFullscreen });
  indexFullscreenClosing.value = true;
  indexFsShowRotateHint.value = false;
  detachFullscreenViewportListeners();
  setTimeout(() => {
    indexFullscreenClosing.value = false;
    indexFullscreenChartKey.value = null;
    document.body.style.overflow = '';

    // 先退出原生全屏，再尝试锁竖屏，最后再恢复图表尺寸
    const doRestore = () => {
      if (shouldAutoRotateOnFullscreen) lockPortrait();
      resetCanvasStylesAndResize(key);
    };

    try {
      if (document.fullscreenElement) {
        const p = document.exitFullscreen?.();
        if (p && typeof (p as any).then === 'function') {
          (p as Promise<void>).then(doRestore).catch(doRestore);
        } else {
          doRestore();
        }
      } else {
        doRestore();
      }
    } catch {
      doRestore();
    }
  }, INDEX_FS_ANIM_MS);
}

const createGradient = (ctx: any, colorStart: string, colorEnd: string) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, 400);
  gradient.addColorStop(0, colorStart);
  gradient.addColorStop(1, colorEnd);
  return gradient;
}

const destroyChartByCanvas = async (ctx: any) => {
  const canvas = ctx?.canvas;
  if (!canvas) return;
  await stopAndDestroyChart(Chart.getChart(canvas));
};

const regionChartInstances = () => [
  mainRegionChartInstance,
  occupancyRegionChartInstance,
  cinemaRegionChartInstance,
];

const regionChartCanvasRefs = () => [
  mainRegionChartRef,
  occupancyRegionChartRef,
  cinemaRegionChartRef,
];

/** 地区 chart 重建前单独销毁，避免打断全国 chart 的入场动画 */
const stopRegionChartAnimators = async () => {
  regionChartInstances().forEach((inst) => {
    inst.value = null;
  });
  for (const canvasRef of regionChartCanvasRefs()) {
    const canvas = canvasRef.value;
    if (!canvas) continue;
    await stopAndDestroyChart(Chart.getChart(canvas));
    enableChartCanvas(canvas);
  }
  await waitAnimationFrames(2);
};

/** 切换作品 / 离开页：顺序销毁全部 chart，避免并行 destroy 与 Animator 竞态 */
const stopClientV2ChartAnimators = async () => {
  syncAbortAllClientV2Charts();
  dailyBoxOfficeChartInstance.value = null;
  allChartInstances().forEach((inst) => {
    inst.value = null;
  });
  regionChartInstances().forEach((inst) => {
    inst.value = null;
  });
  await destroyChartsOnClientV2Canvases();
  await waitAnimationFrames(4);
};

const runChartTeardown = (afterTeardown?: () => void): Promise<void> => {
  syncAbortAllClientV2Charts();
  clientV2ActivateSeq += 1;
  dailyBoxOfficeFetchId += 1;
  bumpDailyBoxOfficeActivateId();
  const work = async () => {
    await stopClientV2ChartAnimators();
    afterTeardown?.();
  };
  const next = chartTeardownChain.then(work, work);
  chartTeardownChain = next.then(() => undefined, () => undefined);
  return next;
};

/** 离开页 / 进详情：先销毁（canvas 仍在 DOM），再卸载 canvas */
const detachClientV2Charts = async () => {
  const work = (async () => {
    await runChartTeardown();
    await destroyAllChartsUnderSelector('.client-v2-page');
    clientV2ChartsMounted.value = false;
    await nextTick();
    await waitAnimationFrames(2);
  })();
  markClientV2IndexChartsDisposing(work);
  await work;
};

// 初始化图表
const initMainChart = async (originData: any, ctx: any) => {
  if (!mainChartRef.value) return;
  enableChartCanvas(mainChartRef.value);

  // 如果已存在图表实例，先销毁
  if (mainChartInstance.value) {
    await stopAndDestroyChart(mainChartInstance.value);
    mainChartInstance.value = null;
  }

  const ctxMain = ctx;
  if (!ctxMain) return;
  await destroyChartByCanvas(ctxMain);
  enableChartCanvas(ctxMain.canvas);

  const dates = originData.map(d => d.date);
  const countTotal = originData.map(d => d.countTotal);
  const seatTotal = originData.map(d => d.seatTotalNo);

  mainChartInstance.value = new Chart(ctxMain, {
    type: 'bar',
    plugins: [transitionChartCompleteTooltipPlugin],
    data: {
      labels: dates,
      datasets: [
        {
          label: '上映回数',
          data: countTotal,
          backgroundColor: createGradient(ctxMain, 'rgba(0, 102, 255, 0.4)', 'rgba(0, 102, 255, 0)'),
          borderColor: '#0066FF',
          borderWidth: 2,
          yAxisID: 'y',
          order: 2,
          animation: { ...DP_CHART_ANIMATION },
        },
        {
          label: '総座席数',
          data: seatTotal,
          type: 'line',
          borderColor: '#EC4899',
          borderWidth: 3,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 8,
          tension: 0.4,
          yAxisID: 'y1',
          order: 1,
          animation: dpChartLineDatasetAnimation(2500, 5),
        },

      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...DP_CHART_ANIMATION },
      events: [...DP_CHART_EVENTS],
      transitions: { ...DP_CHART_TRANSITIONS },
      interaction: { ...DP_CHART_INTERACTION },
      elements: { ...DP_CHART_ELEMENTS },
      plugins: {
        legend: transitionChartLegend,
        tooltip: transitionChartTooltip,
      },
      scales: {
        y: { type: 'linear', display: true, position: 'left', title: chartAxisTitle('上映回数'), grid: { color: chartGridColorStrong() } },
        y1: { type: 'linear', display: true, position: 'right', title: chartAxisTitle('総座席数'), grid: { drawOnChartArea: false } }
      }
    }
  });
};
const initOccupancyChart = async (originData: any, ctx: any) => {
  if (!occupancyChartRef.value) return;
  enableChartCanvas(occupancyChartRef.value);
  if (occupancyChartInstance.value) {
    await stopAndDestroyChart(occupancyChartInstance.value);
    occupancyChartInstance.value = null;
  }
  const ctxOccupancy = ctx;
  const dates = originData.map(d => d.date);
  const occupancy = originData.map(d => d.emptyRate);
  if (!ctxOccupancy) return;
  await destroyChartByCanvas(ctxOccupancy);
  enableChartCanvas(ctxOccupancy.canvas);
  occupancyChartInstance.value = new Chart(ctxOccupancy, {
    type: 'line',
    data: {
      labels: dates,
      datasets: [
        {
          label: '充足率 (%)',
          data: occupancy,
          borderColor: '#10B981',
          fill: false,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 8,
          tension: 0.4,
          animation: dpChartLineDatasetAnimation(2200, 4),
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...DP_CHART_LINE_ANIMATION },
      events: [...DP_CHART_EVENTS],
      transitions: { ...DP_CHART_TRANSITIONS },
      interaction: { ...DP_CHART_INTERACTION },
      elements: { ...DP_CHART_ELEMENTS },
      plugins: {
        tooltip: { ...DP_CHART_TOOLTIP },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: chartGridColorStrong() }
        },
        x: {
          grid: { color: chartGridColorStrong() }
        }
      }
    }
  })
}
const initCinemaChart = async (originData: any, ctx: any) => {
  if (!cinemaChartRef.value) return;
  enableChartCanvas(cinemaChartRef.value);
  if (cinemaChartInstance.value) {
    await stopAndDestroyChart(cinemaChartInstance.value);
    cinemaChartInstance.value = null;
  }
  const ctxCinema = ctx;
  const dates = originData.map(d => d.date);
  const cinemas = originData.map(d => d.cinemaTotalNo);
  if (!ctxCinema) return;
  await destroyChartByCanvas(ctxCinema);
  enableChartCanvas(ctxCinema.canvas);
  cinemaChartInstance.value = new Chart(ctxCinema, {
    type: 'line',
    data: {
      labels: dates,
      datasets: [{
        label: '上映館数',
        data: cinemas,
        borderColor: '#8B5CF6',
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 8,
        tension: 0.4,
        animation: dpChartLineDatasetAnimation(2000, 3),
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 2000, easing: 'easeOutCubic' as const },
      events: [...DP_CHART_EVENTS],
      transitions: { ...DP_CHART_TRANSITIONS },
      interaction: { ...DP_CHART_INTERACTION },
      elements: { ...DP_CHART_ELEMENTS },
      plugins: {
        tooltip: { ...DP_CHART_TOOLTIP },
      },
      scales: {
        y: {
          grid: { color: chartGridColorStrong() },
          ticks: {
            stepSize: 1,
            callback: function (value: any) {
              if (Number.isInteger(value)) {
                return value;
              }
              return '';
            }
          }
        },
        x: {
          grid: { color: chartGridColorStrong() }
        }
      }
    }
  });
}

const initRegionMainChart = async (originData: any, ctx: any) => {
  if (!mainRegionChartRef.value) return;
  enableChartCanvas(mainRegionChartRef.value);

  // 如果已存在图表实例，先销毁
  if (mainRegionChartInstance.value) {
    await stopAndDestroyChart(mainRegionChartInstance.value);
    mainRegionChartInstance.value = null;
  }

  const ctxMain = ctx;
  if (!ctxMain) return;
  await destroyChartByCanvas(ctxMain);
  enableChartCanvas(ctxMain.canvas);

  const dates = originData.map(d => d.date);
  const countTotal = originData.map(d => d.countTotal);
  const seatTotal = originData.map(d => d.seatTotalNo);

  mainRegionChartInstance.value = new Chart(ctxMain, {
    type: 'bar',
    plugins: [transitionChartCompleteTooltipPlugin],
    data: {
      labels: dates,
      datasets: [
        {
          label: '上映回数',
          data: countTotal,
          backgroundColor: createGradient(ctxMain, 'rgba(0, 102, 255, 0.4)', 'rgba(0, 102, 255, 0)'),
          borderColor: '#0066FF',
          borderWidth: 2,
          yAxisID: 'y',
          order: 2,
          animation: { ...DP_CHART_ANIMATION },
        },
        {
          label: '総座席数',
          data: seatTotal,
          type: 'line',
          borderColor: '#EC4899',
          borderWidth: 3,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 8,
          tension: 0.4,
          yAxisID: 'y1',
          order: 1,
          animation: dpChartLineDatasetAnimation(2500, 5),
        },

      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...DP_CHART_ANIMATION },
      events: [...DP_CHART_EVENTS],
      transitions: { ...DP_CHART_TRANSITIONS },
      interaction: { ...DP_CHART_INTERACTION },
      elements: { ...DP_CHART_ELEMENTS },
      plugins: {
        legend: transitionChartLegend,
        tooltip: transitionChartTooltip,
      },
      scales: {
        y: { type: 'linear', display: true, position: 'left', title: chartAxisTitle('上映回数'), grid: { color: chartGridColorStrong() } },
        y1: { type: 'linear', display: true, position: 'right', title: chartAxisTitle('総座席数'), grid: { drawOnChartArea: false } }
      }
    }
  });
};
const initRegionOccupancyChart = async (originData: any, ctx: any) => {
  if (!occupancyRegionChartRef.value) return;
  enableChartCanvas(occupancyRegionChartRef.value);
  if (occupancyRegionChartInstance.value) {
    await stopAndDestroyChart(occupancyRegionChartInstance.value);
    occupancyRegionChartInstance.value = null;
  }
  const ctxOccupancy = ctx;
  const dates = originData.map(d => d.date);
  const occupancy = originData.map(d => d.emptyRate);
  if (!ctxOccupancy) return;
  await destroyChartByCanvas(ctxOccupancy);
  enableChartCanvas(ctxOccupancy.canvas);
  occupancyRegionChartInstance.value = new Chart(ctxOccupancy, {
    type: 'line',
    data: {
      labels: dates,
      datasets: [
        {
          label: '充足率 (%)',
          data: occupancy,
          borderColor: '#10B981',
          fill: false,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 8,
          tension: 0.4,
          animation: dpChartLineDatasetAnimation(2200, 4),
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...DP_CHART_LINE_ANIMATION },
      events: [...DP_CHART_EVENTS],
      transitions: { ...DP_CHART_TRANSITIONS },
      interaction: { ...DP_CHART_INTERACTION },
      elements: { ...DP_CHART_ELEMENTS },
      plugins: {
        tooltip: { ...DP_CHART_TOOLTIP },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: chartGridColorStrong() }
        },
        x: {
          grid: { color: chartGridColorStrong() }
        }
      }
    }
  })
}
const initRegionCinemaChart = async (originData: any, ctx: any) => {
  if (!cinemaRegionChartRef.value) return;
  enableChartCanvas(cinemaRegionChartRef.value);
  if (cinemaRegionChartInstance.value) {
    await stopAndDestroyChart(cinemaRegionChartInstance.value);
    cinemaRegionChartInstance.value = null;
  }
  const ctxCinema = ctx;
  const dates = originData.map(d => d.date);
  const cinemas = originData.map(d => d.cinemaTotalNo);
  if (!ctxCinema) return;
  await destroyChartByCanvas(ctxCinema);
  enableChartCanvas(ctxCinema.canvas);
  cinemaRegionChartInstance.value = new Chart(ctxCinema, {
    type: 'line',
    data: {
      labels: dates,
      datasets: [{
        label: '上映館数',
        data: cinemas,
        borderColor: '#8B5CF6',
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 8,
        tension: 0.4,
        animation: dpChartLineDatasetAnimation(2000, 3),
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 2000, easing: 'easeOutCubic' as const },
      events: [...DP_CHART_EVENTS],
      transitions: { ...DP_CHART_TRANSITIONS },
      interaction: { ...DP_CHART_INTERACTION },
      elements: { ...DP_CHART_ELEMENTS },
      plugins: {
        tooltip: { ...DP_CHART_TOOLTIP },
      },
      scales: {
        y: {
          grid: { color: chartGridColorStrong() },
          ticks: {
            stepSize: 1,
            callback: function (value: any) {
              if (Number.isInteger(value)) {
                return value;
              }
              return '';
            }
          }
        },
        x: {
          grid: { color: chartGridColorStrong() }
        }
      }
    }
  });
}
const initChartsAsync = async (originData: any, ctx: any, activateSeq?: number) => {
  if (!clientV2ChartsMounted.value) return;
  if (activateSeq != null && activateSeq !== clientV2ActivateSeq) return;
  await initMainChart(originData, ctx.mainChartRef);
  if (activateSeq != null && activateSeq !== clientV2ActivateSeq) return;
  await initOccupancyChart(originData, ctx.occupancyChartRef);
  if (activateSeq != null && activateSeq !== clientV2ActivateSeq) return;
  await initCinemaChart(originData, ctx.cinemaChartRef);
};

const initRegionChartsAsync = async (originData: any, ctx: any, activateSeq?: number) => {
  if (!clientV2ChartsMounted.value) return;
  if (activateSeq != null && activateSeq !== clientV2ActivateSeq) return;
  await initRegionMainChart(originData, ctx.mainRegionChartRef);
  if (activateSeq != null && activateSeq !== clientV2ActivateSeq) return;
  await initRegionOccupancyChart(originData, ctx.occupancyRegionChartRef);
  if (activateSeq != null && activateSeq !== clientV2ActivateSeq) return;
  await initRegionCinemaChart(originData, ctx.cinemaRegionChartRef);
};

// 初始化所有图表
const initCharts = (originData: any, ctx: any) => {
  const activateSeq = clientV2ActivateSeq;
  nextTick(() => {
    void initChartsAsync(originData, ctx, activateSeq);
  });
};
const initRegionCharts = (originData: any, ctx: any) => {
  const activateSeq = clientV2ActivateSeq;
  nextTick(() => {
    void initRegionChartsAsync(originData, ctx, activateSeq);
  });
};

/** keep-alive 返回：等 detail 销毁完毕 → 清理残留 → 挂载新 canvas 并重绘 */
const restoreClientV2ChartsFromCache = async () => {
  await waitClientV2DetailChartsIdle();
  await waitClientV2IndexChartsIdle();
  await chartTeardownChain;
  syncAbortAllClientV2Charts();
  await destroyAllChartsUnderSelector('.review-detail-page');
  await destroyAllChartsUnderSelector('.client-v2-page');
  await runChartTeardown();
  clientV2ChartsMounted.value = true;
  clientV2ChartsRemountKey.value += 1;
  dailyBoxOfficeCanvasKey.value += 1;
  await nextTick();
  await waitAnimationFrames(4);
  if (!clientV2ChartsMounted.value) return;
  const activateSeq = clientV2ActivateSeq;
  if (activateSeq !== clientV2ActivateSeq) return;

  if (
    chartData.value?.length > 0
    && mainChartRef.value
    && occupancyChartRef.value
    && cinemaChartRef.value
  ) {
    try {
      await initChartsAsync(chartData.value, {
        mainChartRef: mainChartRef.value.getContext('2d'),
        occupancyChartRef: occupancyChartRef.value.getContext('2d'),
        cinemaChartRef: cinemaChartRef.value.getContext('2d'),
      }, activateSeq);
    } catch (e) {
      console.error('client-v2 charts rebuild (national) failed:', e);
    }
  }
  if (activateSeq !== clientV2ActivateSeq) return;

  if (
    areaChartData.value?.length > 0
    && mainRegionChartRef.value
    && occupancyRegionChartRef.value
    && cinemaRegionChartRef.value
  ) {
    try {
      await initRegionChartsAsync(areaChartData.value, {
        mainRegionChartRef: mainRegionChartRef.value.getContext('2d'),
        occupancyRegionChartRef: occupancyRegionChartRef.value.getContext('2d'),
        cinemaRegionChartRef: cinemaRegionChartRef.value.getContext('2d'),
      }, activateSeq);
    } catch (e) {
      console.error('client-v2 charts rebuild (region) failed:', e);
    }
  }
  if (activateSeq !== clientV2ActivateSeq) return;

  const pack = dailyBoxOfficeChartPack.value;
  if (pack?.labels?.length && !dailyBoxOfficeLoading.value && dailyBoxOfficeChartRef.value) {
    const activateId = dailyBoxOfficeActivateId;
    try {
      await initDailyBoxOfficeChart(0, { activateId });
    } catch (e) {
      console.error('client-v2 daily box office chart rebuild failed:', e);
    }
  }
};

onActivated(() => {
  clientV2LeavingViaDetail = false;
  if (!clientV2NeedsChartRestore) return;
  clientV2NeedsChartRestore = false;
  nextTick(() => {
    void restoreClientV2ChartsFromCache();
  });
});

// keep-alive: 遷移前に全画面解除；进详情已在 goToReviewDetail detach
onDeactivated(() => {
  stopTyping();
  // keep-alive 離脱時、データ取得済みなら分析演出を完了状態にして復帰時に全文表示する。
  // まだ読込中（reportData 未取得）の場合は「分析中」表示を維持する。
  if (reportData.value) {
    stopTyping();
    reportAnalyzing.value = false;
    typedTotal.value = TYPE_TOTAL;
  }
  closeIndexFullscreenImmediate();
  if (!clientV2LeavingViaDetail) {
    clientV2NeedsChartRestore = true;
    void detachClientV2Charts();
  } else {
    clientV2ChartsMounted.value = false;
  }
  clientV2LeavingViaDetail = false;
});

// ─── AI 総合分析レポート（getWeekDigestAIServiceV2 連携 / aiV2）─────────────
interface DigestAnalysis {
  analysis?: string;
  suggestion?: string;
}
/** 各カテゴリは positives / risks の2配列を持つ（V2 データ構造）*/
interface DigestCategory {
  positives?: DigestAnalysis[];
  risks?: DigestAnalysis[];
}
interface WeekDigestAI {
  headline?: string;
  tldr?: string;
  /** 販売動向（analysis = 来客傾向分析 / suggestion = 平日プロモーション強化）*/
  salesAnalysis?: DigestCategory;
  /** SNS動向（analysis = 来客傾向分析 / suggestion = SNS施策の展開）*/
  socialAnalysis?: DigestCategory;
  /** エリア動向（analysis = 来客傾向分析 / suggestion = 地方エリア施策）*/
  areaAnalysis?: DigestCategory;
  dataLimitations?: string[];
}

/** API から取得した AI 週次ダイジェスト（null = 未取得 / データなし） */
const reportData = ref<WeekDigestAI | null>(null);
/** 「分析中」ローディング表示中か（API レスポンス待ちの間ずっと true） */
const reportAnalyzing = ref(false);
/** API は返ったが AI データが空だったか */
const reportEmpty = ref(false);
/**
 * 総合分析レポートの感情フィルター（Good / Bad トグル）。
 * 現状はヘッダーのトグル UI のみ。レポート本文の絞り込みは後続対応。
 * null = 絞り込みなし、'good' = ポジティブ中心、'bad' = リスク中心。
 */
type ReportFilter = 'good' | 'bad' | null;
const reportFilter = ref<ReportFilter>(null);
/**
 * Good 選択時に表示する「精度向上のため参考にさせていただきます」ヒント。
 * ボタンの選択状態（reportFilter）とは独立：Good が選ばれた瞬間に表示し、
 * 3 秒後に自動で消える。選択解除 / Bad 切替時は即座に消す。
 */
const reportTipVisible = ref(false);
let reportTipTimer: ReturnType<typeof setTimeout> | null = null;
const REPORT_TIP_DURATION = 3000;

/**
 * ヒントの表示位置を「画面内に収まるか」で自動決定する。
 * 既定は Figma 仕様どおり Good ボタンの右端揃え。そのままだと画面左（狭い端末）や
 * 画面右（Good が右寄せになるレイアウト）へはみ出す場合だけ、収まる位置へずらす。
 * 矢印は常に Good ボタンの中心を指すよう追従させる。
 */
const reportTipRef = ref<HTMLElement | null>(null);
const reportGoodBtnRef = ref<HTMLElement | null>(null);
const REPORT_TIP_EDGE_GAP = 8; // 画面端との最小余白

const placeReportTip = () => {
  const tip = reportTipRef.value;
  const btn = reportGoodBtnRef.value;
  if (!tip || !btn) return;

  const vw = document.documentElement.clientWidth;
  const gap = REPORT_TIP_EDGE_GAP;

  // 実寸を測る前に、画面幅を超えないよう上限だけ先に効かせる
  tip.style.right = 'auto';
  tip.style.maxWidth = `${Math.max(160, vw - gap * 2)}px`;

  const btnRect = btn.getBoundingClientRect();
  const tipWidth = tip.offsetWidth;

  // 既定位置（ボタン右端揃え）→ 画面内へクランプ
  const maxLeft = Math.max(gap, vw - gap - tipWidth);
  const left = Math.min(Math.max(btnRect.right - tipWidth, gap), maxLeft);

  // offsetParent は Good ボタン（position: relative）なので相対値に変換
  tip.style.left = `${left - btnRect.left}px`;

  const arrow = tip.querySelector<HTMLElement>('.vf-report-tip-arrow');
  if (arrow) {
    const btnCenter = btnRect.left + btnRect.width / 2 - left;
    const arrowCenter = Math.min(Math.max(btnCenter, 16), Math.max(16, tipWidth - 16));
    arrow.style.right = 'auto';
    arrow.style.left = `${arrowCenter - 6}px`; // 6 = 矢印幅 12 の半分
  }
};

// 表示中に画面幅が変わったら位置を取り直す（回転 / リサイズ対応）
watch(width, () => {
  if (reportTipVisible.value) void nextTick(placeReportTip);
});

const hideReportTip = () => {
  reportTipVisible.value = false;
  if (reportTipTimer) {
    clearTimeout(reportTipTimer);
    reportTipTimer = null;
  }
};
const toggleReportFilter = (next: 'good' | 'bad') => {
  const willActivate = reportFilter.value !== next;
  reportFilter.value = willActivate ? next : null;
  // Good を新しく選んだときだけヒントを表示して 3 秒タイマーを起動。
  // それ以外（Good 解除 / Bad 選択 / Bad 解除）は即座に消す。
  if (willActivate && next === 'good') {
    reportTipVisible.value = true;
    void nextTick(placeReportTip);
    if (reportTipTimer) clearTimeout(reportTipTimer);
    reportTipTimer = setTimeout(hideReportTip, REPORT_TIP_DURATION);
  } else {
    hideReportTip();
  }
};

/** カテゴリの positives + risks を1つの配列に正規化する（空判定用） */
const catItems = (cat?: DigestCategory | null): DigestAnalysis[] => [
  ...(cat?.positives ?? []),
  ...(cat?.risks ?? []),
];

// 表示順は 興行成績 → エリア → メディア・SNS。
// タイプライターの reveal 制御（blockStarted/segText）は typedTotal の変化に追従する必要があるが、
// 入れ子 v-for の中だと Vue 3.3 compiler の patchFlag 問題で更新されない。
// そのためテンプレートでは外側 v-for を使わずカードを明示展開し、リストは単一階層 v-for に保つ。
// （ブロックIDは buildTypingBlocks と一致: sales/area/social × pa/ra/ps/rs）

// ─── タイプライター演出 ────────────────────────────────────────
// API レスポンスから動的に組み立てたブロックを1文字ずつストリーミング表示する。
// 各ブロックは id → テキスト。TYPE_ORDER の順に typedTotal が進む。
let typingBlocks: Record<string, string> = {};
let TYPE_ORDER: string[] = [];
const TYPE_START: Record<string, number> = {};
let TYPE_TOTAL = 0;

/** 表示済みのグローバル文字数（0 → TYPE_TOTAL まで進む） */
const typedTotal = ref(0);
let typingTimer: ReturnType<typeof setInterval> | null = null;
let typingDelayTimer: ReturnType<typeof setTimeout> | null = null;

/** reportData からタイプライター用ブロックと表示順を組み立てる */
const buildTypingBlocks = (ai: WeekDigestAI) => {
  typingBlocks = {};
  TYPE_ORDER = [];
  const push = (id: string, text?: string) => {
    typingBlocks[id] = text ?? "";
    TYPE_ORDER.push(id);
  };
  // データの制約事項は最も目立つ先頭に表示するため、最初にタイプする
  (ai.dataLimitations ?? []).forEach((text, i) => {
    push(`limit${i}`, text);
  });
  push("tldr", ai.tldr);
  // 表示順（興行成績 → エリア → メディア・SNS）に合わせてタイプ。
  // 「分析｜施策」を1行に対応表示するため、ペア単位（analysis → suggestion）で連続してタイプする。
  // ブロックIDはテンプレートの key と一致させる（pa=positive analysis / ra=risk analysis / ps=positive suggestion / rs=risk suggestion）
  const cats: Array<[string, DigestCategory | undefined]> = [
    ["sales", ai.salesAnalysis],
    ["area", ai.areaAnalysis],
    ["social", ai.socialAnalysis],
  ];
  cats.forEach(([k, c]) => {
    // ポジティブ要因: 各ペア analysis → suggestion
    (c?.positives ?? []).forEach((s, i) => {
      push(`${k}-pa-${i}`, s.analysis);
      push(`${k}-ps-${i}`, s.suggestion);
    });
    // リスク: 各ペア analysis → suggestion
    (c?.risks ?? []).forEach((s, i) => {
      push(`${k}-ra-${i}`, s.analysis);
      push(`${k}-rs-${i}`, s.suggestion);
    });
  });
  // 各ブロックの開始位置と総文字数を計算
  for (const k of Object.keys(TYPE_START)) delete TYPE_START[k];
  let acc = 0;
  for (const id of TYPE_ORDER) {
    TYPE_START[id] = acc;
    acc += typingBlocks[id].length;
  }
  TYPE_TOTAL = acc;
};

/** ブロックの現在の表示文字列（先頭から typedTotal 文字までを切り出す） */
const segText = (id: string) => {
  const full = typingBlocks[id] ?? "";
  const n = typedTotal.value - (TYPE_START[id] ?? 0);
  if (n <= 0) return "";
  return n >= full.length ? full : full.slice(0, n);
};

/** v-html 用に HTML 特殊文字をエスケープ */
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * 文中の数値（カンマ・小数・符号・%付き含む）を正規表現で <strong> 強調した HTML を返す。
 * 例: "前週比-36.0%（150,477席）" → 数字部分を太字化。
 */
const boldNumbers = (text: string) =>
  escapeHtml(text ?? "").replace(
    /[+\-]?\d[\d,]*(?:\.\d+)?%?/g,
    '<strong class="vf-num">$&</strong>',
  );

/** カーソル（点滅キャレット）を出すブロックか */
const isTypingNow = (id: string) =>
  typedTotal.value < TYPE_TOTAL &&
  typedTotal.value >= (TYPE_START[id] ?? 0) &&
  typedTotal.value < (TYPE_START[id] ?? 0) + (typingBlocks[id]?.length ?? 0);

/** タイピングがそのブロックに到達したか（行の出現制御に使用） */
const blockStarted = (id: string) => typedTotal.value >= (TYPE_START[id] ?? 0);

const startTyping = () => {
  // タイプライター演出（1文字ずつ表示）は廃止。
  // レンダリング前に全文を一括表示し、出現演出は CSS アニメーション（vf-report-reveal）で行う。
  // typedTotal を即座に最大値にすることで segText は全文を返し、isTypingNow は常に false（キャレット非表示）になる。
  typedTotal.value = TYPE_TOTAL;
};

const stopTyping = () => {
  if (typingDelayTimer) {
    clearTimeout(typingDelayTimer);
    typingDelayTimer = null;
  }
  if (typingTimer) {
    clearInterval(typingTimer);
    typingTimer = null;
  }
};

/** AI オブジェクトが空（要約も各シグナルも無い）か */
const isDigestEmpty = (ai: WeekDigestAI) =>
  !ai.tldr &&
  !catItems(ai.salesAnalysis).length &&
  !catItems(ai.socialAnalysis).length &&
  !catItems(ai.areaAnalysis).length;

/** API レスポンスを反映。空ならデータなし表示、データありならタイプ開始 */
const applyReportData = (ai: WeekDigestAI | null | undefined) => {
  stopTyping();
  if (!ai || isDigestEmpty(ai)) {
    reportData.value = null;
    reportAnalyzing.value = false;
    reportEmpty.value = true;
    return;
  }
  reportEmpty.value = false;
  reportData.value = ai;
  buildTypingBlocks(ai);
  typedTotal.value = 0;
  reportAnalyzing.value = false;
  startTyping();
};

/**
 * AI 週次ダイジェストを取得する。
 * レスポンスが返るまで「分析中」アニメーションをループ表示し、
 * データが返れば反映、空なら「データなし」を表示する。
 * 「確定」ボタンでデータ取得が完了するたびに呼ばれ、毎回最初から再生される。
 */
const loadReportDigest = () => {
  stopTyping();
  typedTotal.value = 0;
  reportData.value = null;
  reportEmpty.value = false;
  reportAnalyzing.value = true; // レスポンス待ちの間ずっと「分析中」
  const seq = panelRequestSeq;
  const requestedMovieId = movieId.value;
  getWeekDigestAIServiceV2({
    // movieId: 202352,
    movieId: requestedMovieId,
  })
    .then((res: any) => {
      // 已切换作品/模式时丢弃旧响应，避免新作品显示旧作品的报告
      if (seq !== panelRequestSeq || movieId.value !== requestedMovieId) return;
      // AI ダイジェスト本体（V2）は res.aiV2 に入る。ラッパー（res.data.aiV2 / res.data）にも対応する。
      const hasDigest = (c: any) =>
        c && (c.tldr || c.headline || c.salesAnalysis || c.socialAnalysis || c.areaAnalysis);
      const ai = [res?.aiV2, res?.data?.aiV2, res?.data, res].find(hasDigest) ?? null;
      applyReportData(ai);
    })
    .catch(() => {
      if (seq !== panelRequestSeq || movieId.value !== requestedMovieId) return;
      applyReportData(null);
    });
};

onUnmounted(() => {
  stopTyping();
  closeIndexFullscreenImmediate();
  if (reportTipTimer) clearTimeout(reportTipTimer);
  void (async () => {
    if (mainChartInstance.value) {
      await stopAndDestroyChart(mainChartInstance.value);
      mainChartInstance.value = null;
    }
    await disposeDailyBoxOfficeChartForInit();
  })();
});
</script>

<template>
  <div class="client-v2-page">
    <!--  filter options  -->
    <el-form class="client-v2-filter-form" ref="queryParamsRef" :model="queryParams" :rules="rules"
      :label-width="labelWidth" :label-position="labelPosition">
      <el-row>
        <el-col>
          <el-form-item label="会社">
            <!-- companyName -->
            <span class="name">{{ companyName }}</span>
          </el-form-item>
        </el-col>
      </el-row>
      <!-- 公開前／公開後 模式切换（DEV-14891）：默认公開後，保持原有流程 -->
      <el-row :gutter="0" class="client-v2-no-wrap-row client-v2-mode-row">
        <el-col :span="24">
          <el-form-item label-width="0" class="client-v2-mode-form-item">
            <div class="compare-movie-tabs release-mode-tabs">
              <el-button type="primary" class="client-v2-action-btn compare-movie-tab-btn"
                :class="{ 'is-active': releaseMode === 'pre' }" @click="switchReleaseMode('pre')">公開前</el-button>
              <el-button type="primary" class="client-v2-action-btn compare-movie-tab-btn"
                :class="{ 'is-active': releaseMode === 'post' }" @click="switchReleaseMode('post')">公開後</el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
      <!-- 公開前模式不显示期间选择 -->
      <el-row v-if="!isPreRelease" :gutter="0" class="client-v2-no-wrap-row client-v2-date-row">
        <el-col class="client-v2-input-col client-v2-date-col" :span="20" :xs="18" :sm="18" :md="18" :lg="9" :xl="7">
          <el-form-item label="期間" class="client-v2-date-form-item">
            <ResponsiveDateRangePicker v-model="queryParams.duration" start-placeholder="" end-placeholder=""
              placeholder="期間を選択" :default-value="[new Date(), new Date()]" :disabled-date="disabledDate"
              @change="selectPeriod_fn" style="width: 100%;" />
          </el-form-item>
        </el-col>
        <el-col class="client-v2-filter-action" :span="4" :xs="6" :sm="6" :md="6" :lg="4" :xl="3">
          <el-form-item label-width="0" class="client-v2-search-form-item">
            <el-button type="primary" class="client-v2-action-btn" @click="getMovieListFn">検索</el-button>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row v-if="isShowMovieSelect" :gutter="0" class="client-v2-no-wrap-row client-v2-date-row client-v2-movie-row">
        <el-col class="client-v2-input-col client-v2-movie-col" :class="{ 'is-pre-release': isPreRelease }"
          :span="20" :xs="12" :sm="20" :md="18" :lg="9" :xl="7">
          <el-form-item label="作品" prop="movieId" class="client-v2-movie-form-item">
            <!-- <el-input v-model="queryParams.movieId" placeholder="名前で検索" /> -->
            <!-- <el-select-v2 v-model="movieId" placeholder="名前で検索" filterable :options="movieList" /> -->
            <!-- 公開前模式不传期间、带 preRelease=true 检索；:key 使切换模式时重建组件内部的候选列表 -->
            <LazyLoadMovieSelect :key="releaseMode" ref="lazyLoadRef" v-model:model-value="movieId" :list="movieList"
              :fetch-options="fetchMovieOptions" :company-id="companyId" :dates="isPreRelease ? [] : queryParams.duration"
              :is-fetch="isFetch" :show-open-date="isPreRelease" @change="resetData" valueKey="id" labelKey="name"
              :style="{ width: '100%' }" />
          </el-form-item>
        </el-col>
        <el-col class="client-v2-filter-action" :span="4" :xs="6" :sm="6" :md="6" :lg="4" :xl="3">
          <el-form-item label-width="0">
            <el-button type="primary" class="client-v2-action-btn" @click="handleConfirm">確定</el-button>
          </el-form-item>
        </el-col>
      </el-row>
      <!-- 公開前可选作品范围的提示：显示在「作品」下方（仅公開前） -->
      <p v-if="isShowMovieSelect && isPreRelease" class="release-mode-range-tip">{{ PRE_RELEASE_MOVIE_RANGE_TIP }}</p>
      <el-row v-if="!isPreRelease && (benchmarkLoading || benchmarkMovies.length > 0)" :gutter="0"
        class="client-v2-no-wrap-row client-v2-compare-row">
        <el-col :span="24">
          <el-form-item label-width="0" class="client-v2-compare-form-item">
            <div v-if="benchmarkLoading" class="compare-movie-tabs-loading">
              <i class="fas fa-spinner fa-spin"></i>
              <span>作品を検索中…</span>
            </div>
            <div v-else class="compare-movie-tabs">
              <el-button type="primary" class="client-v2-action-btn compare-movie-tab-btn"
                :class="{ 'is-active': activeMovieTab === 'main' }" :disabled="panelSwitching"
                @click="switchMovieTab('main')">本作品</el-button>
              <el-button v-for="(item, idx) in benchmarkMovies" :key="item.movieId" type="primary"
                class="client-v2-action-btn compare-movie-tab-btn"
                :class="{ 'is-active': activeMovieTab === idx }" :loading="compareLoadingTab === idx"
                :disabled="panelSwitching" @click="switchMovieTab(idx)">
                作品{{ COMPARE_MOVIE_LABELS[idx] }}
                <span v-if="typeof item.similarity === 'number'" class="compare-movie-similarity">（類似度{{ item.similarity }}%）</span>
              </el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <el-divider />
    <!-- 対比電影表示中はヘッダー（BOX OFFICE ANALYTICS バッジ含む）を非表示にする -->
    <!-- panelReady：確定后已反映当前作品数据时才显示（公開前不调 scheduleCount，不能用 cinemaTotalTableData 判断） -->
    <Header v-if="device !== 'mobile' && movieId && panelReady && activeMovieTab === 'main'"
      :movie-list="movieList" :movie-id="movieId" :duration="duration" :pre-release="isPreRelease"></Header>
    <main v-if="movieId && panelReady">
      <!-- AI 総合分析レポート（対比電影表示中・公開前模式不显示） -->
      <div v-if="activeMovieTab === 'main' && !isPreRelease" class="vf-card vf-report-card">
        <div class="vf-card-accent vf-card-accent--ai"></div>
        <div class="vf-report-head">
          <div class="vf-report-head-left">
            <span class="vf-ai-badge">AI</span>
            <h2 class="vf-report-title">総合分析レポート</h2>
          </div>
          <div class="vf-report-filter">
            <button
              ref="reportGoodBtnRef"
              type="button"
              class="vf-report-filter-btn vf-report-filter-btn--good"
              :class="{ 'is-active': reportFilter === 'good' }"
              :aria-pressed="reportFilter === 'good'"
              @click="toggleReportFilter('good')"
            >
              <i class="fas fa-thumbs-up vf-report-filter-icon"></i>
              <span class="vf-report-filter-text">Good</span>
              <Transition name="vf-report-tip">
                <span class="vf-report-tip" ref="reportTipRef" v-if="reportTipVisible">
                  <span class="vf-report-tip-arrow"></span>
                  精度向上のため参考にさせていただきます
                </span>
              </Transition>
            </button>
            <button
              type="button"
              class="vf-report-filter-btn vf-report-filter-btn--bad"
              :class="{ 'is-active': reportFilter === 'bad' }"
              :aria-pressed="reportFilter === 'bad'"
              @click="toggleReportFilter('bad')"
            >
              <i class="fas fa-thumbs-down vf-report-filter-icon"></i>
              <span class="vf-report-filter-text">Bad</span>
            </button>
          </div>
        </div>

        <!-- 分析中ローディング（Google AI 検索風）-->
        <div v-if="reportAnalyzing" class="vf-report-loading">
          <span class="vf-report-loading-text">分析中</span>
          <span class="vf-report-loading-dots"><i></i><i></i><i></i></span>
        </div>

        <!-- データなし -->
        <div v-else-if="reportEmpty" class="vf-report-empty">分析データがありません</div>

        <template v-else>
          <!-- データの制約事項（レポート最上部に目立つバナーとして表示）-->
          <div v-if="reportData?.dataLimitations?.length" class="vf-report-limitations">
            <div class="vf-report-limitations-head">
              <i class="fas fa-exclamation-circle"></i>
              <span>データの制約事項</span>
            </div>
            <ul class="vf-report-limitations-list">
              <li
                v-for="(_item, i) in (reportData?.dataLimitations ?? [])"
                :key="`limit${i}`"
                :class="{ 'vf-action-hidden': !blockStarted(`limit${i}`) }"
              >
                <span v-html="boldNumbers(segText(`limit${i}`))"></span><span v-if="isTypingNow(`limit${i}`)" class="vf-type-caret"></span>
              </li>
            </ul>
          </div>

          <!-- サマリー（tldr）-->
          <div v-if="reportData?.tldr" class="vf-report-summary">
            <div class="vf-report-subhead">
              <span class="vf-report-bar"></span>
              <h3>サマリー</h3>
            </div>
            <p class="vf-report-lead">
              <span v-html="boldNumbers(segText('tldr'))"></span><span v-if="isTypingNow('tldr')" class="vf-type-caret"></span>
            </p>
          </div>

          <!-- 分析結果情報（左）とアクションプラン（右）を行単位で左右対応。
               同じ行のカード（例: 興行成績 ↔ 平日プロモーション強化）は同じ高さで上端が揃う。-->
          <div class="vf-report-grid">
            <div class="vf-report-subhead">
              <span class="vf-report-bar"></span>
              <h3>分析結果情報</h3>
            </div>
            <div class="vf-report-subhead">
              <span class="vf-report-bar"></span>
              <h3>アクションプラン</h3>
            </div>

            <!-- 行1: 興行成績・上映回数・充足率 ↔ 平日プロモーション強化 -->
            <div class="vf-report-panel vf-report-panel--sales">
              <div class="vf-report-panel-head vf-report-panel-head--category">
                <i class="fas fa-chart-line"></i><h4>興行成績・上映回数・充足率</h4>
              </div>
              <template v-if="reportData?.salesAnalysis?.positives?.length">
                <div class="vf-report-panel-head vf-report-panel-head--green vf-report-panel-head--divided">
                  <i class="fas fa-thumbs-up"></i><h4>ポジティブ要因</h4>
                </div>
                <div class="vf-report-paragraphs">
                  <div
                    v-for="(_item, i) in (reportData?.salesAnalysis?.positives ?? [])"
                    :key="`sales-pa-${i}`"
                    class="vf-signal-item"
                    :class="{ 'vf-action-hidden': !blockStarted(`sales-pa-${i}`) }"
                  >
                    <p class="vf-signal-desc"><span v-html="boldNumbers(segText(`sales-pa-${i}`))"></span><span v-if="isTypingNow(`sales-pa-${i}`)" class="vf-type-caret"></span></p>
                  </div>
                </div>
              </template>
              <template v-if="reportData?.salesAnalysis?.risks?.length">
                <div class="vf-report-panel-head vf-report-panel-head--red vf-report-panel-head--divided">
                  <i class="fas fa-exclamation-triangle"></i><h4>リスク</h4>
                </div>
                <div class="vf-report-paragraphs">
                  <div
                    v-for="(_item, i) in (reportData?.salesAnalysis?.risks ?? [])"
                    :key="`sales-ra-${i}`"
                    class="vf-signal-item"
                    :class="{ 'vf-action-hidden': !blockStarted(`sales-ra-${i}`) }"
                  >
                    <p class="vf-signal-desc"><span v-html="boldNumbers(segText(`sales-ra-${i}`))"></span><span v-if="isTypingNow(`sales-ra-${i}`)" class="vf-type-caret"></span></p>
                  </div>
                </div>
              </template>
            </div>
            <div class="vf-report-panel vf-report-panel--sales">
              <div class="vf-report-panel-head vf-report-panel-head--category">
                <i class="fas fa-bullhorn"></i><h4>平日プロモーション強化</h4>
              </div>
              <ol class="vf-suggest-list">
                <li
                  v-for="(_item, i) in (reportData?.salesAnalysis?.positives ?? [])"
                  :key="`sales-ps-${i}`"
                  :class="{ 'vf-action-hidden': !blockStarted(`sales-ps-${i}`) }"
                >
                  <span v-html="boldNumbers(segText(`sales-ps-${i}`))"></span><span v-if="isTypingNow(`sales-ps-${i}`)" class="vf-type-caret"></span>
                </li>
                <li
                  v-for="(_item, i) in (reportData?.salesAnalysis?.risks ?? [])"
                  :key="`sales-rs-${i}`"
                  :class="{ 'vf-action-hidden': !blockStarted(`sales-rs-${i}`) }"
                >
                  <span v-html="boldNumbers(segText(`sales-rs-${i}`))"></span><span v-if="isTypingNow(`sales-rs-${i}`)" class="vf-type-caret"></span>
                </li>
              </ol>
            </div>

            <!-- 行2: エリア分析 ↔ 地方エリア施策 -->
            <div class="vf-report-panel vf-report-panel--area">
              <div class="vf-report-panel-head vf-report-panel-head--category">
                <i class="fas fa-map-marker-alt"></i><h4>エリア分析</h4>
              </div>
              <template v-if="reportData?.areaAnalysis?.positives?.length">
                <div class="vf-report-panel-head vf-report-panel-head--green vf-report-panel-head--divided">
                  <i class="fas fa-thumbs-up"></i><h4>ポジティブ要因</h4>
                </div>
                <div class="vf-report-paragraphs">
                  <div
                    v-for="(_item, i) in (reportData?.areaAnalysis?.positives ?? [])"
                    :key="`area-pa-${i}`"
                    class="vf-signal-item"
                    :class="{ 'vf-action-hidden': !blockStarted(`area-pa-${i}`) }"
                  >
                    <p class="vf-signal-desc"><span v-html="boldNumbers(segText(`area-pa-${i}`))"></span><span v-if="isTypingNow(`area-pa-${i}`)" class="vf-type-caret"></span></p>
                  </div>
                </div>
              </template>
              <template v-if="reportData?.areaAnalysis?.risks?.length">
                <div class="vf-report-panel-head vf-report-panel-head--red vf-report-panel-head--divided">
                  <i class="fas fa-exclamation-triangle"></i><h4>リスク</h4>
                </div>
                <div class="vf-report-paragraphs">
                  <div
                    v-for="(_item, i) in (reportData?.areaAnalysis?.risks ?? [])"
                    :key="`area-ra-${i}`"
                    class="vf-signal-item"
                    :class="{ 'vf-action-hidden': !blockStarted(`area-ra-${i}`) }"
                  >
                    <p class="vf-signal-desc"><span v-html="boldNumbers(segText(`area-ra-${i}`))"></span><span v-if="isTypingNow(`area-ra-${i}`)" class="vf-type-caret"></span></p>
                  </div>
                </div>
              </template>
            </div>
            <div class="vf-report-panel vf-report-panel--area">
              <div class="vf-report-panel-head vf-report-panel-head--category">
                <i class="fas fa-map-marked-alt"></i><h4>地方エリア施策</h4>
              </div>
              <ol class="vf-suggest-list">
                <li
                  v-for="(_item, i) in (reportData?.areaAnalysis?.positives ?? [])"
                  :key="`area-ps-${i}`"
                  :class="{ 'vf-action-hidden': !blockStarted(`area-ps-${i}`) }"
                >
                  <span v-html="boldNumbers(segText(`area-ps-${i}`))"></span><span v-if="isTypingNow(`area-ps-${i}`)" class="vf-type-caret"></span>
                </li>
                <li
                  v-for="(_item, i) in (reportData?.areaAnalysis?.risks ?? [])"
                  :key="`area-rs-${i}`"
                  :class="{ 'vf-action-hidden': !blockStarted(`area-rs-${i}`) }"
                >
                  <span v-html="boldNumbers(segText(`area-rs-${i}`))"></span><span v-if="isTypingNow(`area-rs-${i}`)" class="vf-type-caret"></span>
                </li>
              </ol>
            </div>

            <!-- 行3: メディア・SNS分析 ↔ SNS施策の展開 -->
            <div class="vf-report-panel vf-report-panel--social">
              <div class="vf-report-panel-head vf-report-panel-head--category">
                <i class="fas fa-hashtag"></i><h4>メディア・SNS分析</h4>
              </div>
              <template v-if="reportData?.socialAnalysis?.positives?.length">
                <div class="vf-report-panel-head vf-report-panel-head--green vf-report-panel-head--divided">
                  <i class="fas fa-thumbs-up"></i><h4>ポジティブ要因</h4>
                </div>
                <div class="vf-report-paragraphs">
                  <div
                    v-for="(_item, i) in (reportData?.socialAnalysis?.positives ?? [])"
                    :key="`social-pa-${i}`"
                    class="vf-signal-item"
                    :class="{ 'vf-action-hidden': !blockStarted(`social-pa-${i}`) }"
                  >
                    <p class="vf-signal-desc"><span v-html="boldNumbers(segText(`social-pa-${i}`))"></span><span v-if="isTypingNow(`social-pa-${i}`)" class="vf-type-caret"></span></p>
                  </div>
                </div>
              </template>
              <template v-if="reportData?.socialAnalysis?.risks?.length">
                <div class="vf-report-panel-head vf-report-panel-head--red vf-report-panel-head--divided">
                  <i class="fas fa-exclamation-triangle"></i><h4>リスク</h4>
                </div>
                <div class="vf-report-paragraphs">
                  <div
                    v-for="(_item, i) in (reportData?.socialAnalysis?.risks ?? [])"
                    :key="`social-ra-${i}`"
                    class="vf-signal-item"
                    :class="{ 'vf-action-hidden': !blockStarted(`social-ra-${i}`) }"
                  >
                    <p class="vf-signal-desc"><span v-html="boldNumbers(segText(`social-ra-${i}`))"></span><span v-if="isTypingNow(`social-ra-${i}`)" class="vf-type-caret"></span></p>
                  </div>
                </div>
              </template>
            </div>
            <div class="vf-report-panel vf-report-panel--social">
              <div class="vf-report-panel-head vf-report-panel-head--category">
                <i class="fas fa-share-alt"></i><h4>SNS施策の展開</h4>
              </div>
              <ol class="vf-suggest-list">
                <li
                  v-for="(_item, i) in (reportData?.socialAnalysis?.positives ?? [])"
                  :key="`social-ps-${i}`"
                  :class="{ 'vf-action-hidden': !blockStarted(`social-ps-${i}`) }"
                >
                  <span v-html="boldNumbers(segText(`social-ps-${i}`))"></span><span v-if="isTypingNow(`social-ps-${i}`)" class="vf-type-caret"></span>
                </li>
                <li
                  v-for="(_item, i) in (reportData?.socialAnalysis?.risks ?? [])"
                  :key="`social-rs-${i}`"
                  :class="{ 'vf-action-hidden': !blockStarted(`social-rs-${i}`) }"
                >
                  <span v-html="boldNumbers(segText(`social-rs-${i}`))"></span><span v-if="isTypingNow(`social-rs-${i}`)" class="vf-type-caret"></span>
                </li>
              </ol>
            </div>
          </div>
        </template>
      </div>
      <!-- 最终票房预测（DEV-14891 / DEV-11292）：公開前／公開後共用，暂时只显示档位，不显示预测值与区间；対比電影表示中は非表示 -->
      <template v-if="activeMovieTab === 'main'">
        <h2 class="section-title">
          <i class="fas fa-robot"></i>
          最終興行収入予測
        </h2>
        <div class="dashboard-grid final-prediction-grid">
          <div class="card card-final-prediction">
            <div class="card-header">
              <span>最終興行収入（AI予測）</span>
              <i class="fas fa-chart-line" style="color: var(--brand-color);"></i>
            </div>
            <FinalPredictionBody :prediction="boxOfficePrediction" :loading="boxOfficePredictionLoading" />
          </div>
        </div>
      </template>
      <!-- 興行データ・現在の予約状況：公開前模式完全不显示经营/预约数据（不以 0 占位） -->
      <template v-if="!isPreRelease">
      <!-- 興行データセクション（対比電影表示中はタイトルを対比電影A/B/Cに差し替え） -->
      <h2 class="section-title">
        <i class="fas fa-ticket-alt"></i>
        {{ primarySectionTitle }}
      </h2>
      <div class="dashboard-grid primary-panel-grid">
        <div v-if="panelSwitching" class="primary-panel-loading-overlay">
          <i class="fas fa-spinner fa-spin"></i>
          <span>読み込み中…</span>
        </div>
        <div class="card card-revenue">
          <div class="card-header">
            <span>興行収入（AI推測値）</span>
            <i class="fas fa-chart-line" style="color: var(--brand-color);"></i>
          </div>
          <slot>
            <RevenueRange :formatted-revenue="formattedRevenue">
            </RevenueRange>
          </slot>
        </div>
        <div class="card">
          <div class="card-header">
            <span>最大上映館数</span>
            <i class="fas fa-building" style="color: var(--accent-purple);"></i>
          </div>
          <div class="main-stat" style="color: var(--accent-purple);">{{ cinemaTotalTableData[0]?.countTotal }}</div>
          <div class="sub-stat">全国ロードショー</div>
        </div>
        <div class="card">
          <div class="card-header">
            <span>充足率 (Occupancy)</span>
            <i class="fas fa-percent" style="color: var(--accent-green);"></i>
          </div>
          <div class="main-stat" style="color: var(--accent-green);">{{ SeatTotalTableData[0]?.emptyRate }}%</div>
          <Occupancy :SeatTotalTableData="SeatTotalTableData"></Occupancy>
        </div>
        <div class="card">
          <div class="card-header">
            <span>上映回数 (Total)</span>
            <i class="fas fa-film" style="color: var(--accent-blue);"></i>
          </div>
          <div class="main-stat" style="color: var(--accent-blue);">{{ (tableData[0]?.countTotal ?? 0).toLocaleString()
            }}</div>
          <Total :periodListData="periodListData" :tableData="tableData" :version1ListData="version1ListData"
            :version2ListData="version2ListData"></Total>
        </div>
        <div class="card">
          <div class="card-header">
            <span>総座席数 (Seats)</span>
            <i class="fas fa-chair" style="color: var(--accent-pink);"></i>
          </div>
          <div class="main-stat" style="font-size:1.6rem; color: var(--accent-pink);">{{
            (SeatTotalTableData[0]?.seatTotalNo ?? 0).toLocaleString() }}</div>
          <SeatTotal :SeatTotalTableData="SeatTotalTableData"></SeatTotal>
        </div>
        <div v-if="activeMovieTab === 'main'" class="card card-audience">
          <div class="card-header">
            <span>観客属性 (Gender / Age)</span>
            <i class="fas fa-users" style="color: var(--accent-orange);"></i>
          </div>
          <AudiencePieChart
            :key="'audience-' + clientV2ChartsRemountKey"
            :movieId="movieId"
            :duration="duration"
          ></AudiencePieChart>
        </div>
      </div>
      <!-- 今後3日間 BoxOfficeAI 予測（日次、対比電影表示中もタイトルは差し替えない） -->
      <h2 class="section-title">
        <i class="fas fa-robot"></i>
        現在の予約状況
        <!-- <button
          class="visitor-forecast-entry-btn"
          :disabled="!movieId || !duration?.length"
          @click="goToVisitorForecast"
        >
          <i class="fas fa-users"></i>
          <span>来客数予測分析</span>
          <i class="fas fa-arrow-right"></i>
        </button> -->
      </h2>
      <div class="dashboard-grid daily-box-office-prediction-grid">
        <div class="card card-chart-lg card-daily-box-office-prediction">
          <div class="card-header">
            <span>興行収入</span>
            <i class="fas fa-chart-line" style="color: #10B981;"></i>
          </div>
          <div class="chart-container chart-container--daily-bo chart-container--daily-bo-host">
            <div
              v-if="dailyBoxOfficeLoading"
              class="daily-bo-chart-placeholder daily-bo-chart-placeholder--overlay"
            >
              読み込み中…
            </div>
            <div
              v-else-if="!dailyBoxOfficeChartPack || !dailyBoxOfficeChartPack.labels?.length"
              class="daily-bo-chart-placeholder daily-bo-chart-placeholder--overlay"
            >
              予測データがありません
            </div>
            <canvas
              v-if="clientV2ChartsMounted"
              :key="dailyBoxOfficeCanvasKey"
              ref="dailyBoxOfficeChartRef"
              class="daily-bo-chart-canvas"
            ></canvas>
          </div>
        </div>
      </div>
      </template>
      <!-- 評価・エンゲージメントセクション（レビュー・感情分析含む、対比電影表示中は非表示） -->
      <template v-if="activeMovieTab === 'main'">
      <h2 class="section-title">
        <i class="fas fa-heart"></i>
        評価・エンゲージメント
      </h2>
      <div class="dashboard-grid">
        <div
          class="card card-detail-hover card-engagement"
          @click="goToReviewDetail"
          @mouseleave="onEngagementCardMouseLeave"
        >
          <div class="card-detail-overlay" aria-hidden="true">
            <div class="card-detail-overlay__cta">
              <i class="fas fa-chart-bar"></i>
              <span>詳細を見る</span>
              <i class="fas fa-arrow-right card-detail-overlay__arrow"></i>
            </div>
          </div>
          <div class="card-header">
            <span style="display:flex;align-items:center;gap:8px;position:relative;z-index:11;" @click.stop>
              <el-tooltip
                :content="SOCIAL_CARD_TITLE.engagement"
                placement="top"
                trigger="hover"
                :show-after="200"
              >
                <span class="card-header-title-text">{{ SOCIAL_CARD_TITLE.engagement }}</span>
              </el-tooltip>
              <!-- PC: popover next to button; mobile: dialog -->
              <el-popover
                v-if="!isMobile"
                v-model:visible="ratingRulesPopoverVisible"
                placement="right"
                width="auto"
                trigger="click"
                popper-class="rating-rules-popover"
              >
                <template #reference>
                  <button class="rating-help-btn" title="評価基準を見る">?</button>
                </template>
                <template #default>
                  <div class="rating-rules-dialog-body">
                    <p class="rating-rules-dialog-intro">スコアに基づいて星数が決まります。</p>
                    <div class="rating-rules-list">
                      <div class="rating-rules-list__item">
                        <div class="rrl-stars"><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i></div>
                        <div class="rrl-info"><span class="rrl-range">スコア 5</span></div>
                      </div>
                      <div class="rating-rules-list__item">
                        <div class="rrl-stars"><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i></div>
                        <div class="rrl-info"><span class="rrl-range">スコア 4 〜 5 未満</span></div>
                      </div>
                      <div class="rating-rules-list__item">
                        <div class="rrl-stars"><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i></div>
                        <div class="rrl-info"><span class="rrl-range">スコア 3 〜 4 未満</span></div>
                      </div>
                      <div class="rating-rules-list__item">
                        <div class="rrl-stars"><i class="fas fa-star"></i><i class="fas fa-star"></i></div>
                        <div class="rrl-info"><span class="rrl-range">スコア 2 〜 3 未満</span></div>
                      </div>
                      <div class="rating-rules-list__item">
                        <div class="rrl-stars"><i class="fas fa-star"></i></div>
                        <div class="rrl-info"><span class="rrl-range">スコア 0 〜 2 未満</span></div>
                      </div>
                    </div>
                  </div>
                </template>
              </el-popover>
              <button v-else class="rating-help-btn" @click="showRatingRules = true" title="評価基準を見る">?</button>
            </span>
            <i class="fas fa-star" style="color: var(--brand-color);"></i>
          </div>
          <Comment :commentObj="commentObj"></Comment>
          <div class="card-detail-mobile-hint" v-if="isMobile" aria-hidden="true">
            <i class="fas fa-hand-pointer"></i>
            <span>タップして詳細を見る</span>
            <i class="fas fa-arrow-right"></i>
          </div>
        </div>
        <div
          v-if="hasSocialStatTotal(socialStats.youtube.total)"
          class="card card-detail-hover card-youtube"
          @click="goToReviewDetail('youtube')"
          @mouseleave="youtubeDataTipVisible = false"
        >
          <div class="card-detail-overlay" aria-hidden="true">
            <div class="card-detail-overlay__cta">
              <i class="fab fa-youtube"></i>
              <span>詳細を見る</span>
              <i class="fas fa-arrow-right card-detail-overlay__arrow"></i>
            </div>
          </div>
          <div class="card-header">
            <span
              class="card-header-title-with-help"
              @click.stop
            >
              <el-tooltip
                :content="SOCIAL_CARD_TITLE.youtube"
                placement="top"
                trigger="hover"
                :show-after="200"
              >
                <span class="card-header-title-text">{{ SOCIAL_CARD_TITLE.youtube }}</span>
              </el-tooltip>
              <el-popover
                v-model:visible="youtubeDataTipVisible"
                placement="top"
                width="auto"
                trigger="click"
                popper-class="social-data-source-popover"
              >
                <template #reference>
                  <button
                    type="button"
                    class="rating-help-btn"
                    title="データ出所"
                    @click.stop
                  >
                    ?
                  </button>
                </template>
                <template #default>
                  <p class="social-data-source-tip">{{ SOCIAL_DATA_SOURCE_TIP }}</p>
                </template>
              </el-popover>
            </span>
            <i class="fab fa-youtube card-header-platform-icon" style="color: #FF0000;"></i>
          </div>
          <div class="main-stat" style="color: #FF0000;">{{ formatYoutubeViewCount(socialStats.youtube.total) }}</div>
          <div class="sub-stat" style="margin-top: 8px;">
            <a href="https://www.youtube.com/watch?v=DAiq_4YWXow" target="_blank" rel="noopener noreferrer"
              style="color: inherit; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; transition: color 0.2s ease; cursor: pointer;">
              <!-- <i class="fas fa-play-circle"></i> 予告編動画 -->
            </a>
          </div>
          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-color);">
            <Youtube
              :day-rate="socialStats.youtube.dayRate"
              :week-rate="socialStats.youtube.weekRate"
            />
          </div>
          <div class="card-detail-mobile-hint" v-if="isMobile" aria-hidden="true">
            <i class="fas fa-hand-pointer"></i>
            <span>タップして詳細を見る</span>
            <i class="fas fa-arrow-right"></i>
          </div>
        </div>
        <div
          v-if="hasSocialStatTotal(socialStats.instagram.total)"
          class="card card-detail-hover card-instagram"
          @click="goToReviewDetail('instagram')"
          @mouseleave="instagramDataTipVisible = false"
        >
          <div class="card-detail-overlay" aria-hidden="true">
            <div class="card-detail-overlay__cta">
              <i class="fab fa-instagram"></i>
              <span>詳細を見る</span>
              <i class="fas fa-arrow-right card-detail-overlay__arrow"></i>
            </div>
          </div>
          <div class="card-header">
            <span
              class="card-header-title-with-help"
              @click.stop
            >
              <el-tooltip
                :content="SOCIAL_CARD_TITLE.instagram"
                placement="top"
                trigger="hover"
                :show-after="200"
              >
                <span class="card-header-title-text">{{ SOCIAL_CARD_TITLE.instagram }}</span>
              </el-tooltip>
              <el-popover
                v-model:visible="instagramDataTipVisible"
                placement="top"
                width="auto"
                trigger="click"
                popper-class="social-data-source-popover"
              >
                <template #reference>
                  <button
                    type="button"
                    class="rating-help-btn"
                    title="データ出所"
                    @click.stop
                  >
                    ?
                  </button>
                </template>
                <template #default>
                  <p class="social-data-source-tip">{{ SOCIAL_DATA_SOURCE_TIP }}</p>
                </template>
              </el-popover>
            </span>
            <i class="fab fa-instagram card-header-platform-icon" style="color: #E4405F;"></i>
          </div>
          <div class="main-stat" style="color: #E4405F;">{{ formatInstagramPostCount(socialStats.instagram.total) }}</div>
          <div v-if="socialStats.instagram.hashtag" class="sub-stat" style="margin-top: 8px;">
            <i class="fas fa-hashtag"></i> #{{ socialStats.instagram.hashtag }} 関連投稿
          </div>
          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-color);">
            <Ins
              :day-rate="socialStats.instagram.dayRate"
              :week-rate="socialStats.instagram.weekRate"
            />
          </div>
          <div class="card-detail-mobile-hint" v-if="isMobile" aria-hidden="true">
            <i class="fas fa-hand-pointer"></i>
            <span>タップして詳細を見る</span>
            <i class="fas fa-arrow-right"></i>
          </div>
        </div>
        <div
          v-if="hasSocialStatTotal(socialStats.twitter.total)"
          class="card card-detail-hover card-twitter"
          @click="goToReviewDetail('twitter')"
          @mouseleave="twitterDataTipVisible = false"
        >
          <div class="card-detail-overlay" aria-hidden="true">
            <div class="card-detail-overlay__cta">
              <XPlatformIcon size="1.1rem" />
              <span>詳細を見る</span>
              <i class="fas fa-arrow-right card-detail-overlay__arrow"></i>
            </div>
          </div>
          <div class="card-header">
            <span
              class="card-header-title-with-help"
              @click.stop
            >
              <el-tooltip
                :content="SOCIAL_CARD_TITLE.twitter"
                placement="top"
                trigger="hover"
                :show-after="200"
              >
                <span class="card-header-title-text">{{ SOCIAL_CARD_TITLE.twitter }}</span>
              </el-tooltip>
              <el-popover
                v-model:visible="twitterDataTipVisible"
                placement="top"
                width="auto"
                trigger="click"
                popper-class="social-data-source-popover"
              >
                <template #reference>
                  <button
                    type="button"
                    class="rating-help-btn"
                    title="データ出所"
                    @click.stop
                  >
                    ?
                  </button>
                </template>
                <template #default>
                  <p class="social-data-source-tip">{{ SOCIAL_DATA_SOURCE_TIP }}</p>
                </template>
              </el-popover>
            </span>
            <XPlatformIcon
              class="card-header-platform-icon twitter-brand-fg"
              size="1rem"
            />
          </div>
          <div class="main-stat twitter-brand-fg">{{ formatTwitterPostCount(socialStats.twitter.densityIndex) }}</div>
          <div v-if="socialStats.twitter.hashtag" class="sub-stat" style="margin-top: 8px;">
            <i class="fas fa-hashtag"></i> #{{ socialStats.twitter.hashtag }} 関連投稿
          </div>
          <!-- 投稿密集度（日次）（densityIndex 0~100）。null は formatDensityIndex で「—」表示、進捗バーは 0 幅 -->
          <!-- <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-color);">
            <div class="detail-row">
              <span class="detail-label" style="width: auto;">投稿密集度（日次）</span>
              <div class="detail-bar-container">
                <div
                  class="detail-bar twitter-brand-bg"
                  :style="{ width: `${densityIndexBarWidth(socialStats.twitter.densityIndex)}%` }"
                />
              </div>
              <span class="detail-value twitter-brand-fg">{{ formatDensityIndex(socialStats.twitter.densityIndex) }}</span>
            </div>
          </div> -->
          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-color);">
            <Twitter
              :day-rate="socialStats.twitter.dayRate"
              :week-rate="socialStats.twitter.weekRate"
            />
          </div>
          <div class="card-detail-mobile-hint" v-if="isMobile" aria-hidden="true">
            <i class="fas fa-hand-pointer"></i>
            <span>タップして詳細を見る</span>
            <i class="fas fa-arrow-right"></i>
          </div>
        </div>
        <div class="card card-chart-md">
          <div class="card-header">
            <span>レビュー・感情分析</span>
            <div class="review-sentiment-header-right">
              <div class="heat-scale-legend">
                <div
                  class="heat-scale-item"
                  v-for="scale in hotwordHeatScales"
                  :key="scale.label"
                >
                  <span class="heat-scale-dot" :style="{ background: scale.color }"></span>
                  <span class="heat-scale-label">{{ scale.label }}</span>
                </div>
              </div>
              <i class="fas fa-comments" style="color: var(--accent-orange);"></i>
            </div>
          </div>
          <Hotword ref="hotwordRef" :movie-id="movieId" @loaded="syncHotwordHeatScales" @select="goToHotwordDetail"></Hotword>
        </div>
        <!-- Google トレンド 検索インタレスト（DEV-14892）：期間に依存せず作品の最新スナップショットを表示 -->
        <div class="card card-chart-md card-google-trends" @mouseleave="trendsDataTipVisible = false">
          <div class="card-header">
            <span class="card-header-title-with-help">
              <el-tooltip
                :content="SOCIAL_CARD_TITLE.trends"
                placement="top"
                trigger="hover"
                :show-after="200"
              >
                <span class="card-header-title-text">{{ SOCIAL_CARD_TITLE.trends }}</span>
              </el-tooltip>
              <el-popover
                v-model:visible="trendsDataTipVisible"
                placement="top"
                width="auto"
                trigger="click"
                popper-class="social-data-source-popover"
              >
                <template #reference>
                  <button
                    type="button"
                    class="rating-help-btn"
                    title="データについて"
                    @click.stop
                  >
                    ?
                  </button>
                </template>
                <template #default>
                  <p class="social-data-source-tip">{{ TRENDS_DATA_SOURCE_TIP }}</p>
                </template>
              </el-popover>
            </span>
            <i class="fab fa-google card-header-platform-icon" style="color: #4285F4;"></i>
          </div>
          <GoogleTrends
            :movie-id="movieId"
            :open-date="trendsOpenDate"
            :period-start="trendsPeriod.start"
            :period-end="trendsPeriod.end"
          />
        </div>
      </div>
      </template>
      <!-- 分析チャートセクション（公開前模式不显示） -->
      <template v-if="!isPreRelease">
      <h2 class="section-title">
        <i class="fas fa-chart-line"></i>
        分析チャート
      </h2>
      <div
        v-if="clientV2ChartsMounted"
        class="dashboard-grid"
        :key="'national-' + clientV2ChartsRemountKey"
      >
        <div
          class="card card-chart-lg"
          data-index-chart-key="main"
          :class="{
            'card-chart-fullscreen': indexFullscreenChartKey === 'main',
            'card-chart-fullscreen--closing':
              indexFullscreenClosing && indexFullscreenChartKey === 'main',
          }"
        >
          <div class="card-header">
            <span>推移分析：上映回数 vs 総座席数</span>
            <div class="card-header-chart-actions">
              <button
                v-if="device === 'mobile' && (!indexFullscreenChartKey || indexFullscreenChartKey === 'main')"
                type="button"
                class="chart-fs-btn"
                :aria-label="indexFullscreenChartKey === 'main' ? '全画面を終了' : '全画面表示'"
                @click.stop="
                  indexFullscreenChartKey === 'main' ? closeIndexFullscreen() : openIndexFullscreen('main')
                "
              >
                <i :class="indexFullscreenChartKey === 'main' ? 'fas fa-compress' : 'fas fa-expand'"></i>
              </button>
              <i class="fas fa-chart-bar" style="color: var(--accent-blue);"></i>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="mainChartRef" width="1927" height="304"></canvas>
          </div>
        </div>
        <div
          class="card card-chart-md"
          data-index-chart-key="occupancy"
          :class="{
            'card-chart-fullscreen': indexFullscreenChartKey === 'occupancy',
            'card-chart-fullscreen--closing':
              indexFullscreenClosing && indexFullscreenChartKey === 'occupancy',
          }"
        >
          <div class="card-header">
            <span>日別 充足率推移 (Occupancy)</span>
            <div class="card-header-chart-actions">
              <button
                v-if="device === 'mobile' && (!indexFullscreenChartKey || indexFullscreenChartKey === 'occupancy')"
                type="button"
                class="chart-fs-btn"
                :aria-label="indexFullscreenChartKey === 'occupancy' ? '全画面を終了' : '全画面表示'"
                @click.stop="
                  indexFullscreenChartKey === 'occupancy'
                    ? closeIndexFullscreen()
                    : openIndexFullscreen('occupancy')
                "
              >
                <i
                  :class="indexFullscreenChartKey === 'occupancy' ? 'fas fa-compress' : 'fas fa-expand'"
                ></i>
              </button>
              <i class="fas fa-percent" style="color: var(--accent-green);"></i>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="occupancyChartRef" width="932" height="224"></canvas>
          </div>
        </div>
        <div
          class="card card-chart-md"
          data-index-chart-key="cinema"
          :class="{
            'card-chart-fullscreen': indexFullscreenChartKey === 'cinema',
            'card-chart-fullscreen--closing':
              indexFullscreenClosing && indexFullscreenChartKey === 'cinema',
          }"
        >
          <div class="card-header">
            <span>上映館数 推移</span>
            <div class="card-header-chart-actions">
              <button
                v-if="device === 'mobile' && (!indexFullscreenChartKey || indexFullscreenChartKey === 'cinema')"
                type="button"
                class="chart-fs-btn"
                :aria-label="indexFullscreenChartKey === 'cinema' ? '全画面を終了' : '全画面表示'"
                @click.stop="
                  indexFullscreenChartKey === 'cinema'
                    ? closeIndexFullscreen()
                    : openIndexFullscreen('cinema')
                "
              >
                <i
                  :class="indexFullscreenChartKey === 'cinema' ? 'fas fa-compress' : 'fas fa-expand'"
                ></i>
              </button>
              <i class="fas fa-map-marker-alt" style="color: var(--accent-purple);"></i>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="cinemaChartRef" width="932" height="224"></canvas>
          </div>
        </div>
      </div>
      </template>
    </main>
    <!-- 「期間内合計（エリア別）」区块：対比電影表示中は非表示（エリア別フィルター＋分析チャートを含む）；公開前模式同样不显示 -->
    <div v-if="movieId && activeMovieTab === 'main' && !isPreRelease" class="charts-container" style="margin-top: 51px">
      <p class="title" style="font-size: 16px;margin-bottom: 20px;">期間内合計（エリア別）</p>
      <el-form class="client-v2-area-filter-form" ref="areaQueryParamsRef" :model="areaQueryParams" :rules="areaRules"
        label-width="80" style="margin-bottom: 20px;">
        <el-row :gutter="0" class="client-v2-area-filter-row">
          <el-col class="client-v2-area-select-col" :span="4" :xs="16" :sm="7" :md="6" :lg="5" :xl="4">
            <el-form-item label="エリア" prop="areaId">
              <!-- <el-input v-model="queryParams.movieId" placeholder="名前で検索" /> -->
              <el-select-v2 v-model="areaQueryParams.areaId" placeholder="名前で検索" filterable clearable
                style="width: 100%;" :options="areaList" :props="{
                  value: 'id',
                  label: 'name',
                }" />
            </el-form-item>
          </el-col>
          <el-col class="client-v2-area-action-col" :span="2" :xs="6" :sm="4" :md="3" :lg="2" :xl="2">
            <el-form-item label-width="0">
              <el-button type="primary" class="client-v2-area-action-btn" style="
                    width: 100%;
                    box-shadow: 0px 1px 5px 0px #0000001f;
                    box-shadow: 0px 2px 2px 0px #00000024;
                    box-shadow: 0px 3px 1px -2px #00000033;
                    border: none;
                  " @click="getAreaChart">確定</el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <main v-if="areaChartData && areaChartData.length > 0">
        <!-- 分析チャートセクション -->
        <h2 class="section-title">
          <i class="fas fa-chart-line"></i>
          分析チャート
        </h2>
        <div
          v-if="clientV2ChartsMounted"
          class="dashboard-grid"
          :key="'region-' + clientV2ChartsRemountKey"
        >
          <div
            class="card card-chart-lg"
            data-index-chart-key="mainRegion"
            :class="{
              'card-chart-fullscreen': indexFullscreenChartKey === 'mainRegion',
              'card-chart-fullscreen--closing':
                indexFullscreenClosing && indexFullscreenChartKey === 'mainRegion',
            }"
          >
            <div class="card-header">
              <span>推移分析：上映回数 vs 総座席数</span>
              <div class="card-header-chart-actions">
                <button
                  v-if="
                    device === 'mobile' &&
                    (!indexFullscreenChartKey || indexFullscreenChartKey === 'mainRegion')
                  "
                  type="button"
                  class="chart-fs-btn"
                  :aria-label="indexFullscreenChartKey === 'mainRegion' ? '全画面を終了' : '全画面表示'"
                  @click.stop="
                    indexFullscreenChartKey === 'mainRegion'
                      ? closeIndexFullscreen()
                      : openIndexFullscreen('mainRegion')
                  "
                >
                  <i
                    :class="
                      indexFullscreenChartKey === 'mainRegion' ? 'fas fa-compress' : 'fas fa-expand'
                    "
                  ></i>
                </button>
                <i class="fas fa-chart-bar" style="color: var(--accent-blue);"></i>
              </div>
            </div>
            <div class="chart-container">
              <canvas ref="mainRegionChartRef" width="1927" height="304"></canvas>
            </div>
          </div>
          <div
            class="card card-chart-md"
            data-index-chart-key="occupancyRegion"
            :class="{
              'card-chart-fullscreen': indexFullscreenChartKey === 'occupancyRegion',
              'card-chart-fullscreen--closing':
                indexFullscreenClosing && indexFullscreenChartKey === 'occupancyRegion',
            }"
          >
            <div class="card-header">
              <span>日別 充足率推移 (Occupancy)</span>
              <div class="card-header-chart-actions">
                <button
                  v-if="
                    device === 'mobile' &&
                    (!indexFullscreenChartKey || indexFullscreenChartKey === 'occupancyRegion')
                  "
                  type="button"
                  class="chart-fs-btn"
                  :aria-label="
                    indexFullscreenChartKey === 'occupancyRegion' ? '全画面を終了' : '全画面表示'
                  "
                  @click.stop="
                    indexFullscreenChartKey === 'occupancyRegion'
                      ? closeIndexFullscreen()
                      : openIndexFullscreen('occupancyRegion')
                  "
                >
                  <i
                    :class="
                      indexFullscreenChartKey === 'occupancyRegion'
                        ? 'fas fa-compress'
                        : 'fas fa-expand'
                    "
                  ></i>
                </button>
                <i class="fas fa-percent" style="color: var(--accent-green);"></i>
              </div>
            </div>
            <div class="chart-container">
              <canvas ref="occupancyRegionChartRef" width="932" height="224"></canvas>
            </div>
          </div>
          <div
            class="card card-chart-md"
            data-index-chart-key="cinemaRegion"
            :class="{
              'card-chart-fullscreen': indexFullscreenChartKey === 'cinemaRegion',
              'card-chart-fullscreen--closing':
                indexFullscreenClosing && indexFullscreenChartKey === 'cinemaRegion',
            }"
          >
            <div class="card-header">
              <span>上映館数 推移</span>
              <div class="card-header-chart-actions">
                <button
                  v-if="
                    device === 'mobile' &&
                    (!indexFullscreenChartKey || indexFullscreenChartKey === 'cinemaRegion')
                  "
                  type="button"
                  class="chart-fs-btn"
                  :aria-label="
                    indexFullscreenChartKey === 'cinemaRegion' ? '全画面を終了' : '全画面表示'
                  "
                  @click.stop="
                    indexFullscreenChartKey === 'cinemaRegion'
                      ? closeIndexFullscreen()
                      : openIndexFullscreen('cinemaRegion')
                  "
                >
                  <i
                    :class="
                      indexFullscreenChartKey === 'cinemaRegion'
                        ? 'fas fa-compress'
                        : 'fas fa-expand'
                    "
                  ></i>
                </button>
                <i class="fas fa-map-marker-alt" style="color: var(--accent-purple);"></i>
              </div>
            </div>
            <div class="chart-container">
              <canvas ref="cinemaRegionChartRef" width="932" height="224"></canvas>
            </div>
          </div>
        </div>
      </main>
      <!-- <ChartCom :chart-list="areaChartData" type="line"></ChartCom> -->
      <!-- 移动端：卡片列表；桌面端：表格 -->
      <template v-if="device === 'mobile'">
        <div class="client-v2-area-cards">
          <div
            v-for="(row, index) in areaTableData"
            :key="index"
            class="client-v2-area-card"
          >
            <div class="client-v2-area-card__row">
              <span class="client-v2-area-card__label">地方</span>
              <span class="client-v2-area-card__value">{{ row.areaName || '—' }}</span>
            </div>
            <div class="client-v2-area-card__row">
              <span class="client-v2-area-card__label">都道府県</span>
              <span class="client-v2-area-card__value">{{ row.prefectureName || '—' }}</span>
            </div>
            <div class="client-v2-area-card__row">
              <span class="client-v2-area-card__label">上映館数</span>
              <span class="client-v2-area-card__value">{{
                row.cinemaTotalNo != null && row.cinemaTotalNo !== '' ? Number(row.cinemaTotalNo).toLocaleString() : '—'
              }}</span>
            </div>
            <div class="client-v2-area-card__row">
              <span class="client-v2-area-card__label">上映回数</span>
              <span class="client-v2-area-card__value">{{
                row.countTotal != null && row.countTotal !== '' ? Number(row.countTotal).toLocaleString() : '—'
              }}</span>
            </div>
            <div class="client-v2-area-card__row">
              <span class="client-v2-area-card__label">座席数</span>
              <span class="client-v2-area-card__value">{{
                row.seatTotalNo != null && row.seatTotalNo !== '' ? Number(row.seatTotalNo).toLocaleString() : '—'
              }}</span>
            </div>
            <div class="client-v2-area-card__row">
              <span class="client-v2-area-card__label">充足率</span>
              <span class="client-v2-area-card__value">{{
                row.emptyRate != null && row.emptyRate !== '' ? row.emptyRate + '%' : '—'
              }}</span>
            </div>
            <a
              href="javascript:void(0)"
              class="client-v2-area-card__link"
              @click="handleDetail(row)"
            >詳細</a>
          </div>
        </div>
      </template>
      <div v-else class="client-v2-table-scroll">
        <el-table :data="areaTableData" class="areaTable" style="margin-top: 30px; width: 100%;">
          <el-table-column label="地方" prop="areaName" width="100"></el-table-column>
          <el-table-column label="都道府県" prop="prefectureName" width="100"></el-table-column>
          <el-table-column label="上映館数" prop="cinemaTotalNo" width="140">
            <template #default="{ row }">
              {{
                row.cinemaTotalNo !== undefined &&
                  row.cinemaTotalNo !== null &&
                  row.cinemaTotalNo !== ""
                  ? row.cinemaTotalNo.toLocaleString()
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column label="上映回数" prop="countTotal" width="140">
            <template #default="{ row }">
              {{
                row.countTotal !== undefined && row.countTotal !== null && row.countTotal !== ""
                  ? row.countTotal.toLocaleString()
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column label="座席数" prop="seatTotalNo" width="140">
            <template #default="{ row }">
              {{
                row.seatTotalNo !== undefined && row.seatTotalNo !== null && row.seatTotalNo !== ""
                  ? row.seatTotalNo.toLocaleString()
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column label="充足率" prop="emptyRate" width="140">
            <template #default="{ row }">
              {{
                row.emptyRate !== undefined && row.emptyRate !== null && row.emptyRate !== ""
                  ? row.emptyRate + "%"
                  : ""
              }}
            </template>
          </el-table-column>
          <el-table-column>
            <template #default="{ row }">
              <a href="javascript:void(0)" style="color: var(--brand-color); text-decoration: none" @click="handleDetail(row)">
                詳細
              </a>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <!-- 详细弹窗：桌面用表格，移动端用卡片列表 -->
      <el-dialog
        v-model="isShowDetail"
        title="都道府県の詳細"
        :width="device === 'mobile' ? '95%' : '80%'"
        :class="{ 'client-v2-detail-dialog--mobile': device === 'mobile' }"
        :close-on-click-modal="false"
      >
        <div v-loading="detailLoading" class="client-v2-detail-dialog__body">
          <el-table v-if="device !== 'mobile'" :data="detailTableData" class="detailTable">
          <el-table-column label="都道府県" prop="prefectureName" width="80">
            <template #default="{ row }">
              <span v-if="row.isFirstInGroup" style="font-weight: bold">{{
                row.prefectureName
              }}</span>
              <span v-else style="color: transparent">{{ row.prefectureName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="映画館チェーン" prop="brandName" width="130">
            <template #default="{ row }">
              <span v-if="row.isFirstInGroup" style="font-weight: bold">{{ row.brandName }}</span>
              <span v-else style="color: transparent">{{ row.brandName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="映画館" prop="cinemaName" width="140">
            <template #default="{ row }">
              <span v-if="row.isBrandRow" style="font-weight: bold"></span>
              <span v-else>{{ row.cinemaName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="上映回数" prop="countTotal" width="90">
            <template #default="{ row }">
              <span :style="{
                // color: row.isBrandRow ? '#f9da49' : '#181818',
                fontWeight: row.isBrandRow ? 'bold' : 'normal',
              }">
                {{ row.countTotal.toLocaleString() }}
              </span>
            </template>
          </el-table-column>
          <el-table-column v-for="item in periodNameList" :key="item.name" :label="item.name" :width="70">
            <template #header>
              <span style="color: var(--text-primary, #181818)">
                {{ item.name }}
              </span>
            </template>
            <template #default="{ row }">
              <span :style="{
                color: 'var(--text-primary, #181818)',
                fontWeight: row.isBrandRow ? 'bold' : 'normal',
              }">
                {{ (row[`${item.value}CountTotal`] || 0).toLocaleString() }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="座席数" prop="seatTotalNo" width="90">
            <template #default="{ row }">
              <span :style="{
                // color: row.isBrandRow ? '#f9da49' : '#181818',
                fontWeight: row.isBrandRow ? 'bold' : 'normal',
              }">
                {{ row.seatTotalNo.toLocaleString() }}
              </span>
            </template>
          </el-table-column>
          <el-table-column v-for="item in periodNameList" :key="item.name" :label="item.name" :width="70"
            row-class-name="duration">
            <template #header>
              <span style="color: var(--text-primary, #181818)">
                {{ item.name }}
              </span>
            </template>
            <template #default="{ row }">
              <span :style="{
                // color: row.isBrandRow ? '#f9da49' : '#181818',
                fontWeight: row.isBrandRow ? 'bold' : 'normal',
              }">
                {{ (row[`${item.value}SeatTotalNo`] || 0).toLocaleString() }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="充足率" prop="emptyRate" width="90">
            <template #default="{ row }">
              <span :style="{
                // color: row.isBrandRow ? '#f9da49' : '#181818',
                fontWeight: row.isBrandRow ? 'bold' : 'normal',
              }">
                {{ row.emptyRate + "%" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column v-for="item in periodNameList" :key="item.name" :label="item.name" :width="70">
            <template #header>
              <span style="color: var(--text-primary, #181818)">
                {{ item.name }}
              </span>
            </template>
            <template #default="{ row }">
              <span :style="{
                // color: row.isBrandRow ? '#f9da49' : '#181818',
                fontWeight: row.isBrandRow ? 'bold' : 'normal',
              }">
                {{ row[`${item.value}EmptyRate`] + "%" }}
              </span>
            </template>
          </el-table-column>
          </el-table>

          <!-- 移动端：卡片列表 -->
          <div v-else class="client-v2-detail-dialog-mobile">
            <template v-for="(row, idx) in detailTableData" :key="idx">
              <div
                v-if="row.isBrandRow"
                class="client-v2-detail-mobile__brand"
              >
                <div class="client-v2-detail-mobile__brand-title">
                  {{ row.prefectureName }} · {{ row.brandName }}
                </div>
                <div class="client-v2-detail-mobile__brand-stats">
                  <span>上映回数 {{ row.countTotal.toLocaleString() }}</span>
                  <span>座席数 {{ row.seatTotalNo.toLocaleString() }}</span>
                  <span>充足率 {{ row.emptyRate + '%' }}</span>
                </div>
                <div class="client-v2-detail-mobile__periods">
                  <span
                    v-for="item in periodNameList"
                    :key="item.name"
                    class="client-v2-detail-mobile__period"
                  >
                    {{ item.name }}: {{ (row[`${item.value}CountTotal`] || 0).toLocaleString() }}回 / {{ (row[`${item.value}SeatTotalNo`] || 0).toLocaleString() }}席 / {{ (row[`${item.value}EmptyRate`] ?? 0) }}%
                  </span>
                </div>
              </div>
              <div
                v-else
                class="client-v2-detail-mobile__cinema"
              >
                <div class="client-v2-detail-mobile__cinema-name">{{ row.cinemaName }}</div>
                <div class="client-v2-detail-mobile__cinema-stats">
                  <span>上映 {{ row.countTotal.toLocaleString() }}回</span>
                  <span>座席 {{ row.seatTotalNo.toLocaleString() }}</span>
                  <span>{{ row.emptyRate }}%</span>
                </div>
                <div class="client-v2-detail-mobile__periods client-v2-detail-mobile__periods--small">
                  <span
                    v-for="item in periodNameList"
                    :key="item.name"
                    class="client-v2-detail-mobile__period"
                  >
                    {{ item.name }} {{ (row[`${item.value}CountTotal`] || 0).toLocaleString() }} / {{ (row[`${item.value}EmptyRate`] ?? 0) }}%
                  </span>
                </div>
              </div>
            </template>
            <div v-if="device === 'mobile' && !detailLoading && detailTableData.length === 0" class="client-v2-detail-mobile__empty">
              データがありません
            </div>
          </div>
        </div>
      </el-dialog>

      <!-- 評価基準説明ダイアログ -->
      <el-dialog
        v-model="showRatingRules"
        title="⭐ 評価基準"
        width="360px"
        :show-close="true"
        align-center
        class="rating-rules-dialog"
      >
        <div class="rating-rules-dialog-body">
          <p class="rating-rules-dialog-intro">スコアに基づいて星数が決まります。</p>
          <div class="rating-rules-list">
            <div class="rating-rules-list__item">
              <div class="rrl-stars">
                <i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i>
              </div>
              <div class="rrl-info">
                <span class="rrl-range">スコア 5</span>
              </div>
            </div>
            <div class="rating-rules-list__item">
              <div class="rrl-stars">
                <i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i>
              </div>
              <div class="rrl-info">
                <span class="rrl-range">スコア 4 〜 5 未満</span>
              </div>
            </div>
            <div class="rating-rules-list__item">
              <div class="rrl-stars">
                <i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i>
              </div>
              <div class="rrl-info">
                <span class="rrl-range">スコア 3 〜 4 未満</span>
              </div>
            </div>
            <div class="rating-rules-list__item">
              <div class="rrl-stars">
                <i class="fas fa-star"></i><i class="fas fa-star"></i>
              </div>
              <div class="rrl-info">
                <span class="rrl-range">スコア 2 〜 3 未満</span>
              </div>
            </div>
            <div class="rating-rules-list__item">
              <div class="rrl-stars">
                <i class="fas fa-star"></i>
              </div>
              <div class="rrl-info">
                <span class="rrl-range">スコア 0 〜 2 未満</span>
              </div>
            </div>
          </div>
        </div>
      </el-dialog>
    </div>
    <div
      v-if="device === 'mobile' && indexFullscreenChartKey"
      class="index-chart-fs-backdrop"
      :class="{ 'index-chart-fs-backdrop--closing': indexFullscreenClosing }"
      aria-hidden="true"
      @click="closeIndexFullscreen"
    />
    <!-- iOS など锁屏非対応：全屏 + 縦屏のときデバイス回転を促すヒント -->
    <div
      v-if="device === 'mobile' && indexFullscreenChartKey && indexFsShowRotateHint"
      class="chart-fs-rotate-hint"
      aria-live="polite"
    >
      <i class="fas fa-mobile-alt chart-fs-rotate-hint__phone"></i>
      <span>横向きで見ると見やすくなります</span>
    </div>
  </div>
</template>
<style lang="less">
/* SNS データ出所 popover */
.social-data-source-popover {
  padding: 10px 12px !important;
  max-width: 280px;

  .social-data-source-tip {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.5;
    color: var(--text-primary, #333);
  }
}

.card-header-title-with-help {
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 11;
  line-height: 1.45;
  padding-right: 8px;

  .card-header-title-text {
    display: inline;
    cursor: default;
    transition: color 0.28s ease;
  }

  .rating-help-btn {
    display: inline-flex;
    vertical-align: middle;
    margin-left: 6px;
  }

  :deep(.el-tooltip__trigger) {
    display: inline;
    vertical-align: middle;
  }
}

.card-detail-hover .card-header {
  position: relative;
  z-index: 12;
  transition: color 0.28s ease, background 0.28s ease, box-shadow 0.28s ease;
}

.card-detail-hover .card-header-platform-icon {
  position: relative;
  z-index: 11;
  transition: opacity 0.28s ease, visibility 0.28s ease;
}

.card-detail-hover:hover .card-header-platform-icon {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

/* hover 遮罩上でも ? ボタンを押せるよう overlay より前面に */
.card-detail-hover .card-header .rating-help-btn {
  position: relative;
  z-index: 12;
}

/* 评价基准 popover */
.rating-rules-popover {
  padding: 8px 10px 6px !important;
  .rating-rules-dialog-body {
    .rating-rules-dialog-intro {
      font-size: 0.74rem;
      color: var(--text-secondary, #888);
      margin: 0 0 8px;
      white-space: nowrap;
    }
    .rating-rules-list {
      display: flex;
      flex-direction: column;
      &__item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 5px 0;
        border-bottom: 1px solid var(--border-color, #f0f0f0);
        &:last-child { border-bottom: none; }
      }
    }
    .rrl-stars {
      display: flex;
      gap: 2px;
      width: 72px;
      flex-shrink: 0;
      align-items: center;
      .fas.fa-star { color: #f9b749; font-size: 0.72rem; }
    }
    .rrl-info {
      display: flex;
      align-items: center;
      flex: 1;
    }
    .rrl-range { font-size: 0.72rem; color: var(--text-primary, #333); }
  }
}

/* 评价基准 ? 按钮 */
.rating-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid var(--brand-color, #f9b749);
  background: transparent;
  color: var(--brand-color, #f9b749);
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
  &:hover {
    background: var(--brand-color, #f9b749);
    color: #fff;
  }
}

/* 评价基准弹窗 */
.rating-rules-dialog {
  .rating-rules-dialog-body {
    padding: 4px 0 8px;
  }
  .rating-rules-dialog-intro {
    font-size: 0.8rem;
    color: var(--text-secondary, #888);
    margin: 0 0 16px;
  }
  .rating-rules-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    &__item {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 10px 0;
      border-bottom: 1px solid var(--border-color, #f0f0f0);
      &:last-child { border-bottom: none; }
    }
  }
  .rrl-stars {
    display: flex;
    gap: 3px;
    width: 86px;
    flex-shrink: 0;
    align-items: center;
    .fas.fa-star {
      color: var(--brand-color, #f9b749);
      font-size: 0.82rem;
    }
  }
  .rrl-info {
    display: flex;
    align-items: center;
    flex: 1;
  }
  .rrl-range {
    font-size: 0.8rem;
    color: var(--text-primary, #333);
  }
}

@keyframes chartFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 全屏卡片进入：轻微上浮 + 缩放 + 过冲（不用 box-shadow，避免父级 overflow 裁切） */
@keyframes indexChartFsIn {
  0% {
    opacity: 0;
    transform: translate3d(0, 18px, 0) scale(0.92);
    filter: brightness(0.88) saturate(0.95);
  }

  55% {
    opacity: 1;
    transform: translate3d(0, -4px, 0) scale(1.012);
    filter: brightness(1.02) saturate(1);
  }

  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
    filter: brightness(1) saturate(1);
  }
}

/* 全屏卡片退出：下沉微缩 + 淡出 */
@keyframes indexChartFsOut {
  from {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
    filter: brightness(1);
  }

  to {
    opacity: 0;
    transform: translate3d(0, 14px, 0) scale(0.94);
    filter: brightness(0.92);
  }
}

@keyframes indexChartFsBackdropIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes indexChartFsBackdropOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

/* 全屏内图表区域稍快显现，避免与卡片动画脱节 */
@keyframes indexChartFsHeaderIn {
  from {
    opacity: 0;
    transform: translate3d(0, -10px, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.company-name {
  margin-bottom: 24px;

  >span {
    font-size: 16px;
    letter-spacing: 0.9px;
    color: var(--text-primary, #181818);
    margin-right: 22px;

    &.name {
      font-size: 12px;
      letter-spacing: -0.83px;
      font-weight: 700;
    }
  }
}

.client-v2-page {
  padding: 30px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 12px 24px;
    padding-top: max(16px, env(safe-area-inset-top, 0px));
  }

  @media (max-width: 480px) {
    padding: 12px 10px 20px;
  }

  @media (max-width: 375px) {
    padding: 10px 8px 16px;
  }

  .index-chart-fs-backdrop {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: linear-gradient(
      165deg,
      rgba(8, 10, 28, 0.62) 0%,
      rgba(18, 14, 40, 0.55) 45%,
      rgba(12, 12, 22, 0.5) 100%
    );
    backdrop-filter: blur(10px) saturate(1.15);
    -webkit-backdrop-filter: blur(10px) saturate(1.15);
    animation: indexChartFsBackdropIn 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    transform: translateZ(0);

    &--closing {
      animation: indexChartFsBackdropOut 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
      pointer-events: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .index-chart-fs-backdrop {
      animation: none;
      opacity: 1;
    }

    .index-chart-fs-backdrop--closing {
      opacity: 0;
    }
  }
}

/* iOS など锁屏非対応：全屏 + 縦屏のとき回転を促すヒント（backdrop / 全屏 card より上、クリック透過） */
.chart-fs-rotate-hint {
  position: fixed;
  top: calc(8px + env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--brand-color) 72%, transparent);
  background: linear-gradient(90deg, color-mix(in srgb, var(--brand-color) 20%, transparent), rgba(255, 120, 30, 0.14));
  color: #5e4000;
  font-size: 0.74rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  pointer-events: none;
  animation: chartFsHintEnter 0.4s ease-out;

  &__phone {
    color: #b28700;
    font-size: 0.76rem;
  }
}

html.dark .chart-fs-rotate-hint {
  color: var(--text-primary, #f0f0f0);

  .chart-fs-rotate-hint__phone {
    color: var(--text-secondary, #a8adb8);
  }
}

@keyframes chartFsHintEnter {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-12px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

// Filter row layout
// Align action buttons - ensure consistent alignment
.client-v2-filter-action {
  display: flex;
  align-items: center; // Center align buttons vertically
  justify-content: flex-start;
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;

  ::v-deep(.el-form-item) {
    width: 100%;
    margin-bottom: 0 !important;
    margin-top: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    text-align: left;
    display: flex;
    align-items: center;
  }

  ::v-deep(.el-form-item__content) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    text-align: left;
  }

  ::v-deep(.el-form-item__error) {
    display: none;
  }
}

// Fixed width action buttons
.client-v2-action-btn {
  width: 80px !important;
  min-width: 70px !important; // Allow slightly smaller on very small screens
  max-width: 80px !important;
  box-shadow: 0px 1px 5px 0px #0000001f, 0px 2px 2px 0px #00000024, 0px 3px 1px -2px #00000033 !important;
  border: none !important;
  margin-left: 0 !important;
  margin-right: auto !important;
  margin-top: 0 !important; // Remove any top margin to ensure alignment
  margin-bottom: 0 !important; // Remove any bottom margin to ensure alignment
  flex-shrink: 0 !important;

  @media (max-width: 400px) {
    width: 72px !important;
    min-width: 64px !important;
    max-width: 72px !important;
    font-size: 12px !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  @media (max-width: 320px) {
    width: 64px !important;
    min-width: 56px !important;
    max-width: 64px !important;
    font-size: 12px !important;
  }
}

// Limit input column width on all screens - adaptive with max-width
.client-v2-input-col {
  max-width: 400px;

  // Set max-width on all screen sizes to prevent excessive width
  @media (max-width: 768px) {
    max-width: 400px;
  }

  @media (max-width: 576px) {
    max-width: 350px;
  }

  @media (max-width: 400px) {
    max-width: 300px;
  }

  // Ensure input can adapt to content but with limits
  ::v-deep(.el-input),
  ::v-deep(.el-select),
  ::v-deep(.el-cascader),
  ::v-deep(.el-autocomplete) {
    max-width: 100%; // Don't exceed column width
    width: 100% !important;
  }
}

// Limit date picker column width - ensure date display is not cut off
.client-v2-date-col {
  max-width: 320px;

  // Set max-width on all screen sizes to prevent excessive width
  @media (max-width: 768px) {
    max-width: 320px;
  }

  @media (max-width: 576px) {
    max-width: 300px;
  }

  @media (max-width: 400px) {
    max-width: 280px;
  }

  // Ensure date picker input itself can adapt to content but with limits
  ::v-deep(.el-date-editor) {
    min-width: 280px; // Minimum width to display full date range "2025-12-01 - 2026-01-31"
    max-width: 100%; // Don't exceed column width
    width: 100% !important;

    @media (max-width: 576px) {
      min-width: 260px;
    }

    @media (max-width: 400px) {
      min-width: 240px;
    }

    // Ensure input field can expand to show full date
    .el-range-input {
      min-width: 0;
      flex: 1;
    }

    .el-range-separator {
      flex-shrink: 0;
    }
  }
}

// 公開前：候选项带公开日，列宽加宽（手机宽度下仍由下方 576px 规则按行宽收缩）
.client-v2-movie-col.is-pre-release {
  min-width: 440px !important;
  width: 440px !important;
  max-width: 440px !important;
  flex: 0 0 440px !important;
}

// 作品选择：固定列宽，有值/清空/hover 时 select 宽度不变
.client-v2-movie-col {
  min-width: 300px !important;
  width: 300px !important;
  max-width: 300px !important;
  flex: 0 0 300px !important;
  flex-shrink: 0 !important;

  .client-v2-movie-form-item {
    width: 100%;
    max-width: 100%;

    ::v-deep(.el-form-item__content) {
      flex: 1 1 auto;
      width: 0;
      min-width: 0;
      max-width: 100%;
    }
  }

  ::v-deep(.lazy-load-movie-select),
  ::v-deep(.el-select),
  ::v-deep(.select-trigger),
  ::v-deep(.el-input) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    display: block;
    box-sizing: border-box;
  }

  ::v-deep(.el-input__wrapper) {
    width: 100% !important;
    box-sizing: border-box;
  }

  ::v-deep(.el-input__inner),
  ::v-deep(input) {
    width: 100% !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 576px) {
    min-width: 280px !important;
    width: 280px !important;
    max-width: 280px !important;
    flex: 0 0 280px !important;
  }
}

// Ensure form items align properly
.client-v2-filter-form {
  ::v-deep(.el-form-item) {
    margin-bottom: 18px;
    display: flex;
    align-items: center; // Center align label and content vertically
    flex-wrap: nowrap !important; // Prevent label and content from wrapping
  }

  ::v-deep(.el-form-item__label) {
    padding-top: 0;
    padding-bottom: 0;
    flex-shrink: 0; // Prevent label from shrinking
    white-space: nowrap; // Prevent label text from wrapping
  }

  ::v-deep(.el-form-item__content) {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0; // Allow content to shrink if needed
  }

  // 作品行：校验错误不占 flex 行高，避免确定按钮与 select 错位
  .client-v2-movie-row {
    align-items: flex-start !important;

    > .client-v2-filter-action {
      align-self: flex-start;
    }
  }

  .client-v2-compare-row {
    margin-bottom: 18px;

    @media (max-width: 576px) {
      margin-bottom: 10px;
    }
  }

  .client-v2-compare-form-item {
    margin-bottom: 0 !important;
  }

  // 公開前／公開後 模式切换行（DEV-14891），间距与对比 Tab 行一致
  .client-v2-mode-row {
    margin-bottom: 18px;

    @media (max-width: 576px) {
      margin-bottom: 10px;
    }
  }

  .client-v2-mode-form-item {
    margin-bottom: 0 !important;
  }

  // 「作品」行下方的公開前范围提示：抵消作品行的下边距，贴近作品行显示
  .release-mode-range-tip {
    margin: -12px 0 18px;
    font-size: 0.78rem;
    line-height: 1.5;
    color: var(--text-secondary, #555);
  }

  .client-v2-movie-form-item {
    align-items: flex-start !important;
    margin-bottom: 18px !important;

    ::v-deep(.el-form-item__label) {
      height: 32px;
      line-height: 32px;
    }

    ::v-deep(.el-form-item__content) {
      position: relative;
    }

    &.is-error ::v-deep(.el-form-item__content) {
      margin-bottom: 20px;
    }

    ::v-deep(.el-form-item__error) {
      position: absolute;
      top: calc(100% + 2px);
      left: 0;
      padding-top: 0;
      line-height: 1.2;
      white-space: nowrap;
    }
  }

  // hover/focus 时保持 1px inset 描边，避免输入框变宽导致旁边按钮位移
  .client-v2-input-col {
    ::v-deep(.el-select),
    ::v-deep(.el-date-editor) {
      width: 100% !important;
      max-width: 100%;
    }

    ::v-deep(.el-input__wrapper) {
      box-sizing: border-box;
      box-shadow: 0 0 0 1px var(--el-border-color) inset !important;

      &:hover {
        box-shadow: 0 0 0 1px var(--el-border-color-hover) inset !important;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px var(--el-color-primary) inset !important;
      }
    }
  }

  // Ensure buttons align at the same vertical position
  .el-row {
    display: flex;
    align-items: center; // Center align all columns vertically - ensures input and button on same line are aligned
    flex-wrap: nowrap !important; // Prevent wrapping at all times

    // Ensure both button columns start at the same horizontal position and height
    .client-v2-filter-action {
      flex-shrink: 0;
      min-width: 80px; // Button minimum width
      display: flex;
      align-items: center; // Center button vertically
      justify-content: flex-start;

      ::v-deep(.el-form-item) {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        display: flex;
        align-items: center;
        width: 100%;
      }

      ::v-deep(.el-form-item__content) {
        padding-top: 0 !important;
        margin-top: 0 !important;
        padding-bottom: 0 !important;
        margin-bottom: 0 !important;
        display: flex;
        align-items: center;
      }
    }

    // Ensure input columns have same height and alignment
    .client-v2-input-col {
      display: flex;
      align-items: center; // Center align with row

      ::v-deep(.el-form-item) {
        width: 100%;
        display: flex;
        align-items: center;
        margin-bottom: 0 !important;
        margin-top: 0 !important;
      }

      ::v-deep(.el-form-item__label) {
        padding-top: 0;
        padding-bottom: 0;
        display: flex;
        align-items: center;
        line-height: 32px; // Match input height
        // 「作品」が「作／品」に折り返さないよう明示（display: flex 配下でも効かせる）
        white-space: nowrap !important;
        word-break: keep-all !important;
        overflow-wrap: normal !important;
        flex-shrink: 0;
      }

      ::v-deep(.el-form-item__content) {
        padding-top: 0;
        padding-bottom: 0;
        display: flex;
        align-items: center;
      }
    }

    // Make sure input columns have consistent width to align button columns
    // Both input columns should take the same width to ensure button alignment
    .client-v2-input-col:not(.client-v2-movie-col) {
      flex-shrink: 1; // Allow shrinking
      flex-grow: 0; // Don't grow beyond max-width
      min-width: 0; // Allow shrinking
    }

    .client-v2-input-col.client-v2-movie-col {
      flex-shrink: 0 !important;
      flex-grow: 0 !important;
    }

    // スマホ幅：作品列は 280px 固定＋flex-shrink: 0 のため、320px 端末では
    // 「確定」ボタンが画面外へはみ出してページ全体が横に溢れる。
    // 280px は「希望幅」に留め、幅が足りないときだけ縮むようにする
    //（縮小は行幅で決まるので、値の有無による幅ブレは起きない）。
    // NOTE: 後続の .client-v2-date-col などに打ち消されないよう .el-col で詳細度を上げている。
    @media (max-width: 576px) {
      .el-col.client-v2-input-col.client-v2-movie-col {
        flex: 0 1 auto !important;
        flex-shrink: 1 !important;
        min-width: 0 !important;
        max-width: 100% !important;
      }

      // ボタン列は実ボタン幅ぶんだけ確保（min-width: 80px だと 320px で溢れる）
      .el-col.client-v2-filter-action {
        min-width: 0 !important;
        flex-shrink: 0 !important;
      }
    }

    // Date picker column - 移动端使用弹性布局
    .client-v2-date-col {
      width: auto !important;
      max-width: none !important;
      min-width: 0;
      flex: 0 0 auto;

      @media (max-width: 768px) {
        width: 240px !important;
        max-width: none !important;
        min-width: 0;
        // flex: 1 1 340px; // 移动端使用弹性布局，自动适应
      }
    }

    // Work input column - widths handled by .client-v2-movie-col
    .client-v2-input-col:not(.client-v2-date-col):not(.client-v2-movie-col) {
      width: auto !important;
      max-width: none !important;
      min-width: 0;
      flex: 0 0 auto;
    }
  }

  // Optimize spacing on small screens
  @media (max-width: 576px) {
    .el-row {
      // Reduce padding and margins on small screens
      align-items: center !important; // Center align on small screens

      ::v-deep(.el-col) {
        padding-left: 4px !important;
        padding-right: 4px !important;
        align-items: center; // Center align columns on small screens
      }
    }

    // Ensure button and input columns maintain alignment on small screens
    .client-v2-filter-action {
      display: flex;
      align-items: center;

      ::v-deep(.el-form-item) {
        display: flex;
        align-items: center;
      }
    }

    .client-v2-input-col {
      display: flex;
      align-items: center;

      ::v-deep(.el-form-item) {
        display: flex;
        align-items: center;
      }
    }
  }

  // Minimize gap between input and button columns - buttons should be right next to inputs
  .client-v2-no-wrap-row {
    ::v-deep(.el-row) {
      // Remove gutter spacing completely
      margin-left: 0 !important;
      margin-right: 0 !important;
      align-items: center !important; // Center align all columns vertically
    }

    // Override Element Plus gutter spacing
    ::v-deep(.el-col) {
      padding-left: 0 !important;
      padding-right: 0 !important;
      display: flex;
      align-items: center; // Center align content
    }

    // Ensure alignment on small screens (575px and below)
    @media (max-width: 575px) {
      ::v-deep(.el-row) {
        align-items: center !important; // Center align on small screens
      }

      ::v-deep(.el-col) {
        align-items: center !important; // Center align on small screens
      }

      .client-v2-filter-action {
        display: flex !important;
        align-items: center !important;

        ::v-deep(.el-form-item) {
          display: flex !important;
          align-items: center !important;
        }

        ::v-deep(.el-form-item__content) {
          display: flex !important;
          align-items: center !important;
        }
      }

      .client-v2-input-col {
        display: flex !important;
        align-items: center !important;

        ::v-deep(.el-form-item) {
          display: flex !important;
          align-items: center !important;
        }
      }
    }

    // Minimize spacing between input and button - just enough to prevent overlap
    .client-v2-input-col {
      padding-right: 6px !important;
      margin-right: 0 !important;

      @media (max-width: 576px) {
        padding-right: 4px !important;
      }

      @media (max-width: 400px) {
        padding-right: 3px !important;
      }
    }

    // .client-v2-filter-action {
    //   padding-left: 6px !important;
    //   margin-left: 0 !important;

    //   @media (max-width: 576px) {
    //     padding-left: 4px !important;
    //   }

    //   @media (max-width: 400px) {
    //     padding-left: 3px !important;
    //   }
    // }
  }

  // Prevent row wrapping on all screen sizes
  .client-v2-no-wrap-row {
    flex-wrap: nowrap !important;

    ::v-deep(.el-row) {
      flex-wrap: nowrap !important;
    }
  }

  // Make sure both input rows have consistent spacing
  .el-row:first-of-type .client-v2-filter-action,
  .el-row:nth-of-type(2) .client-v2-filter-action {
    ::v-deep(.el-form-item) {
      margin-top: 0;
      padding-top: 0;
    }
  }

  // Ensure both button columns start at the same horizontal position
  @media (min-width: 768px) {
    .el-row {

      // Ensure consistent column spacing
      .el-col {
        box-sizing: border-box;
      }

      // Make sure both input columns contribute the same width to push buttons
      // Use flex-basis to ensure consistent width calculation
      .client-v2-input-col {
        flex: 0 0 auto;
      }

      .client-v2-filter-action {
        flex: 0 0 auto;
      }
    }

    // Ensure both rows have the same input column width for button alignment
    // The key is to make sure the input columns + gutter take the same space
    .el-row:first-of-type,
    .el-row:nth-of-type(2) {
      .client-v2-input-col {
        // Both should reserve the same space
        width: 100%;
      }
    }
  }
}

// Tablet and medium desktop (768px - 1200px)
@media (max-width: 1200px) and (min-width: 769px) {
  .client-v2-page {
    padding: 24px;
  }
}

// Tablet (576px - 768px)
@media (max-width: 768px) and (min-width: 577px) {
  .client-v2-page {
    padding: 20px;
  }

  .client-v2-filter-form {
    ::v-deep(.el-form-item__label) {
      padding-bottom: 4px;
    }
  }
}

// Mobile (≤768px) - 优化移动端布局
@media (max-width: 768px) {
  .client-v2-page {
    padding: 12px;
  }

  .client-v2-date-row {
    gap: 8px !important;

    .client-v2-date-col {
      flex: 1;
      min-width: 0;
      max-width: none !important;
      width: auto !important;
    }

    .client-v2-filter-action {
      flex-shrink: 0;
      min-width: 70px;
      // padding-left: 8px !important;
    }
  }

  .client-v2-date-form-item {
    ::v-deep(.el-form-item__label) {
      font-size: 14px;
      padding-right: 8px;
      min-width: 50px;
      flex-shrink: 0;
    }

    ::v-deep(.el-form-item__content) {
      flex: 1;
      min-width: 0;
    }
  }

  .client-v2-search-form-item {
    ::v-deep(.el-form-item__content) {
      display: flex;
      justify-content: flex-start;
    }
  }
}

// Mobile (≤576px)
@media (max-width: 576px) {
  .client-v2-page {
    padding: 12px;
  }

  .client-v2-filter-action {
    align-items: center;
    margin-top: 0 !important;
    // padding-left: 6px !important;
  }

  .client-v2-date-row {
    gap: 6px !important;

    .client-v2-date-col {
      flex: 1;
      min-width: 0;
    }

    .client-v2-filter-action {
      min-width: 65px;
      // padding-left: 6px !important;
    }
  }

  .client-v2-date-form-item {
    ::v-deep(.el-form-item__label) {
      font-size: 13px;
      padding-right: 6px;
      min-width: 45px;
    }
  }

  .client-v2-filter-form {
    ::v-deep(.el-form-item) {
      margin-bottom: 14px;
    }

    ::v-deep(.el-form-item__label) {
      padding-bottom: 6px;
      line-height: 1.4;
    }

    ::v-deep(.el-date-picker) {
      width: 100% !important;
    }
  }
}

// Mobile (≤480px) - 小屏幕优化
@media (max-width: 480px) {
  .client-v2-date-row {
    gap: 4px !important;

    .client-v2-filter-action {
      min-width: 60px;
      // padding-left: 4px !important;
    }
  }

  .client-v2-date-form-item {
    ::v-deep(.el-form-item__label) {
      font-size: 12px;
      padding-right: 4px;
      min-width: 40px;
    }
  }
}

// Mobile (≤375px) - iPhone SE 等小屏设备
@media (max-width: 375px) {
  .client-v2-page {
    padding: 10px;
  }

  .client-v2-date-row {
    gap: 4px !important;

    .client-v2-filter-action {
      min-width: 55px;
      // padding-left: 4px !important;
    }
  }

  .client-v2-date-form-item {
    ::v-deep(.el-form-item__label) {
      font-size: 12px;
      padding-right: 4px;
      min-width: 38px;
    }
  }
}

// Mobile (≤320px) - 超小屏幕
@media (max-width: 320px) {
  .client-v2-page {
    padding: 8px;
  }

  .client-v2-date-row {
    gap: 3px !important;

    .client-v2-filter-action {
      min-width: 50px;
      // padding-left: 3px !important;
    }
  }

  .client-v2-date-form-item {
    ::v-deep(.el-form-item__label) {
      font-size: 11px;
      padding-right: 3px;
      min-width: 35px;
    }
  }
}

.charts-container {
  title {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.9px;
  }
}

// 「期間内合計（エリア別）」フィルター - limit max width on large screens
.client-v2-area-filter-form {

  // Ensure all form items have consistent alignment
  ::v-deep(.el-form-item) {
    margin-bottom: 0 !important;
    margin-top: 0 !important;
    display: flex;
    align-items: center;
  }

  ::v-deep(.el-form-item__label) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    display: flex;
    align-items: center;
    line-height: 32px; // Match input height
  }

  ::v-deep(.el-form-item__content) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    display: flex;
    align-items: center;
  }

  .client-v2-area-filter-row {
    display: flex;
    align-items: center !important; // Ensure vertical center alignment
    flex-wrap: nowrap;
    justify-content: flex-start;

    ::v-deep(.el-row) {
      display: flex;
      align-items: center !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    // 通用列样式 - 但特定列会覆盖
    ::v-deep(.el-col) {
      display: flex;
      align-items: center; // Center align columns vertically
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }

  // Limit select column so it doesn't become too long on large windows
  // 使用更具体的选择器确保优先级，覆盖通用el-col的padding设置
  .client-v2-area-filter-row .client-v2-area-select-col {
    display: flex;
    align-items: center; // Center align with row
    padding-right: 6px !important; // Fixed spacing - same for all screen sizes
    margin-right: 0 !important;
    width: 100%; // Ensure el-col takes full width of its allocated space

    ::v-deep(.el-form-item) {
      width: 100%; // Form item takes full width of el-col
      margin-right: 0 !important;
      padding-right: 0 !important;
    }

    ::v-deep(.el-form-item__content) {
      width: 100%; // Content takes full width
      margin-right: 0 !important;
      padding-right: 0 !important;
    }

    ::v-deep(.el-select-v2) {
      width: 100% !important; // Select component takes full width
    }
  }

  // Fix action column width so layout stays compact
  // 使用更具体的选择器确保优先级，覆盖通用el-col的padding设置
  .client-v2-area-filter-row .client-v2-area-action-col {
    max-width: 120px !important;
    min-width: 96px !important;
    display: flex;
    align-items: center; // Center align with row
    padding-left: 6px !important; // Fixed spacing - same for all screen sizes
    margin-left: 0 !important;

    ::v-deep(.el-form-item) {
      width: 100%;
      margin-left: 0 !important;
      padding-left: 0 !important;
    }

    ::v-deep(.el-form-item__content) {
      margin-left: 0 !important;
      padding-left: 0 !important;
    }
  }

  .client-v2-area-action-btn {
    width: 96px !important;
    max-width: 96px !important;
    min-width: 96px !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
}

// 会社名：小屏不换行溢出
.client-v2-filter-form .el-form-item .name {
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  display: inline-block;
}

// 期間内合計区块：自适应间距与标题
.charts-container {
  margin-top: 51px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin-top: 32px;
  }

  @media (max-width: 480px) {
    margin-top: 24px;
  }

  .title {
    font-size: 16px;
    margin-bottom: 20px;
    color: var(--text-primary);

    @media (max-width: 768px) {
      font-size: 15px;
      margin-bottom: 16px;
    }

    @media (max-width: 480px) {
      font-size: 14px;
      margin-bottom: 12px;
    }
  }
}

// 移动端：表格以卡片形式展示，每行一条卡片
.client-v2-area-cards {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .client-v2-area-card {
    background: var(--bg-panel);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    padding: 14px 16px;
    box-shadow: var(--shadow-sm);

    .client-v2-area-card__row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 0;
      font-size: 14px;
      border-bottom: 1px solid var(--border-color);

      &:last-of-type {
        border-bottom: none;
      }
    }

    .client-v2-area-card__label {
      color: var(--text-secondary);
      font-weight: 500;
      flex-shrink: 0;
      margin-right: 12px;
    }

    .client-v2-area-card__value {
      color: var(--text-primary);
      font-weight: 600;
      text-align: right;
      word-break: break-all;
    }

    .client-v2-area-card__link {
      display: inline-block;
      margin-top: 10px;
      padding: 6px 12px;
      color: var(--brand-color, #f9da49);
      font-weight: 600;
      font-size: 14px;
      text-decoration: none;
      -webkit-tap-highlight-color: transparent;

      &:active {
        opacity: 0.8;
      }
    }
  }

  @media (max-width: 480px) {
    margin-top: 16px;
    gap: 10px;

    .client-v2-area-card {
      padding: 12px 14px;

      .client-v2-area-card__row {
        font-size: 13px;
        padding: 5px 0;
      }

      .client-v2-area-card__link {
        margin-top: 8px;
        font-size: 13px;
      }
    }
  }
}

// Scroll container for tables on small screens (avoid clipping)
.client-v2-table-scroll {
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  margin-left: -8px;
  margin-right: -8px;
  padding-left: 8px;
  padding-right: 8px;

  @media (max-width: 768px) {
    margin-left: -4px;
    margin-right: -4px;
    padding-left: 4px;
    padding-right: 4px;
  }
}

.client-v2-table-scroll .areaTable {
  min-width: 900px; // keep column layout readable, allow horizontal scroll

  @media (max-width: 480px) {
    min-width: 680px;
    font-size: 13px;
  }
}

.areaTable,
.movieTable,
.detailTable {
  // Element Plus 表格跟随主题；明暗色用显式值，避免切换后 hover 内联样式残留脏底
  --el-table-bg-color: #ffffff;
  --el-table-tr-bg-color: #ffffff;
  --el-table-header-bg-color: color-mix(in srgb, var(--brand-color, #f9da49) 12%, #ffffff);
  --el-table-header-text-color: var(--brand-color, #cc251b);
  --el-table-text-color: #181818;
  --el-table-border-color: var(--border-color, #e5e5e5);
  --el-table-row-hover-bg-color: color-mix(in srgb, var(--brand-color, #f9da49) 8%, #ffffff);
  --el-fill-color-blank: #ffffff;

  .el-table__header-wrapper th.el-table__cell,
  .el-table__fixed-header-wrapper th.el-table__cell {
    font-weight: 700;
    color: var(--brand-color, #cc251b) !important;
    background-color: color-mix(in srgb, var(--brand-color, #f9da49) 12%, #ffffff) !important;
  }

  .el-table__body-wrapper td.el-table__cell,
  .el-table__fixed-body-wrapper td.el-table__cell {
    font-size: 14px;
    font-weight: 500;
    color: #181818 !important;
    background-color: #ffffff !important;
  }

  .el-table__body tr.hover-row > td.el-table__cell,
  .el-table__body tr.current-row > td.el-table__cell,
  .el-table__fixed-body-wrapper tr.hover-row > td.el-table__cell,
  .el-table__fixed-body-wrapper tr.current-row > td.el-table__cell {
    background-color: color-mix(in srgb, var(--brand-color, #f9da49) 8%, #ffffff) !important;
  }
}

// 列过渡动画样式
.column-fade-enter-active,
.column-fade-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.column-fade-enter-from {
  opacity: 0;
  transform: translateX(-50px);
  width: 0;
}

.column-fade-enter-to {
  opacity: 1;
  transform: translateX(0);
  width: auto;
}

.column-fade-leave-from {
  opacity: 1;
  transform: translateX(0);
  width: auto;
}

.column-fade-leave-to {
  opacity: 0;
  transform: translateX(-50px);
  width: 0;
  color: var(--el-color-primary-light-3, #db665f);
}

// 表格列动画优化
.areaTable {
  ::v-deep(.el-table__header-wrapper) {
    .el-table__cell {
      transition: all 0.3s ease-in-out;
    }
  }
}

// 详细弹窗表格样式
.detailTable {
  .el-table__header-wrapper .el-table__cell {
    color: var(--brand-color, #cc251b) !important;
    background-color: color-mix(in srgb, var(--brand-color, #f9da49) 12%, #ffffff) !important;
  }

  .el-table__body-wrapper .el-table__cell {
    color: #181818 !important;
    background-color: #ffffff !important;
  }
}

// 详细弹窗：移动端适配
.client-v2-detail-dialog--mobile {
  ::v-deep(.el-dialog__body) {
    max-height: 70vh;
    overflow-y: auto;
    padding: 12px 16px;
  }
}

.client-v2-detail-dialog__body {
  min-height: 120px;
}

.client-v2-detail-dialog-mobile {
  font-size: 14px;
  color: var(--text-primary, #181818);
}

.client-v2-detail-mobile__brand {
  background: var(--gray-2, #f2f3f5);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
}

.client-v2-detail-mobile__brand-title {
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
  color: var(--text-primary, #181818);
}

.client-v2-detail-mobile__brand-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-bottom: 6px;
  font-size: 13px;

  span {
    color: var(--text-secondary, #666);
  }
}

.client-v2-detail-mobile__periods {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 12px;
  color: var(--text-secondary, #666);

  &--small {
    font-size: 11px;
    gap: 6px 10px;
  }
}

.client-v2-detail-mobile__period {
  white-space: nowrap;
}

.client-v2-detail-mobile__cinema {
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-color, #eee);

  &:last-child {
    border-bottom: none;
  }
}

.client-v2-detail-mobile__cinema-name {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 6px;
  color: var(--text-primary, #181818);
}

.client-v2-detail-mobile__cinema-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  font-size: 13px;
  color: var(--text-secondary, #666);
}

.client-v2-detail-mobile__empty {
  text-align: center;
  padding: 32px 16px;
  color: var(--text-secondary, #9ca3af);
  font-size: 13px;
}

.movieTable {
  .el-table__header-wrapper th.el-table__cell {
    font-weight: 700;
    color: var(--brand-color, #cc251b) !important;
    background-color: color-mix(in srgb, var(--brand-color, #f9da49) 12%, #ffffff) !important;
  }
}

.comment-area {
  display: flex;
  margin-top: 30px;

  .progress-bar-container {

    // margin-left: 26px;
    // width: 110px;
    .progress-item {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 16px;

      span {
        font-size: 14px;
        font-weight: 400;
        letter-spacing: 0.17px;
      }

      .el-progress {
        width: 110px;
      }
    }
  }

  .star-info-container {
    margin-left: 22px;
    margin-top: -10px;

    .stars {
      margin-left: -10px;
      width: 143px;
      height: 24px;
      background: url("https://eigaland.com/assets/eigaland/images/star.png") center top no-repeat;

      &.star-0 {
        background-position: center -26px;
      }

      &.star-1 {
        background-position: center -50px;
      }

      &.star-1-5 {
        background-position: center -74px;
      }

      &.star-2 {
        background-position: center -98px;
      }

      &.star-2-5 {
        background-position: center -121px;
      }

      &.star-3 {
        background-position: center -146px;
      }

      &.star-3-5 {
        background-position: center -168px;
      }

      &.star-4 {
        background-position: center -190px;
      }

      &.star-4-5 {
        background-position: center -212px;
      }

      &.star-5 {
        background-position: center -237px;
      }
    }

    p {
      margin: 0;
      padding: 0;

      &.star-score {
        font-size: 24px;
        font-weight: 500;
        letter-spacing: 0;
        text-align: left;
      }

      &.votes {
        font-size: 16px;
        font-weight: 400;
        color: #999999;
        letter-spacing: 0.4px;
      }
    }

    img {
      width: 14px;
      height: 14px;
      margin-right: 1.8px;
    }

    .like-info,
    .saw-info {
      margin-top: 2px;
      // margin-bottom: 4px;
      display: flex;
      align-items: center;
      color: #666666;
      font-size: 16px;
      font-weight: 800;

      >img {
        margin-right: 3px;
      }
    }

    .like-count,
    .saw-count {
      font-size: 16px;
      font-weight: 800;
      color: #f9b749;
    }
  }
}

.compare-movie-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;

  // 狭い画面では余白を詰めて 2 列で収まるようにする
  @media (max-width: 576px) {
    gap: 6px;
  }
}

// 対比電影タブボタン（本片／A/B/C 共通）：client-v2-action-btn の見た目（黄色ベース）を踏襲しつつ、
// 幅固定を解除し、非選択中は薄く表示して選択状態を区別する
.compare-movie-tab-btn {
  width: auto !important;
  min-width: auto !important;
  max-width: 100% !important;
  padding: 6px 16px !important;
  // client-v2-action-btn の margin-right: auto を打ち消す（ボタン間が離れてしまうため）
  margin-right: 0 !important;
  opacity: 0.5;
  transition: opacity 0.15s ease;

  &.is-active {
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
  }

  // スマホ幅：文字・余白・高さを縮めてタブ群をコンパクトに
  @media (max-width: 576px) {
    height: auto !important;
    min-height: 28px !important;
    padding: 4px 10px !important;
    font-size: 12px !important;
    line-height: 1.3 !important;
    // 長いラベルは折り返して横スクロールを防ぐ
    white-space: normal !important;
    text-align: center;
  }

  @media (max-width: 360px) {
    padding: 4px 8px !important;
    font-size: 11px !important;
  }
}

.compare-movie-similarity {
  font-weight: 400;
  font-size: 0.85em;

  @media (max-width: 576px) {
    font-size: 0.8em;
  }
}

.compare-movie-tabs-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--text-secondary);

  i {
    color: var(--brand-color);
  }
}

.section-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 32px 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--brand-color);
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: -0.3px;

  &:first-child {
    margin-top: 0;
  }

  i {
    color: var(--brand-color);
    font-size: 1.2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.15rem;
    margin: 24px 0 12px 0;
    gap: 8px;
    i { font-size: 1.1rem; }
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
    margin: 20px 0 10px 0;
    padding-bottom: 8px;
    i { font-size: 1rem; }
  }
}

.visitor-forecast-entry-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 16px;
  border: 1px solid var(--brand-color);
  border-radius: 999px;
  background: var(--bg-panel, #ffffff);
  color: var(--brand-color);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0;

  i {
    color: inherit;
    font-size: 0.85rem;
  }

  &:hover:not(:disabled) {
    background: var(--brand-color);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 0.74rem;
    span { display: none; }
  }
}

.daily-box-office-prediction-grid {
  > .card-daily-box-office-prediction {
    grid-column: 1 / -1;
  }
}

// 最终票房预测卡片（DEV-14891）：占满一行，卡片内容样式见 components/final-prediction-body.vue
.final-prediction-grid {
  > .card-final-prediction {
    grid-column: 1 / -1;
  }
}

.daily-bo-chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.daily-bo-chart-placeholder--overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  min-height: 0;
  background: var(--bg-panel, #fff);
  pointer-events: none;
}

.primary-panel-grid {
  position: relative;
}

.primary-panel-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: var(--bg-panel, #fff);
  border-radius: 12px;
  color: var(--text-secondary);
  font-size: 0.95rem;

  i {
    color: var(--brand-color);
    font-size: 1.4rem;
  }
}

.chart-container--daily-bo-host {
  position: relative;
  min-height: 260px;
}

.daily-bo-chart-canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.dashboard-grid .card .chart-container.chart-container--daily-bo {
  min-height: 260px;
  opacity: 1;
  animation: none;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 14px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }

  /* 横屏かつ狭い幅：分析チャート用 grid のみ、大グラフは全幅・2 枚の中グラフは各 50%（minmax で均等）かつ stretch で同じ高さ */
  @media (orientation: landscape) and (max-width: 1024px) {
    &:has(> .card.card-chart-md) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: stretch;
      gap: 12px;

      > .card.card-chart-lg {
        grid-column: 1 / -1;
      }

      > .card.card-chart-md {
        grid-column: auto;
        min-height: 0;
        align-self: stretch;
      }
    }
  }

  .card {
    background: var(--bg-panel);
    border-radius: var(--border-radius);
    padding: 20px;

    @media (max-width: 768px) {
      padding: 16px;
    }

    @media (max-width: 480px) {
      padding: 14px;
    }
    border: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    /* 評価 / YouTube / Instagram：hover 遮罩 + 詳細 CTA */
    &.card-detail-hover {
      transition: transform 0.3s cubic-bezier(0.34, 1.4, 0.64, 1), box-shadow 0.3s ease;
      cursor: pointer;

      .card-detail-overlay {
        position: absolute;
        inset: 0;
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.28s ease;
        z-index: 10;
        border-radius: inherit;
        pointer-events: none;

        &__cta {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 13px 30px;
          border-radius: 50px;
          background: rgba(255, 255, 255, 0.16);
          border: 1.5px solid rgba(255, 255, 255, 0.55);
          color: #fff;
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.3);
          transform: translateY(16px) scale(0.88);
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        &__arrow {
          transition: transform 0.25s ease;
        }
      }

      .card-detail-mobile-hint {
        display: none;
      }

      &:hover {
        transform: translateY(-8px);

        .card-detail-overlay {
          opacity: 1;

          &__cta {
            transform: translateY(0) scale(1);
          }

          &__arrow {
            transform: translateX(5px);
          }
        }
      }

      @media (max-width: 1024px) {
        .card-detail-mobile-hint {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 12px;
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid color-mix(in srgb, var(--brand-color) 55%, transparent);
          background: linear-gradient(90deg, color-mix(in srgb, var(--brand-color) 12%, transparent), rgba(255, 120, 30, 0.12));
          color: #7a5600;
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          pointer-events: none;
        }
      }
    }

    &.card-engagement {
      .card-detail-overlay {
        background: linear-gradient(
          145deg,
          rgba(255, 160, 30, 0.82) 0%,
          rgba(20, 12, 55, 0.90) 100%
        );
      }

      &:hover {
        box-shadow:
          0 20px 48px rgba(0, 0, 0, 0.16),
          0 0 0 2px rgba(255, 160, 30, 0.35);

        .card-header,
        .card-header-title-text,
        .card-header-title-with-help :deep(.el-tooltip__trigger) {
          color: #fff;
        }
      }
    }

    &.card-youtube {
      .card-detail-overlay {
        background: linear-gradient(
          145deg,
          rgba(255, 0, 0, 0.75) 0%,
          rgba(20, 12, 55, 0.90) 100%
        );
      }

      &:hover {
        box-shadow:
          0 20px 48px rgba(0, 0, 0, 0.16),
          0 0 0 2px rgba(255, 0, 0, 0.35);

        .card-header,
        .card-header-title-text,
        .card-header-title-with-help :deep(.el-tooltip__trigger) {
          color: var(--text-primary, #181818);
        }
      }
    }

    &.card-instagram {
      .card-detail-overlay {
        background: linear-gradient(
          145deg,
          rgba(228, 64, 95, 0.78) 0%,
          rgba(131, 58, 180, 0.88) 50%,
          rgba(20, 12, 55, 0.90) 100%
        );
      }

      &:hover {
        box-shadow:
          0 20px 48px rgba(0, 0, 0, 0.16),
          0 0 0 2px rgba(228, 64, 95, 0.35);

        .card-header,
        .card-header-title-text,
        .card-header-title-with-help :deep(.el-tooltip__trigger) {
          color: var(--text-primary, #181818);
        }
      }
    }

    &.card-twitter {
      .card-detail-overlay {
        /* 上部偏亮，与 Ins 同样便于 hover 时深色 title 阅读 */
        background: linear-gradient(
          145deg,
          rgba(210, 218, 228, 0.78) 0%,
          rgba(130, 140, 155, 0.85) 48%,
          rgba(20, 12, 55, 0.90) 100%
        );
      }

      &:hover {
        box-shadow:
          0 20px 48px rgba(0, 0, 0, 0.16),
          0 0 0 2px rgba(130, 140, 155, 0.45);

        .card-header,
        .card-header-title-text,
        .card-header-title-with-help :deep(.el-tooltip__trigger) {
          color: var(--text-primary, #181818);
        }
      }
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, var(--brand-color), var(--accent-orange));
    }

    &.card-revenue {
      grid-column: span 2;

      @media (max-width: 768px) {
        grid-column: span 1;
      }
    }

    &.card-audience {
      grid-column: span 2;
      align-self: start;
      height: auto;
      overflow: visible;

      .card-header {
        align-self: stretch;
        width: 100%;
      }

      @media (max-width: 768px) {
        grid-column: span 1;
      }
    }

    &.card-chart-md {
      grid-column: span 2;
      min-height: 300px;

      @media (max-width: 1024px) {

        .card-chart-lg,
        .card-chart-md {
          grid-column: span 2;
        }
      }

      @media (max-width: 768px) {
        grid-column: span 1;
        min-height: 250px;
      }
    }

    &.card-chart-lg {
      grid-column: span 4;
      min-height: 380px;

      @media (max-width: 1024px) {

        .card-chart-lg,
        .card-chart-md {
          grid-column: span 2;
        }
      }

      @media (max-width: 768px) {
        grid-column: span 1;
        min-height: 250px;
      }
    }

    &.card-chart-fullscreen {
      position: fixed;
      z-index: 10001;
      inset: 0;
      width: 100vw;
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100vw;
      max-height: 100vh;
      margin: 0;
      border-radius: 0;
      grid-column: unset;
      min-height: 0 !important;
      display: flex;
      flex-direction: column;
      padding:
        max(12px, env(safe-area-inset-top, 0px))
        max(12px, env(safe-area-inset-right, 0px))
        max(12px, env(safe-area-inset-bottom, 0px))
        max(12px, env(safe-area-inset-left, 0px));
      transform-origin: 50% 45%;
      animation: indexChartFsIn 0.72s cubic-bezier(0.22, 1, 0.36, 1) forwards;

      .card-header {
        animation: indexChartFsHeaderIn 0.62s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
      }

      .chart-container {
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }
    }

    &.card-chart-fullscreen--closing {
      animation: indexChartFsOut 0.62s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    @media (prefers-reduced-motion: reduce) {
      &.card-chart-fullscreen {
        animation: none;
        opacity: 1;
        filter: none;

        .card-header {
          animation: none;
        }
      }

      &.card-chart-fullscreen--closing {
        animation: none;
        opacity: 0;
        transition: opacity 0.2s ease;
      }
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
      color: var(--text-secondary);
      font-size: 0.9rem;
      font-weight: 600;
      flex-shrink: 0;
    }

    .card-header-chart-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0; // 全画面ボタンが長いタイトルに圧迫されないよう固定
    }

    // chart カードのタイトル：min-width:0 で収縮可能にし、全画面ボタン分の空間を確保
    // ※ & を付けないと `.card [data-index-chart-key] ...` に展開され、
    //    属性が .card 自身にあるため一致しない（タイトルが縮まずボタンが押し出される）
    &[data-index-chart-key] .card-header > span {
      min-width: 0;
      flex: 1 1 auto;
      word-break: break-word;
    }

    .chart-fs-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      padding: 0;
      border: none;
      border-radius: 10px;
      background: rgba(0, 0, 0, 0.06);
      color: var(--text-secondary, #555);
      cursor: pointer;
      transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

      &:active {
        transform: scale(0.94);
      }

      .card-chart-fullscreen & {
        background: rgba(0, 0, 0, 0.08);
      }
    }

    .chart-container {
      position: relative;
      height: 100%;
      width: 100%;
      flex-grow: 1;
      min-height: 0;
      overflow: hidden;
      opacity: 0;
      animation: chartFadeIn 1s ease-out forwards;
    }

    .chart-container:nth-of-type(2) {
      animation-delay: 0.6s;
    }

    .main-stat {
      font-size: 2.5rem;
      color: var(--brand-color);
      font-weight: 700;

      @media (max-width: 768px) {
        font-size: 2rem;
      }

      @media (max-width: 480px) {
        font-size: 1.75rem;
      }
    }

    .sub-stat {
      font-size: 0.85rem;
      color: var(--text-secondary);
      font-weight: 500;
    }

    .revenue-range {
      display: flex;
      align-items: baseline;
      gap: 10px;

      .revenue-min {
        font-size: 1.5rem;
        color: var(--text-secondary);
        font-weight: 600;
      }

      .text-secondary {
        color: var(--text-secondary);
      }

      .revenue-max {
        font-size: 2.5rem;
        color: var(--brand-color);
        font-weight: 700;

        @media (max-width: 768px) {
          font-size: 2rem;
        }
      }
    }

    .detail-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid var(--border-color);
      font-size: 0.85rem;

      .detail-label {
        color: var(--text-secondary);
        width: 40px;
        font-weight: 500;
      }

      .detail-bar-container {
        flex-grow: 1;
        margin: 0 10px;
        height: 6px;
        background: var(--border-color, #f0f0f0);
        border-radius: 3px;
        overflow: hidden;

        .detail-bar {
          height: 100%;
          border-radius: 2px;
        }
      }

      .detail-value {
        font-weight: 700;
        text-align: right;
        min-width: 60px;
        font-size: 0.9rem;
        color: var(--text-primary);
      }
    }

    .detail-row:last-child {
      border-bottom: none;
    }

    .detail-section-title {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--text-secondary);
      margin-bottom: 8px;
      padding-bottom: 4px;
      letter-spacing: 0.3px;
      text-transform: none;
    }
  }
}

/* ─── AI 総合分析レポート（来客数予測分析ページから移植）─────────── */
.vf-card {
  background: var(--bg-panel, #ffffff);
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 6px;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1));
  margin-bottom: 20px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

.vf-card-accent {
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  height: 4px;
  background: linear-gradient(90deg, var(--brand-color, #f9da49) 0%, #f97316 100%);
}

.vf-card-accent--ai {
  background: linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%);
}

.vf-report-card {
  position: relative;
  // Figma: コンテンツ幅 1576 ≒ カード幅 − 25×2、セクション間 gap 21px
  padding: 25px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

.vf-report-head {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 21px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
}

.vf-report-head-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.vf-ai-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  padding: 0 11.9062px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    #8b5cf6 0%,
    #925df0 7.14%,
    #995de9 14.29%,
    #a05de3 21.43%,
    #a75ddc 28.57%,
    #ae5dd6 35.71%,
    #b55ccf 42.86%,
    #bc5bc9 50%,
    #c35ac2 57.14%,
    #ca58bc 64.29%,
    #d156b5 71.43%,
    #d853ae 78.57%,
    #de50a7 85.71%,
    #e54ca0 92.86%,
    #ec4899 100%
  );
  color: #ffffff;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  letter-spacing: -0.3125px;
}

.vf-report-title {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  letter-spacing: -0.3125px;
  color: var(--text-primary, #4a4a4a);
}

/* レビュー・感情分析：ホットワード ヒートスケール凡例（Figma 仕様）。
   hotwordHeatScales の色の数だけスケールを表示する。 */
.review-sentiment-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.heat-scale-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.heat-scale-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.heat-scale-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.heat-scale-label {
  font-size: 9px;
  line-height: 14px;
  font-weight: 400;
  letter-spacing: 0.167px;
  color: var(--text-secondary, #9CA3AF);
  white-space: nowrap;
}

/* Good / Bad 感情フィルター（Figma: Frame 427322163） */
.vf-report-filter {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

.vf-report-filter-btn {
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px;
  gap: 4px;
  height: 26px;
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 4px;
  background: var(--bg-panel, #ffffff);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  .vf-report-filter-icon {
    width: 12px;
    height: 12px;
    font-size: 12px;
    line-height: 1;
    color: var(--text-secondary, #6b7280);
    transition: color 0.15s ease;
  }

  .vf-report-filter-text {
    font-size: 11px;
    line-height: 16px;
    font-weight: 500;
    text-align: center;
    letter-spacing: 0.0644531px;
    color: var(--text-secondary, #6b7280);
    transition: color 0.15s ease;
  }

  /* 選択中の共通スタイル：枠線を消して tint 背景に */
  &.is-active {
    border-color: transparent;
  }
}

/* Good 選択中（Figma 仕様：#FFF6F0 背景 / #F97316 文字） */
.vf-report-filter-btn--good.is-active {
  background: #fff6f0;

  .vf-report-filter-icon,
  .vf-report-filter-text {
    color: #f97316;
  }
}

/* Bad 選択中（Figma 仕様：#DDE4F2 背景 / #07399E 文字） */
.vf-report-filter-btn--bad.is-active {
  background: #dde4f2;

  .vf-report-filter-icon,
  .vf-report-filter-text {
    color: #07399e;
  }
}

/* Good 選択中のフィードバックヒント（Figma: Tooltip） */
.vf-report-tip {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  width: 243px;
  padding: 10px;
  background: #fff6f0;
  border-radius: 4px;
  box-shadow: 0 0 12px rgba(133, 69, 0, 0.19);
  font-size: 12px;
  line-height: 24px;
  font-weight: 700;
  letter-spacing: -0.3125px;
  color: #f97316;
  white-space: nowrap;
  pointer-events: none;
}

/* 表示 / 非表示のフェード（Vue <Transition name="vf-report-tip">） */
.vf-report-tip-enter-active,
.vf-report-tip-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.vf-report-tip-enter-from,
.vf-report-tip-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ツールチップ上向き矢印（Good ボタン直下を指す） */
.vf-report-tip-arrow {
  position: absolute;
  top: -6px;
  right: 22px;
  width: 12px;
  height: 12px;
  background: #fff6f0;
  border-radius: 2px;
  transform: rotate(45deg);
  box-shadow: 0 -3px 8px rgba(133, 69, 0, 0.15);
}

/* 〜400px：左右位置は placeReportTip() が画面内に収まるよう算出する。
   CSS 側は「幅 243px 固定をやめて、収まらなければ折り返す」ことだけ担保する */
@media (max-width: 400px) {
  .vf-report-tip {
    width: max-content;
    white-space: normal;
    line-height: 1.6;
  }
}

.vf-report-summary {
  margin-bottom: 21px;
}

// ─── 分析中ローディング（Google AI 検索風）───────────────────
.vf-report-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0 10px;
}

// グラデーションが左右に流れるシマーテキスト
.vf-report-loading-text {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  background: linear-gradient(90deg, #8b5cf6 0%, #ec4899 25%, #8b5cf6 50%, #ec4899 75%, #8b5cf6 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: vf-report-shimmer 1.6s linear infinite;
}

@keyframes vf-report-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: 0% 0;
  }
}

// 順番に跳ねるグラデーションドット
.vf-report-loading-dots {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  i {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
    animation: vf-report-dot-bounce 0.9s ease-in-out infinite;
  }

  i:nth-child(2) {
    animation-delay: 0.15s;
  }

  i:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes vf-report-dot-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

.vf-report-subhead {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 15px;
    line-height: 22px;
    font-weight: 700;
    color: var(--text-primary, #000000);
  }
}

// 見出し横の縦グラデーションバー（4×24 pill）
.vf-report-bar {
  flex-shrink: 0;
  width: 4px;
  height: 24px;
  border-radius: 999px;
  background: linear-gradient(180deg, #8b5cf6 0%, #ec4899 100%);
}

.vf-report-lead {
  margin: 0;
  font-size: 18px;
  line-height: 23px;
  color: var(--text-primary, #111111);
}

// 本文中の強調色（スクリーンショット準拠）
.vf-em-green {
  color: #10b981;
}
.vf-em-orange {
  color: #f97316;
}

// タイプライター演出の点滅キャレット
.vf-type-caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: -0.12em;
  background: #8b5cf6;
  animation: vf-caret-blink 0.8s steps(1) infinite;
}

@keyframes vf-caret-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

// レポート出現演出: 読み込み完了後、各セクションを下からふわっとフェードイン（段階表示）。
// タイプライター演出の置き換え。typedTotal は描画前に全文表示済みなので、ここでは純粋に CSS で見せる。
@keyframes vf-report-reveal {
  from {
    opacity: 0;
    transform: translateY(14px);
    filter: blur(3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.vf-report-card {
  // 上部セクション（制約事項バナー → サマリー）を順に表示
  .vf-report-limitations,
  .vf-report-summary {
    animation: vf-report-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  .vf-report-limitations {
    animation-delay: 0.05s;
  }
  .vf-report-summary {
    animation-delay: 0.16s;
  }

  // 分析グリッドの各要素（見出し・パネル）をカスケード表示
  .vf-report-grid > * {
    animation: vf-report-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  .vf-report-grid > *:nth-child(1) {
    animation-delay: 0.26s;
  }
  .vf-report-grid > *:nth-child(2) {
    animation-delay: 0.31s;
  }
  .vf-report-grid > *:nth-child(3) {
    animation-delay: 0.38s;
  }
  .vf-report-grid > *:nth-child(4) {
    animation-delay: 0.43s;
  }
  .vf-report-grid > *:nth-child(5) {
    animation-delay: 0.5s;
  }
  .vf-report-grid > *:nth-child(6) {
    animation-delay: 0.55s;
  }
  .vf-report-grid > *:nth-child(n + 7) {
    animation-delay: 0.62s;
  }
}

// アクセシビリティ: モーション軽減設定時はアニメーションを無効化し即時表示
@media (prefers-reduced-motion: reduce) {
  .vf-report-card .vf-report-limitations,
  .vf-report-card .vf-report-summary,
  .vf-report-card .vf-report-grid > * {
    animation: none;
  }
}

// 分析結果情報（左）とアクションプラン（右）を行単位で対応させるグリッド。
// 各行の左右カードは align-items: stretch により同じ高さ＝上端が揃う。
.vf-report-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  row-gap: 16px;
  align-items: stretch;

  // 見出し行（分析結果情報 / アクションプラン）は行間の余白だけで十分
  > .vf-report-subhead {
    margin-bottom: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

// アクションプラン: suggestion を番号付きで列挙するリスト
// vf-action-hidden は display:none のため、タイプ表示済みの項目だけが 1,2,3… と採番される
.vf-suggest-list {
  margin: 12px 0 0;
  padding-left: 22px;
  list-style: decimal;

  li {
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 21px;
    color: var(--text-secondary, #374151);

    &::marker {
      font-weight: 700;
      color: var(--text-secondary, #374151);
    }

    strong {
      color: var(--text-primary, #111111);
    }

    &.vf-action-hidden {
      display: none;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}


.vf-report-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 17px;
  background: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f9fafb));
  border-radius: 8px;
  color: var(--text-primary);

  // 左カラム3カードは内容（カテゴリ）ごとに背景色を分けて識別しやすくする
  &--sales {
    background: color-mix(in srgb, var(--accent-blue, #3b82f6) 10%, var(--bg-panel, #eff6ff));
  }

  &--area {
    background: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f9fafb));
  }

  &--social {
    background: color-mix(in srgb, var(--accent-purple, #8b5cf6) 10%, var(--bg-panel, #faf5ff));
  }
}

.vf-report-panel-head {
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 16px;
    color: var(--text-primary, #000000);
  }

  h4 {
    margin: 0;
    font-size: 14px;
    line-height: 21px;
    font-weight: 700;
    color: var(--text-primary, #000000);
  }

  // 短期施策（1-2週間）: Figma 仕様に合わせる
  &--orange {
    align-self: stretch;
    height: 21px;
    padding: 0;

    .vf-action-icon {
      flex: none;
      width: 20px;
      height: 20px;
    }

    i,
    h4 {
      color: var(--accent-orange, #f97316);
    }

    h4 {
      font-family: "Inter", sans-serif;
      letter-spacing: -0.150391px;
    }
  }

  // 来客傾向分析: Figma 仕様（アイコン 20x20）
  &--analysis {
    align-self: stretch;
    height: 21px;
    padding: 0;
    margin-bottom: 12px;

    .vf-action-icon {
      flex: none;
      width: 20px;
      height: 20px;
    }

    h4 {
      font-family: "Inter", sans-serif;
      letter-spacing: -0.150391px;
      color: var(--text-primary, #000000);
    }
  }

  // カテゴリ見出し（カード最上部: アイコン＋カテゴリ名）
  &--category {
    i {
      font-size: 15px;
      color: var(--text-primary, #111111);
    }

    h4 {
      color: var(--text-primary, #111111);
    }
  }

  // 施策見出し（右カラム: アクションプラン）
  &--action {
    i,
    h4 {
      color: var(--accent-orange, #f97316);
    }
  }

  &--green {
    i,
    h4 {
      color: var(--accent-green, #10b981);
    }
  }

  &--red {
    i,
    h4 {
      color: var(--accent-red, #ef4444);
    }
  }

  &--purple {
    i,
    h4 {
      color: var(--accent-purple, #8b5cf6);
    }
  }

  &--blue {
    i,
    h4 {
      color: var(--accent-blue, #3b82f6);
    }
  }
}

// 同一パネル内で 注視ポイント の下に続く小見出し（競合分析 / エリア分析）は
// 区切り線と上余白で視覚的に分離する
.vf-report-panel-head--divided {
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

// データの制約事項バナー（レポート最上部・目立つ警告調）
.vf-report-limitations {
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid color-mix(in srgb, var(--brand-color, #f9da49) 55%, #fcd34d);
  border-left: 4px solid var(--accent-orange, #f59e0b);
  border-radius: 8px;
  background: color-mix(in srgb, var(--brand-color, #f9da49) 18%, var(--bg-panel, #fffbeb));
}

.vf-report-limitations-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;

  i {
    font-size: 16px;
    color: var(--accent-orange, #d97706);
  }

  span {
    font-size: 14px;
    line-height: 21px;
    font-weight: 700;
    color: color-mix(in srgb, var(--accent-orange, #d97706) 70%, var(--text-primary, #92400e));
  }
}

.vf-report-limitations-list {
  margin: 0;
  padding-left: 20px;
  list-style: disc;

  li {
    font-size: 14px;
    line-height: 21px;
    color: color-mix(in srgb, var(--accent-orange, #d97706) 45%, var(--text-primary, #78350f));

    &.vf-action-hidden {
      display: none;
    }
  }
}

// シグナル1件（タイトル＋根拠）。タイピング到達まで非表示
.vf-signal-item {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &.vf-action-hidden {
    display: none;
  }
}

.vf-signal-title {
  margin: 0;
  font-size: 14px;
  line-height: 21px;
  font-weight: 700;
  color: var(--text-primary, #111111);
}

.vf-signal-desc {
  margin: 0;
  font-size: 14px;
  line-height: 21px;
  color: var(--text-secondary, #4a4a4a);
}

// 数値強調（boldNumbers で本文中の数字を太字化）
.vf-num {
  font-weight: 700;
  color: var(--text-primary, #111111);
}

// データなし表示
.vf-report-empty {
  padding: 8px 0 12px;
  font-size: 14px;
  line-height: 21px;
  color: var(--text-secondary, #6b7280);
}

.vf-report-paragraphs {
  display: flex;
  flex-direction: column;
  gap: 8px;

  p {
    margin: 0;
    font-size: 14px;
    line-height: 21px;
    color: var(--text-secondary, #4a4a4a);

    strong {
      color: var(--text-primary, #111111);
    }

    strong.vf-em-green {
      color: var(--accent-green, #10b981);
    }
    strong.vf-em-orange {
      color: var(--accent-orange, #f97316);
    }
  }
}

.vf-action-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    // タイピングが到達するまで行ごと非表示
    &.vf-action-hidden {
      display: none;
    }
  }
}

.vf-action-num {
  flex-shrink: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 700;
  color: #f97316;
}

.vf-action-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vf-action-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--text-primary, #000000);
}

.vf-action-desc {
  font-size: 14px;
  line-height: 20px;
  color: var(--text-secondary, #4a4a4a);
}

/* X / Twitter brand: light は黒、dark はライトテキスト */
.twitter-brand-fg,
.dashboard-grid .card .main-stat.twitter-brand-fg,
.card-twitter .twitter-brand-fg {
  color: #0F1419;
}
.twitter-brand-bg {
  background: #0F1419;
}
html.dark .twitter-brand-fg,
html.dark .dashboard-grid .card .main-stat.twitter-brand-fg,
html.dark .card-twitter .twitter-brand-fg {
  color: #E7E9EA;
}
html.dark .twitter-brand-bg {
  background: #E7E9EA;
}

/* 全画面ボタン：dark は白透過 */
html.dark .chart-fs-btn {
  background: rgba(255, 255, 255, 0.08);

  .card-chart-fullscreen & {
    background: rgba(255, 255, 255, 0.12);
  }
}

/* モバイル詳細ヒント：dark は暖色で視認性良く。
   light 側は .dashboard-grid .card.card-detail-hover 配下で color:#7a5600 を指定しており、
   単なる html.dark .card-detail-mobile-hint だと優先度負けして茶字が暗背景に溶け込み灰色に見えるため、
   同じネスト深さで上書きする。 */
html.dark .dashboard-grid .card.card-detail-hover .card-detail-mobile-hint {
  color: var(--brand-color, #f9da49);
  border-color: rgba(204, 37, 27, 0.6);
  background: linear-gradient(90deg, rgba(204, 37, 27, 0.2), rgba(255, 120, 30, 0.16));
}

/* VF レポート：dark 用のフィルター / tip / パネル / 制約事項 */
html.dark .vf-report-filter-btn--good.is-active {
  background: rgba(249, 115, 22, 0.16);

  .vf-report-filter-icon,
  .vf-report-filter-text {
    color: var(--accent-orange, #fb923c);
  }
}

html.dark .vf-report-filter-btn--bad.is-active {
  background: rgba(76, 141, 255, 0.16);

  .vf-report-filter-icon,
  .vf-report-filter-text {
    color: var(--accent-blue, #4c8dff);
  }
}

html.dark .vf-report-tip,
html.dark .vf-report-tip-arrow {
  background: color-mix(in srgb, var(--accent-orange, #fb923c) 14%, var(--bg-panel, #20242b));
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.35);
}

html.dark .vf-report-tip {
  color: var(--accent-orange, #fb923c);
}

html.dark .vf-report-card {
  color: var(--text-primary, #f0f0f0);
}

html.dark .vf-report-subhead h3,
html.dark .vf-report-lead,
html.dark .vf-signal-title,
html.dark .vf-num,
html.dark .vf-action-title,
html.dark .vf-suggest-list li strong,
html.dark .vf-report-paragraphs p strong {
  color: var(--text-primary, #f0f0f0);
}

html.dark .vf-report-paragraphs p,
html.dark .vf-signal-desc,
html.dark .vf-suggest-list li,
html.dark .vf-suggest-list li::marker {
  color: var(--text-secondary, #a8adb8);
}

html.dark .vf-report-panel {
  color: var(--text-primary, #f0f0f0);
  background: color-mix(in srgb, #ffffff 4%, var(--bg-panel, #20242b));

  &--sales {
    background: color-mix(in srgb, var(--accent-blue, #4c8dff) 12%, var(--bg-panel, #20242b));
  }

  &--area {
    background: color-mix(in srgb, #ffffff 4%, var(--bg-panel, #20242b));
  }

  &--social {
    background: color-mix(in srgb, var(--accent-purple, #a78bfa) 12%, var(--bg-panel, #20242b));
  }
}

html.dark .vf-report-panel-head {
  i,
  h4 {
    color: var(--text-primary, #f0f0f0);
  }

  &--green i,
  &--green h4 {
    color: var(--accent-green, #34d399);
  }

  &--red i,
  &--red h4 {
    color: var(--accent-red, #f87171);
  }

  &--orange i,
  &--orange h4,
  &--action i,
  &--action h4 {
    color: var(--accent-orange, #fb923c);
  }

  &--purple i,
  &--purple h4 {
    color: var(--accent-purple, #a78bfa);
  }

  &--blue i,
  &--blue h4 {
    color: var(--accent-blue, #4c8dff);
  }
}

html.dark .vf-report-panel-head--divided {
  border-top-color: var(--border-color, #30343c);
}

html.dark .vf-report-limitations {
  background: color-mix(in srgb, var(--brand-color, #f9da49) 10%, var(--bg-panel, #20242b));
  border-color: color-mix(in srgb, var(--brand-color, #f9da49) 35%, var(--border-color, #30343c));
  border-left-color: var(--accent-orange, #fb923c);
}

html.dark .vf-report-limitations-head {
  i {
    color: var(--accent-orange, #fb923c);
  }

  span {
    color: color-mix(in srgb, var(--brand-color, #f9da49) 55%, #f0f0f0);
  }
}

html.dark .vf-report-limitations-list li {
  color: color-mix(in srgb, var(--brand-color, #f9da49) 28%, #f0f0f0);
}

html.dark .vf-report-empty {
  color: var(--text-secondary, #a8adb8);
}

html.dark .areaTable,
html.dark .movieTable,
html.dark .detailTable {
  --el-table-header-bg-color: color-mix(in srgb, var(--brand-color, #f9da49) 14%, var(--bg-panel, #20242b));
  --el-table-header-text-color: var(--el-color-primary-light-3, #db665f);
  --el-table-bg-color: var(--bg-panel, #20242b);
  --el-table-tr-bg-color: var(--bg-panel, #20242b);
  --el-table-text-color: var(--text-primary, #f0f0f0);
  --el-table-border-color: var(--border-color, #30343c);
  --el-table-row-hover-bg-color: color-mix(in srgb, var(--brand-color, #f9da49) 10%, var(--bg-panel, #20242b));
  --el-fill-color-blank: var(--bg-panel, #20242b);

  .el-table__header-wrapper th.el-table__cell,
  .el-table__fixed-header-wrapper th.el-table__cell {
    color: var(--el-color-primary-light-3, #db665f) !important;
    background-color: color-mix(in srgb, var(--brand-color, #f9da49) 14%, var(--bg-panel, #20242b)) !important;
  }

  .el-table__body-wrapper td.el-table__cell,
  .el-table__fixed-body-wrapper td.el-table__cell {
    color: var(--text-primary, #f0f0f0) !important;
    background-color: var(--bg-panel, #20242b) !important;
  }

  .el-table__body tr.hover-row > td.el-table__cell,
  .el-table__body tr.current-row > td.el-table__cell,
  .el-table__fixed-body-wrapper tr.hover-row > td.el-table__cell,
  .el-table__fixed-body-wrapper tr.current-row > td.el-table__cell {
    background-color: color-mix(in srgb, var(--brand-color, #f9da49) 10%, var(--bg-panel, #20242b)) !important;
  }
}
</style>